<script setup>
// 🍎📅 AGENDA no design Apple (item 210, 23/09) — Consultas | Teleconsultas |
// Exames | Cirurgias, cada tipo no seu trilho e na sua cor, visões Mês /
// Semana / Dia (abre na SEMANA). Cada agendamento guarda: nome, telefone,
// problema/exame, dia, horário, médico e unidade (ou local da cirurgia, ou
// "online" na teleconsulta). Criado à mão aqui ou pelo Atendente de
// Agendamento (IA). Peças: kit "iMac G3 + vidro" + _cevico-agenda.scss.
import { openPatientChat } from 'dashboard/composables/useCevicoPatientChat';
import { ref, computed, watch, onMounted, onBeforeUnmount } from 'vue';
import SkeletonScreen from 'dashboard/components-next/cevico/SkeletonScreen.vue';
import AgendaTimeColumn from 'dashboard/components-next/cevico/agenda/AgendaTimeColumn.vue';
import { useRoute, useRouter } from 'vue-router';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import { useAdmin } from 'dashboard/composables/useAdmin';
import { useCevicoAccess } from 'dashboard/composables/useCevicoAccess';
import CevicoViewOnlyPill from 'dashboard/components-next/cevico/CevicoViewOnlyPill.vue';
import { frontendURL } from 'dashboard/helper/URLHelper';
import PatientSpaceIcon from 'dashboard/routes/dashboard/patient/PatientSpaceIcon.vue';
import {
  startOfMonth, endOfMonth, startOfWeek, endOfWeek,
  addDays, addWeeks, addMonths, isSameDay, isSameMonth, format,
} from 'date-fns';
import CrmAPI from 'dashboard/api/crm';
import { copyTextToClipboard } from 'shared/helpers/clipboard';
import { vCvMenu } from 'dashboard/composables/useCevicoContextMenu';
import {
  DOCTORS, MODALITIES, ONLINE_UNIT,
  TYPES, TYPE_BY_KEY, GENERAL_TYPE, typeOf, LEGACY_KIND_TO_TYPES,
  kindFor, kindOf, kindVars, hexToRgbSpaced, isAppointmentTask as isAppointment,
  resolveWindows, resolveOpenWindows, windowOnDay, resolveBlocked, resolveBlockedDays, resolveExamWindows,
  wholeBlockedDays, partialBlocksOn, isWindowBlocked, sameBlock,
  resolveSurgeryWindows, slotsFor as sharedSlotsFor, dateKey, blockKey, scanAgenda,
  TASK_COLORS, taskColorOf, attendanceMarkOf,
  occupiesSlot,
  patientNameOf, handConfirmed, isConfirmed, isDeclined, confirmationTitle,
  isRescheduled, rescheduledTitle,
  ORIGINS, ORIGIN_BY_KEY, originOf, LEAD_CHANNELS, LEAD_CHANNEL_BY_KEY,
  reservedFor, windowAccepts, windowAt,
  effectiveAppointmentPrice,
  presetAppointmentPrice,
  priceLabel,
} from 'dashboard/helper/cevicoAgenda';
import AgendaSettings from './AgendaSettings.vue';
import PatientSuggest from 'dashboard/components-next/cevico/PatientSuggest.vue';

const store = useStore();
const { isAdmin } = useAdmin();
// 🔐 rodada 334: acessos em árvore — agenda (marcar/remarcar/cancelar), agenda.cirurgias,
// agenda.exames, agenda.bloqueios (cadeado), agenda.lixeira, agenda.imprimir,
// agenda.configuracao (engrenagem/janelas). unitAllowed esconde unidades que a pessoa não vê.
const { canView, canEdit, unitAllowed } = useCevicoAccess();
const canEditAgenda = computed(() => canEdit('agenda'));
const TYPE_NODE = { cirurgia: 'agenda.cirurgias', exames: 'agenda.exames' };
const visibleTypes = computed(() => TYPES.filter(t => !TYPE_NODE[t.key] || canView(TYPE_NODE[t.key])));
const route = useRoute();
const router = useRouter();

// Espaço do Paciente: consulta amarrada ao contato abre a página única
// item 298: a conversa do paciente num popup, sem sair da Agenda
const openTaskChat = task => openPatientChat({ contactId: task.contact_id, phone: task.phone });
const openPatientSpace = task => {
  if (!task?.contact_id) return;
  router.push(frontendURL(`accounts/${route.params.accountId}/patient/${task.contact_id}`));
};

const agents = useMapGetter('agents/getAgents');
const currentUser = useMapGetter('getCurrentUser');
const allTasks = useMapGetter('tasks/getTasks');
const crmSettings = useMapGetter('crm/getSettings');

const isLoading = ref(true);
const cursor = ref(new Date()); // data de referência da navegação
const viewMode = ref('week'); // 'month' | 'week' | 'day' — padrão SEMANA
// filtro: 'clinic' (todas) | 'unit:x' | 'doctor:Nome' | 'me' | '<agentId>'
const view = ref('clinic');

// ── 🎨 item 234 (25/09): CAMADAS por TIPO — avaliação · retorno · pós-op ·
// exame · teleconsulta · cirurgia. Todas ligadas = "Agenda geral" (tom
// neutro); uma só = a página veste a cor daquele tipo. Fica salvo no navegador.
const LAYERS_KEY = 'cevico_agenda_layers';
const loadLayers = () => {
  try {
    const saved = JSON.parse(localStorage.getItem(LAYERS_KEY) || 'null');
    if (Array.isArray(saved) && saved.some(x => TYPE_BY_KEY[x])) return saved.filter(x => TYPE_BY_KEY[x]);
  } catch (e) { /* sem preferência salva */ }
  return TYPES.map(t => t.key);
};
const layers = ref(new Set(loadLayers()));
const persistLayers = () => {
  try { localStorage.setItem(LAYERS_KEY, JSON.stringify([...layers.value])); } catch (e) { /* sem espaço */ }
};
const layerOn = key => layers.value.has(key);
const toggleLayer = key => {
  const next = new Set(layers.value);
  if (next.has(key) && next.size > 1) next.delete(key);
  else next.add(key);
  layers.value = next;
  persistLayers();
};
const soloLayer = key => { layers.value = new Set([key]); persistLayers(); };
const allLayers = () => { layers.value = new Set(TYPES.map(t => t.key)); persistLayers(); };
const ensureLayer = key => {
  if (!TYPE_BY_KEY[key] || layers.value.has(key)) return;
  const next = new Set(layers.value); next.add(key); layers.value = next; persistLayers();
};
const activeTypes = computed(() => TYPES.filter(t => layers.value.has(t.key)));
const isGeneral = computed(() => activeTypes.value.length === TYPES.length);
const singleType = computed(() => (activeTypes.value.length === 1 ? activeTypes.value[0] : null));
const k = computed(() => singleType.value || GENERAL_TYPE); // o "tipo" da página (cor, nome, ícone)
const pageVars = computed(() => kindVars(k.value));
const resources = computed(() => new Set(activeTypes.value.map(t => t.resource)));
// modos "só um recurso" (o resto da tela decide janelas, ocupação e filtros por eles)
const isSurgeryMode = computed(() => resources.value.size === 1 && resources.value.has('surgery'));
const isTele = computed(() => resources.value.size === 1 && resources.value.has('online'));
const isExam = computed(() => resources.value.size === 1 && resources.value.has('exam'));
const isPhysical = computed(() => resources.value.has('doctor') || resources.value.has('exam'));
const showSurgery = computed(() => resources.value.has('surgery'));
const EXAM_COLOR = TYPE_BY_KEY.exames.color;
const isSurgeryTask = t => t.task_type === 'cirurgia';
const typeVarsOf = key => {
  const tt = TYPE_BY_KEY[key] || GENERAL_TYPE;
  return {
    '--k-grad': tt.grad,
    '--k-deep': tt.deep,
    '--k-rgb': hexToRgbSpaced(tt.color),
    '--k-deep-rgb': hexToRgbSpaced(tt.deep),
  };
};
// "Nova avaliação" / "Agendar cirurgia" / "Novo agendamento" (geral)
const newLabel = computed(() =>
  isSurgeryMode.value ? 'Agendar cirurgia' : `${k.value.article === 'o' ? 'Novo' : 'Nova'} ${k.value.noun}`
);
const cap = s => s.charAt(0).toUpperCase() + s.slice(1);

// ── JANELAS DA SALA CIRÚRGICA (clínica parceira + dia + horário + bloco) ──
const surgeryWindows = computed(() => resolveSurgeryWindows(crmSettings.value));
const surgeryWindowsForDay = day => surgeryWindows.value.filter(w => w.dow === day.getDay());
const surgeryLocationLabel = key =>
  surgeryLocations.value.find(l => l.key === key)?.label || key || 'Local a definir';

// ── JANELA DE EXAMES (unidade + dia + horário + bloco) — 23/09 ──
const examWindows = computed(() => resolveExamWindows(crmSettings.value).filter(w => unitAllowed(w.unit)));
const examWindowsForDay = day =>
  examWindows.value
    .filter(w => w.dow === day.getDay())
    .filter(w => !activeUnit.value || w.unit === activeUnit.value);
const showExamWindowsModal = ref(false);
const editExamWindows = ref([]);
const isSavingExamWindows = ref(false);
const openExamWindowsModal = () => {
  editExamWindows.value = examWindows.value.map(w => ({ ...w }));
  showExamWindowsModal.value = true;
};
const addExamWindow = () => {
  editExamWindows.value.push({ dow: 1, unit: 'paulista', start: '08:00', end: '17:00', block: 30, exam: true });
};
const removeExamWindow = i => editExamWindows.value.splice(i, 1);
const saveExamWindows = async () => {
  if (isSavingExamWindows.value) return;
  isSavingExamWindows.value = true;
  try {
    const clean = editExamWindows.value
      .filter(w => w.start && w.end && w.unit)
      .map(w => ({ dow: Number(w.dow), unit: w.unit, start: w.start, end: w.end, block: Number(w.block) || 30 }));
    await CrmAPI.updateExamWindows(clean);
    await store.dispatch('crm/fetchSettings');
    showExamWindowsModal.value = false;
    useAlert('Janela de exames salva!');
  } catch {
    useAlert('Erro ao salvar a janela de exames.');
  } finally {
    isSavingExamWindows.value = false;
  }
};

const showSurgeryWindowsModal = ref(false);
const editSurgeryWindows = ref([]);
const isSavingSurgeryWindows = ref(false);
const openSurgeryWindowsModal = () => {
  editSurgeryWindows.value = surgeryWindows.value.map(w => ({ ...w }));
  showSurgeryWindowsModal.value = true;
};
const addSurgeryWindow = () => {
  editSurgeryWindows.value.push({
    dow: 1, location: surgeryLocations.value[0]?.key || '', start: '08:00', end: '12:00', block: 60,
  });
};
const removeSurgeryWindow = i => editSurgeryWindows.value.splice(i, 1);
const saveSurgeryWindows = async () => {
  if (isSavingSurgeryWindows.value) return;
  isSavingSurgeryWindows.value = true;
  try {
    const clean = editSurgeryWindows.value
      .filter(w => w.start && w.end && w.location)
      .map(w => ({ dow: Number(w.dow), location: w.location, start: w.start, end: w.end, block: Number(w.block) || 60 }));
    await CrmAPI.updateSurgeryWindows(clean);
    await store.dispatch('crm/fetchSettings');
    showSurgeryWindowsModal.value = false;
    useAlert('Janelas da sala cirúrgica salvas!');
  } catch {
    useAlert('Erro ao salvar as janelas.');
  } finally {
    isSavingSurgeryWindows.value = false;
  }
};

// ── Duração dos agendamentos (blocos proporcionais nas visões) ──
// O bloco da janela (médico ou sala) onde o horário cai define a duração.
// Sem janela: consulta 15 · teleconsulta 20 · exame 30 · cirurgia 60.
const DEFAULT_DURATION = { consultas: 15, teleconsultas: 20, exames: 30, cirurgias: 60 };
const taskDuration = task => {
  const d = new Date(task.due_at);
  const mins = d.getHours() * 60 + d.getMinutes();
  const list = isSurgeryTask(task) ? surgeryWindows.value : kindOf(task) === 'exames' ? examWindows.value : windows.value;
  const win = list.find(w => {
    if (!windowOnDay(w, d)) return false;
    if (task.unit && w.unit !== task.unit) return false;
    const [sh, sm] = w.start.split(':').map(Number);
    const [eh, em] = w.end.split(':').map(Number);
    return mins >= sh * 60 + sm && mins < eh * 60 + em;
  });
  if (win?.block) return Number(win.block);
  return DEFAULT_DURATION[kindOf(task)] || 15;
};

// Locais de cirurgia (clínicas parceiras — IOP etc.)
const DEFAULT_SURGERY_LOCATIONS = [
  { key: 'iop', label: 'IOP' },
  { key: 'ocular_surgery', label: 'Ocular Surgery' },
];
const LOCATION_COLORS = { iop: '#0EA5E9', ocular_surgery: '#64748B' };
const LOCATION_FALLBACK = ['#0EA5E9', '#64748B', '#0284C7', '#818CF8'];
const surgeryLocations = computed(() => {
  const list = crmSettings.value?.surgery_locations;
  const base = Array.isArray(list) && list.length ? list : DEFAULT_SURGERY_LOCATIONS;
  return base.map((l, i) => ({
    ...l,
    color: LOCATION_COLORS[l.key] || LOCATION_FALLBACK[i % LOCATION_FALLBACK.length],
  }));
});
const surgeryLocationOf = task => surgeryLocations.value.find(l => l.key === task.unit) || null;

const showLocationsModal = ref(false);
const locationsDraft = ref([]);
const isSavingLocations = ref(false);
const openLocationsModal = () => {
  locationsDraft.value = surgeryLocations.value.map(l => ({ ...l }));
  showLocationsModal.value = true;
};
const addLocationRow = () => locationsDraft.value.push({ key: '', label: '' });
const removeLocationRow = i => locationsDraft.value.splice(i, 1);
const slugifyLocation = text =>
  text.toString().trim().toLowerCase().normalize('NFD')
    .replace(/[̀-ͯ]/g, '').replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '');
const saveLocations = async () => {
  if (isSavingLocations.value) return;
  isSavingLocations.value = true;
  try {
    const list = locationsDraft.value
      .filter(l => l.label.trim())
      .map(l => ({ key: l.key || slugifyLocation(l.label), label: l.label.trim() }));
    await CrmAPI.updateSurgeryLocations(list);
    await store.dispatch('crm/fetchSettings');
    showLocationsModal.value = false;
    useAlert('Locais de cirurgia salvos');
  } catch {
    useAlert('Erro ao salvar os locais.');
  } finally {
    isSavingLocations.value = false;
  }
};

const WEEKDAYS = ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'];
const WEEKDAY_FULL = ['Domingo', 'Segunda', 'Terça', 'Quarta', 'Quinta', 'Sexta', 'Sábado'];
const VIEW_MODES = [
  { key: 'month', label: 'Mês', icon: 'i-lucide-calendar' },
  { key: 'week', label: 'Semana', icon: 'i-lucide-calendar-range' },
  { key: 'day', label: 'Dia', icon: 'i-lucide-calendar-check' },
];

// Unidades da clínica (+ "online" da teleconsulta)
const UNITS = {
  tatuape:  { label: 'Tatuapé',      short: 'TAT', color: '#2563EB' },
  paulista: { label: 'Av. Paulista', short: 'PAU', color: '#EA580C' },
};
const ONLINE = { label: 'Online', short: 'ON', color: '#7C3AED' };

const PROBLEMAS = [
  'Catarata', 'Refrativa', 'Ceratocone', 'Lentes Fácicas',
  'Consulta geral', 'Pós-operatório', 'Plástica ocular', 'Retorno de exames',
];
// 🔬 24/09 (pedido dele): OCT separado por tipo, curva tensional e os dois
// procedimentos a laser da quarta 14h (iridotomia e capsulotomia) entram na lista
const EXAMES = [
  'Pentacam', 'Topografia', 'OCT Córnea', 'OCT Retina', 'OCT Nervo', 'OCT Segmento',
  'Biometria', 'Paquimetria', 'Campo visual', 'Curva tensional diária',
  'Retinografia', 'Microscopia especular', 'Mapeamento de retina', 'Aberrometria',
  'Iridotomia', 'Capsulotomia',
];
const PROCEDURES = [
  'Catarata', 'Refrativa PRK', 'Refrativa Lasik', 'Lente Fácica',
  'Lente de Foco Estendido', 'Trifocal', 'Anel de Ferrara', 'Pterígio',
  'Capsulotomia YAG', 'Outro',
];
const procedureOptions = computed(() => {
  // 💰 item 324: os exames com valor pré-configurado entram na lista de sugestões
  if (form.value.kind === 'exames') return [...new Set([...(appointmentPrices.value?.exames || []).map(e => e.name), ...EXAMES])];
  if (form.value.kind === 'cirurgias') return PROCEDURES;
  return PROBLEMAS;
});
const procedureLabel = computed(() => {
  if (form.value.kind === 'exames') return 'Exame';
  if (form.value.kind === 'cirurgias') return 'Procedimento';
  return 'Problema';
});

// ── Médicos e janelas de avaliação da clínica ───────────────
const doctorColor = name => DOCTORS.find(d => d.name === name)?.color || '#64748B';
const doctorShort = name => DOCTORS.find(d => d.name === name)?.short || name;
// item 375: as aberturas pontuais (data fixa) somam às faixas da semana
const windows = computed(() =>
  [...resolveWindows(crmSettings.value), ...resolveOpenWindows(crmSettings.value)].filter(w => unitAllowed(w.unit))
);

// horários fechados com o cadeado ({date, time, unit})
const blockedList = computed(() => resolveBlocked(crmSettings.value));
const blockedSet = computed(() => new Set(blockedList.value.map(b => blockKey(b.date, b.time, b.unit))));
const isBlocked = (day, win, slot) => blockedSet.value.has(blockKey(dateKey(day), slot, win.unit));

const isSavingBlock = ref(false);
const toggleBlock = async (day, win, slot) => {
  if (isSavingBlock.value) return;
  isSavingBlock.value = true;
  try {
    const key = { date: dateKey(day), time: slot, unit: win.unit };
    const exists = blockedList.value.some(b => b.date === key.date && b.time === key.time && b.unit === key.unit);
    const next = exists
      ? blockedList.value.filter(b => !(b.date === key.date && b.time === key.time && b.unit === key.unit))
      : [...blockedList.value, { ...key, doctor: win.doctor }];
    await CrmAPI.updateAgendaBlocked(next);
    await store.dispatch('crm/fetchSettings');
    useAlert(exists ? 'Horário reaberto' : 'Horário fechado 🔒');
  } catch {
    useAlert('Erro ao atualizar o horário.');
  } finally {
    isSavingBlock.value = false;
  }
};

const isWeekend = day => day.getDay() === 0 || day.getDay() === 6;
// item 267: dia INTEIRO fechado (string) × PARTE do dia (unidade / médico)
const blockedDayList = computed(() => resolveBlockedDays(crmSettings.value));
const blockedDays = computed(() => wholeBlockedDays(blockedDayList.value));
const isDayBlocked = day => blockedDays.value.has(dateKey(day));
const partialBlocksFor = day => partialBlocksOn(blockedDayList.value, dateKey(day));
const isPartBlocked = (day, part) => partialBlocksFor(day).some(b => sameBlock(b, part));
// menu "Fechar dia": dia inteiro · só uma unidade · só um médico (dos que atendem no dia)
const blockMenu = ref(null);
const blockOptions = day => {
  const wins = rawWindowsForDay(day).filter(w => w.doctor);
  const units = [...new Set(wins.map(w => w.unit).filter(Boolean))];
  const doctors = [...new Set(wins.map(w => w.doctor))];
  return [
    { key: 'all', label: 'Dia inteiro', part: dateKey(day), on: isDayBlocked(day), icon: 'i-lucide-calendar-off' },
    ...units.map(u => ({ key: 'u:' + u, label: `Só ${(UNITS[u]?.label || u)}`, part: { date: dateKey(day), unit: u }, on: isPartBlocked(day, { date: dateKey(day), unit: u }), icon: 'i-lucide-building-2' })),
    ...doctors.map(d => ({ key: 'd:' + d, label: `Só ${doctorShort(d)}`, part: { date: dateKey(day), doctor: d }, on: isPartBlocked(day, { date: dateKey(day), doctor: d }), icon: 'i-lucide-stethoscope' })),
  ];
};
const dayBlockSummary = day => {
  if (isDayBlocked(day)) return 'fechado';
  const parts = partialBlocksFor(day);
  if (!parts.length) return '';
  return parts.map(b => (b.unit ? (UNITS[b.unit]?.label || b.unit) : doctorShort(b.doctor))).join(', ') + ' fechado';
};
// teleconsulta pode acontecer em qualquer dia útil, mesmo sem janela
// item 375: sábado/domingo com abertura pontual é dia de agenda
const hasOpenWindow = day => windows.value.some(w => w.date === dateKey(day));
const isDayOff = day => (isWeekend(day) && !hasOpenWindow(day)) || isDayBlocked(day);

const toggleBlockDay = async (day, part = null) => {
  if (isSavingBlock.value) return;
  isSavingBlock.value = true;
  try {
    const key = part || dateKey(day);
    const list = resolveBlockedDays(crmSettings.value);
    const exists = list.some(b => sameBlock(b, key));
    const next = exists ? list.filter(b => !sameBlock(b, key)) : [...list, key];
    await CrmAPI.updateAgendaBlockedDays(next);
    await store.dispatch('crm/fetchSettings');
    blockMenu.value = null;
    const what = typeof key === 'string' ? 'Dia' : key.unit ? (UNITS[key.unit]?.label || key.unit) : doctorShort(key.doctor);
    useAlert(exists ? `${what} reaberto ✓` : `${what} fechado 🔒`);
  } catch {
    useAlert('Erro ao atualizar o dia.');
  } finally {
    isSavingBlock.value = false;
  }
};

const slotsFor = sharedSlotsFor;

// janelas de um dia no trilho ativo: consultas/exames = médicos (com o
// filtro de unidade/médico); cirurgias = sala cirúrgica; teleconsultas = nenhuma
// item 234: com várias camadas ligadas, as janelas dos recursos ligados se
// somam (médicos + sala cirúrgica); a janela de exames (dia inteiro) só
// aparece quando a camada de exames está sozinha, para não sujar a semana
const rawWindowsForDay = day => {
  const out = [];
  if (resources.value.has('doctor')) {
    out.push(...windows.value
      .filter(w => windowOnDay(w, day))
      .filter(w => !activeUnit.value || w.unit === activeUnit.value)
      .filter(w => !activeDoctor.value || w.doctor === activeDoctor.value));
  }
  if (isExam.value) out.push(...examWindowsForDay(day));
  if (showSurgery.value) out.push(...surgeryWindowsForDay(day));
  return out;
};
// item 267: janela de unidade/médico fechada naquele dia some da grade
const windowsForDay = day =>
  rawWindowsForDay(day).filter(w => !isWindowBlocked(blockedDayList.value, dateKey(day), w));
const toMin = hm => {
  const [h, m] = hm.split(':').map(Number);
  return h * 60 + m;
};
// faixas para a coluna de horas (semana/dia)
const bandsForDay = day =>
  windowsForDay(day).map(w => ({
    key: (w.doctor || w.unit) + w.start,
    startMin: toMin(w.start),
    endMin: toMin(w.end),
    block: Number(w.block) || 15,
    color: winColor(w),
    label: (w.doctor ? doctorShort(w.doctor) : w.exam ? 'Exames' : surgeryLocationLabel(w.unit)) + (reservedFor(w).length ? ` · só ${reservedLabelOf(w)}` : ''),
    title: `${winTitle(w)} · ${w.start}–${w.end} (${winUnitLabel(w)}) · blocos de ${w.block} min` + (reservedFor(w).length ? ` · faixa reservada para ${reservedLabelOf(w)}` : ''),
    unit: w.unit,
    doctor: w.doctor || '',
    only: reservedFor(w), // item 307: faixa reservada → o agendamento já nasce do tipo certo
    exam: Boolean(w.exam), // item 283: o menu do botão direito sabe de quem é a faixa
  }));

// item 255 (26/09, regra dele: "os pacientes do Oftalmofácil concorrem pelos
// mesmos horários — evitar agendamento duplo"): quem OCUPA um bloco vem de
// TODOS os agendamentos do dia (não só das camadas/filtros ligados) e conta
// na sala cirúrgica e na agenda de exames, por SOBREPOSIÇÃO de tempo; na janela
// do MÉDICO, só o bloco em que começa (item 297). Agendamento do Oftalmofácil
// ocupa qualquer janela da unidade dele (consulta, exame ou sala cirúrgica).
const liveTasksByDay = computed(() => {
  const map = {};
  liveTasks.value.forEach(task => {
    const key = format(new Date(task.due_at), 'yyyy-MM-dd');
    (map[key] ||= []).push(task);
  });
  Object.values(map).forEach(arr => arr.sort((a, b) => new Date(a.due_at) - new Date(b.due_at)));
  return map;
});
const isSurgeryWin = win => !win.doctor && !win.exam;
// ⏱️ item 367 (23/10, ele: "limitar em 4 avaliações por hora, em todos os horários"):
// os robôs nunca passam do teto; aqui a equipe VÊ a hora cheia e pode encaixar, confirmando.
// Contam avaliação e consulta sem tipo (inclusive do Oftalmofácil); exame, retorno,
// pós-op e teleconsulta não contam — a mesma conta do servidor (Crm::AgendaSlots)
const NOT_EVALUATION = ['exames', 'retorno', 'pos_op', 'teleconsulta'];
const hourlyCap = computed(() => crmSettings.value?.agenda_hourly_evaluations || 4);
const isEvaluationTask = t => t.task_type === 'consulta' && !NOT_EVALUATION.includes(t.modality) && t.unit && t.unit !== ONLINE_UNIT;
const hourOf = t => String(Math.floor(taskStartMin(t) / 60)).padStart(2, '0');
const evaluationsInHour = (dayKey, hh, unit, skipId) =>
  (liveTasksByDay.value[dayKey] || []).filter(t => t.id !== skipId && t.unit === unit && isEvaluationTask(t) && hourOf(t) === hh).length;
const hourLoad = (day, win, hh) => evaluationsInHour(format(day, 'yyyy-MM-dd'), hh, win.unit);
const hoursOfWin = win => [...new Set(slotsFor(win).map(s => s.slice(0, 2)))];
const countsHourly = win => Boolean(win.doctor) && !win.exam;
const hourFull = (day, win, slot) => countsHourly(win) && hourLoad(day, win, slot.slice(0, 2)) >= hourlyCap.value;
const taskStartMin = t => {
  const d = new Date(t.due_at);
  return d.getHours() * 60 + d.getMinutes();
};
// 🔪 item 330 (06/10, pedido dele: "as cirurgias não devem ocupar a agenda dos
// médicos; a agenda cirúrgica é independente"): cada agendamento ocupa SÓ a
// agenda do seu tipo — cirurgia na sala cirúrgica, exame na agenda de exames,
// consulta (avaliação, retorno, pós-op) na janela do médico. Vale também para
// o que vem do Oftalmofácil (antes o item do hub ocupava qualquer janela da unidade).
const occupiesWindow = (t, win) => {
  const fromHub = t.source === 'oftalmofacil';
  const unitOk = t.unit ? t.unit === win.unit : !fromHub;
  if (!unitOk) return false;
  if (isSurgeryWin(win)) return isSurgeryTask(t);
  if (isSurgeryTask(t)) return false;
  return win.exam ? kindOf(t) === 'exames' : kindOf(t) !== 'exames';
};
// item 330: retorno e pós-operatório são consultas "menos prioritárias" — ficam
// no bloco do médico, mas com balão mais leve, lembrando que podem receber encaixe
const isSoftTask = t => !isSurgeryTask(t) && ['retorno', 'pos_op'].includes(t.modality);
const softTitle = t => (isSoftTask(t) ? ' · retorno/pós-op (pode receber encaixe de avaliação)' : '');
// agendamentos ocupando um bloco (pode haver ENCAIXE: 2+ no mesmo horário)
const tasksAtSlotAll = (day, win, slot) => {
  const slotStart = toMin(slot);
  return (liveTasksByDay.value[format(day, 'yyyy-MM-dd')] || []).filter(t => {
    if (!occupiesWindow(t, win)) return false;
    // item 297: na janela do médico, só o bloco em que o agendamento começa
    return occupiesSlot({
      taskStart: taskStartMin(t),
      duration: taskDuration(t),
      slotStart,
      block: win.block,
      win,
    });
  });
};
const taskAtSlot = (day, win, slot) => tasksAtSlotAll(day, win, slot)[0];
// item 319 (03/10, pedido dele: "no espaço do médico, precisamos que apareçam
// todos os contatos, independente do horário"): agendamento do dia que NÃO cai
// em nenhum bloco de nenhuma faixa (ex.: 10:30 numa faixa 08:30–10:00) aparece
// no fim do bloco do médico dele — ou, sem médico igual, na faixa mais próxima.
const outsideTasksByWin = day => {
  const wins = windowsForDay(day);
  const placed = new Set();
  wins.forEach(w => slotsFor(w).forEach(slot => tasksAtSlotAll(day, w, slot).forEach(t => placed.add(t.id))));
  const map = new Map();
  (liveTasksByDay.value[format(day, 'yyyy-MM-dd')] || []).forEach(t => {
    if (placed.has(t.id)) return;
    const homes = wins.filter(w => occupiesWindow(t, w));
    if (!homes.length) return;
    const start = taskStartMin(t);
    const gap = w => Math.min(Math.abs(start - toMin(w.start)), Math.abs(start - toMin(w.end)));
    const sameDoctor = homes.filter(w => w.doctor && t.doctor && w.doctor === t.doctor);
    const home = (sameDoctor.length ? sameDoctor : homes).reduce((a, b) => (gap(b) < gap(a) ? b : a));
    map.set(home, [...(map.get(home) || []), t]);
  });
  return map;
};
const outsideTasks = (day, win) => outsideTasksByWin(day).get(win) || [];

// 🗂️ item 307 (01/10): CONFIGURAÇÕES DA AGENDA — abrir e fechar, faixas de
// horário (com o "serve para"), regras para a IA e conferência do dia num
// lugar só (AgendaSettings.vue). Substitui o popup "Janelas dos médicos".
const showSettings = ref(false);
const settingsTab = ref('abrir');
const openSettings = (tabKey = 'abrir') => {
  settingsTab.value = tabKey;
  showSettings.value = true;
};

// médico com a agenda fechada (item 76) — o abrir/fechar mora nas Configurações
const closedDoctors = computed(() => crmSettings.value?.agenda_closed_doctors || []);
const isDoctorClosed = name => closedDoctors.value.includes(name);

// ── etiquetas + resposta de formulário na lista do dia (item 76) ──
const dayDetails = ref({});
const formAnswersTask = ref(null);
const loadDayDetails = async () => {
  const ids = dayViewTasks.value.filter(t => t.contact_id).map(t => t.id);
  if (!ids.length) {
    dayDetails.value = {};
    return;
  }
  try {
    const { data } = await CrmAPI.getAgendaDayDetails(ids);
    dayDetails.value = Object.fromEntries(data.map(d => [d.task_id, d]));
  } catch {
    dayDetails.value = {};
  }
};
watch([viewMode, cursor, layers, allTasks], () => {
  if (viewMode.value === 'day') loadDayDetails();
});
const detailOf = task => dayDetails.value[task.id] || null;
const openFormAnswers = task => {
  const detail = detailOf(task);
  if (detail?.form_response) formAnswersTask.value = { name: displayName(task), detail };
};

// ── Filtro ──
const activeUnit = computed(() => (view.value.startsWith('unit:') ? view.value.slice(5) : null));
const activeDoctor = computed(() => (view.value.startsWith('doctor:') ? view.value.slice(7) : null));
const isPersonalView = computed(() => view.value === 'me' || /^\d+$/.test(view.value));

// tudo o que está no calendário (canceladas ficam fora; continuam no banco)
const liveTasks = computed(() => allTasks.value.filter(x => x.due_at && !x.canceled_at).filter(x => !UNITS[x.unit] || unitAllowed(x.unit)));
// unidades que a pessoa pode ver (chips e seletor); vazio no servidor = todas
const visibleUnits = computed(() => Object.fromEntries(Object.entries(UNITS).filter(([key]) => unitAllowed(key))));
const inKind = t => layers.value.has(typeOf(t)); // item 234: camadas por tipo

// 🏥 item 228: origem do agendamento — 'all' | 'cevico' (nasceu aqui) | 'oftalmofacil'
const originFilter = ref('all');
const inOrigin = t => originFilter.value === 'all' || (originFilter.value === 'cevico' ? !t.source : t.source === originFilter.value);
const hasExternal = computed(() => liveTasks.value.some(t => t.source));
const originLabel = t => (t.source === 'oftalmofacil' ? (t.source_detail || 'Oftalmofácil') : '');

const visibleTasks = computed(() => {
  const list = liveTasks.value.filter(inOrigin);
  if (view.value === 'clinic') return list.filter(isAppointment).filter(inKind);
  if (activeUnit.value) return list.filter(x => x.unit === activeUnit.value).filter(inKind);
  if (activeDoctor.value) return list.filter(x => isAppointment(x) && x.doctor === activeDoctor.value).filter(inKind);
  // 🔁 item 343: card da Equipe dos retornos aparece em "Minha agenda" de quem é da equipe
  if (view.value === 'me') return list.filter(x => !x.unit && (x.assignee?.id === currentUser.value.id || x.retorno_team));
  return list.filter(x => !x.unit && x.assignee?.id === Number(view.value));
});

const tasksByDay = computed(() => {
  const map = {};
  visibleTasks.value.forEach(task => {
    const key = format(new Date(task.due_at), 'yyyy-MM-dd');
    (map[key] ||= []).push(task);
  });
  Object.values(map).forEach(arr => arr.sort((a, b) => new Date(a.due_at) - new Date(b.due_at)));
  return map;
});
const dayTasks = day => tasksByDay.value[format(day, 'yyyy-MM-dd')] || [];
const isToday = day => isSameDay(day, new Date());
const inMonth = day => isSameMonth(day, cursor.value);

// ── intervalo visível (mês/semana/dia) → contagem por tipo no seletor ──
const rangeStart = computed(() => {
  if (viewMode.value === 'month') return startOfWeek(startOfMonth(cursor.value), { weekStartsOn: 0 });
  if (viewMode.value === 'week') return startOfWeek(cursor.value, { weekStartsOn: 0 });
  const d = new Date(cursor.value); d.setHours(0, 0, 0, 0); return d;
});
const rangeEnd = computed(() => {
  if (viewMode.value === 'month') return addDays(endOfWeek(endOfMonth(cursor.value), { weekStartsOn: 0 }), 1);
  if (viewMode.value === 'week') return addDays(startOfWeek(cursor.value, { weekStartsOn: 0 }), 7);
  return addDays(rangeStart.value, 1);
});
const kindCounts = computed(() => {
  const out = Object.fromEntries(TYPES.map(t => [t.key, 0]));
  const s = rangeStart.value.getTime();
  const e = rangeEnd.value.getTime();
  liveTasks.value.filter(isAppointment).forEach(t => {
    const ts = new Date(t.due_at).getTime();
    if (ts >= s && ts < e) out[typeOf(t)] += 1;
  });
  return out;
});
const rangeNoun = computed(() =>
  ({ month: 'no mês', week: 'na semana', day: 'no dia' })[viewMode.value]
);

// ── Calendário interativo (popover do rótulo do período) ──
const showDatePicker = ref(false);
const pickerCursor = ref(new Date());
const toggleDatePicker = () => {
  pickerCursor.value = new Date(cursor.value);
  showDatePicker.value = !showDatePicker.value;
};
const pickerLabel = computed(() =>
  cap(pickerCursor.value.toLocaleDateString('pt-BR', { month: 'long', year: 'numeric' }))
);
const pickerWeeks = computed(() => {
  const start = startOfWeek(startOfMonth(pickerCursor.value), { weekStartsOn: 0 });
  const end = endOfWeek(endOfMonth(pickerCursor.value), { weekStartsOn: 0 });
  const days = [];
  let d = start;
  while (d <= end) { days.push(d); d = addDays(d, 1); }
  const out = [];
  for (let i = 0; i < days.length; i += 7) out.push(days.slice(i, i + 7));
  return out;
});
const pickDate = day => {
  cursor.value = new Date(day);
  showDatePicker.value = false;
};

// ── item 244: MINI CALENDÁRIO sempre aberto (bloco "Calendário", como o do
// Google Agenda): segue a data escolhida, destaca a semana/dia em vista, e as
// setas dele só folheiam os meses (sem mudar a agenda até clicar num dia) ──
const miniCursor = ref(startOfMonth(new Date()));
watch(cursor, c => { miniCursor.value = startOfMonth(c); }, { immediate: true });
const miniLabel = computed(() =>
  cap(miniCursor.value.toLocaleDateString('pt-BR', { month: 'long', year: 'numeric' }))
);
const miniWeeks = computed(() => {
  const start = startOfWeek(startOfMonth(miniCursor.value), { weekStartsOn: 0 });
  const out = [];
  let d = start;
  for (let w = 0; w < 6; w += 1) {
    const week = [];
    for (let i = 0; i < 7; i += 1) { week.push(d); d = addDays(d, 1); }
    out.push(week);
  }
  // 6ª linha inteira do mês seguinte? corta
  return isSameMonth(out[5][0], miniCursor.value) ? out : out.slice(0, 5);
});
const miniInView = day => {
  if (viewMode.value === 'day') return isSameDay(day, cursor.value);
  if (viewMode.value === 'week') {
    const a = startOfWeek(cursor.value, { weekStartsOn: 0 });
    const b = endOfWeek(cursor.value, { weekStartsOn: 0 });
    return day >= a && day <= b;
  }
  return false;
};
const miniPick = day => { cursor.value = new Date(day); };

// ── Navegação ──
const step = dir => {
  if (viewMode.value === 'month') cursor.value = addMonths(cursor.value, dir);
  else if (viewMode.value === 'week') cursor.value = addWeeks(cursor.value, dir);
  else cursor.value = addDays(cursor.value, dir);
};
const goToday = () => { cursor.value = new Date(); };
const stepLabel = computed(() => ({ month: 'mês', week: 'semana', day: 'dia' })[viewMode.value]);

const navLabel = computed(() => {
  if (viewMode.value === 'month') {
    return cap(cursor.value.toLocaleDateString('pt-BR', { month: 'long', year: 'numeric' }));
  }
  if (viewMode.value === 'week') {
    const start = startOfWeek(cursor.value, { weekStartsOn: 0 });
    const end = endOfWeek(cursor.value, { weekStartsOn: 0 });
    const sameMonth = start.getMonth() === end.getMonth();
    const a = start.toLocaleDateString('pt-BR', { day: 'numeric', month: sameMonth ? undefined : 'short' });
    const b = end.toLocaleDateString('pt-BR', { day: 'numeric', month: 'long', year: 'numeric' });
    return `${a} – ${cap(b)}`.replace(' de ', ' de ');
  }
  return cap(cursor.value.toLocaleDateString('pt-BR', { weekday: 'long', day: 'numeric', month: 'long' }));
});
const navSub = computed(() => {
  if (viewMode.value === 'week') {
    const start = startOfWeek(cursor.value, { weekStartsOn: 0 });
    return `semana ${format(start, 'dd/MM')} – ${format(endOfWeek(cursor.value, { weekStartsOn: 0 }), 'dd/MM')}`;
  }
  if (viewMode.value === 'day') return format(cursor.value, 'dd/MM/yyyy');
  return '';
});

// grade mensal (6 semanas, domingo primeiro)
const weeks = computed(() => {
  const start = startOfWeek(startOfMonth(cursor.value), { weekStartsOn: 0 });
  const end = endOfWeek(endOfMonth(cursor.value), { weekStartsOn: 0 });
  const days = [];
  let d = start;
  while (d <= end) { days.push(d); d = addDays(d, 1); }
  const result = [];
  for (let i = 0; i < days.length; i += 7) result.push(days.slice(i, i + 7));
  return result;
});
const MONTH_MAX = 4;

// dias da semana da visão semanal — "esconder sáb/dom" (item 76)
const hideWeekend = ref(localStorage.getItem('cevico_agenda_hide_weekend') !== '0');
const toggleWeekend = () => {
  hideWeekend.value = !hideWeekend.value;
  localStorage.setItem('cevico_agenda_hide_weekend', hideWeekend.value ? '1' : '0');
};
// 24/09 (pedido dele): a metade direita da visão Dia (bloco do médico +
// conferência) RECOLHE, como a barra lateral; o calendário ganha a largura toda.
const dayPanelOpen = ref(localStorage.getItem('cevico_agenda_day_panel') !== '0');
// 26/09 (pedido dele: "pegar o layout de caixas" da agenda do Oftalmofácil):
// o painel do dia tem dois jeitos — 'itens' = só as caixas do dia, limpas
// (hora | quem e o quê | selos), como no Oftalmofácil; 'bloco' = o bloco do
// médico com a grade de horários + conferência com os botões. Fica gravado.
const dayPanelMode = ref(localStorage.getItem('cevico_agenda_day_panel_mode') === 'bloco' ? 'bloco' : 'itens');
const setDayPanelMode = mode => {
  dayPanelMode.value = mode;
  localStorage.setItem('cevico_agenda_day_panel_mode', mode);
};
const toggleDayPanel = () => {
  dayPanelOpen.value = !dayPanelOpen.value;
  localStorage.setItem('cevico_agenda_day_panel', dayPanelOpen.value ? '1' : '0');
};
// DIVISOR ARRASTÁVEL entre o calendário e o painel (o "puxador" de janela que
// ele mostrou): arrasta para regular a largura; arrastou até quase o fim =
// recolhe; clique duplo = volta ao padrão. Lembrado por pessoa.
const DAY_PANEL_DEFAULT = 58;
const dayPanelPct = ref(Number(localStorage.getItem('cevico_agenda_day_panel_pct')) || DAY_PANEL_DEFAULT);
const dayLayoutEl = ref(null);
const isSplitting = ref(false);
const startSplit = e => {
  const box = dayLayoutEl.value?.getBoundingClientRect();
  if (!box) return;
  e.preventDefault();
  isSplitting.value = true;
  const move = ev => {
    const pct = ((box.right - ev.clientX) / box.width) * 100;
    if (pct < 16) { dayPanelOpen.value = false; localStorage.setItem('cevico_agenda_day_panel', '0'); stop(); return; }
    dayPanelPct.value = Math.round(Math.min(72, Math.max(26, pct)));
  };
  const stop = () => {
    isSplitting.value = false;
    localStorage.setItem('cevico_agenda_day_panel_pct', String(dayPanelPct.value));
    window.removeEventListener('pointermove', move);
    window.removeEventListener('pointerup', stop);
  };
  window.addEventListener('pointermove', move);
  window.addEventListener('pointerup', stop);
};
const resetSplit = () => {
  dayPanelPct.value = DAY_PANEL_DEFAULT;
  localStorage.setItem('cevico_agenda_day_panel_pct', String(DAY_PANEL_DEFAULT));
};
const weekDays = computed(() => {
  const start = startOfWeek(cursor.value, { weekStartsOn: 0 });
  const days = Array.from({ length: 7 }, (_, i) => addDays(start, i));
  return hideWeekend.value ? days.filter(d => d.getDay() !== 0 && d.getDay() !== 6) : days;
});
const weekGridCols = computed(() => `56px repeat(${weekDays.value.length}, minmax(0, 1fr))`);

// ── grade de horas: o expediente (08–18) esticado até cobrir TUDO o que
//    está marcado no intervalo (nada fica escondido) ──
const WEEK_ROW_PX = 64;
const DAY_ROW_PX = 96;
const hourSpan = (tasks, baseStart, baseEnd) => {
  let start = baseStart;
  let end = baseEnd;
  tasks.forEach(t => {
    const d = new Date(t.due_at);
    const h = d.getHours();
    const endH = Math.ceil((h * 60 + d.getMinutes() + taskDuration(t)) / 60);
    if (h < start) start = h;
    if (endH > end) end = endH;
  });
  return { start: Math.max(6, start), end: Math.min(23, Math.max(end, start + 1)) };
};
const weekTasks = computed(() => weekDays.value.flatMap(d => dayTasks(d)));
const weekSpan = computed(() => hourSpan(weekTasks.value, 8, 18));
const weekHours = computed(() =>
  Array.from({ length: weekSpan.value.end - weekSpan.value.start }, (_, i) => weekSpan.value.start + i)
);
const dayViewTasks = computed(() => dayTasks(cursor.value));
const daySpan = computed(() => hourSpan(dayViewTasks.value, 7, 20));
const dayHours = computed(() =>
  Array.from({ length: daySpan.value.end - daySpan.value.start }, (_, i) => daySpan.value.start + i)
);

// linha de AGORA (atualiza a cada minuto)
const nowMinutes = ref(new Date().getHours() * 60 + new Date().getMinutes());
let nowTimer = null;

// ── Helpers de exibição ──
// item 300: sem o prefixo do tipo e sem o ✅ digitado à mão (virou selo)
const displayName = task => patientNameOf(task);
const chipTime = task =>
  new Date(task.due_at).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
const unitOf = task => {
  if (task.unit === ONLINE_UNIT) return ONLINE;
  if (task.unit && UNITS[task.unit]) return UNITS[task.unit];
  if (isSurgeryTask(task)) {
    const loc = surgeryLocationOf(task);
    if (loc) return { label: loc.label, color: loc.color };
  }
  return null;
};
// item 234: cor do balão = a cor do TIPO (avaliação, retorno, pós-op, exame,
// tele, cirurgia); a unidade/local vira uma etiqueta pequena no balão
// item 290: a cor que a equipe deu ao agendamento vence a cor do tipo (balão
// e cartão); o selo do TIPO continua sempre na cor do tipo
const typeColorOf = task => TYPE_BY_KEY[typeOf(task)]?.color || '#2563EB';
const accentOf = task => taskColorOf(task) || typeColorOf(task);
const tagOf = task => {
  const u = unitOf(task);
  return u ? { label: u.label, short: u.short || u.label.slice(0, 3).toUpperCase() } : null;
};
const evVars = task => ({ '--ev': accentOf(task), '--ev-rgb': hexToRgbSpaced(accentOf(task)) });
const modalityOf = task =>
  MODALITIES.find(m => m.key === (task.modality || 'avaliacao')) || MODALITIES[0];
const kindLabelOf = task => TYPE_BY_KEY[typeOf(task)]?.noun || 'consulta';

// ── Ocupação da agenda (% preenchida — dia/semana/mês) ──
// Consultas e exames dividem os MESMOS blocos dos médicos (a ocupação soma
// os dois — é o que o Atendente enxerga ao oferecer horários); cirurgias
// contra a sala cirúrgica; teleconsulta não ocupa bloco físico.
const occWindows = computed(() => {
  if (isSurgeryMode.value) return surgeryWindows.value;
  if (isExam.value) return examWindows.value.filter(w => !activeUnit.value || w.unit === activeUnit.value);
  return windows.value
    .filter(w => !activeUnit.value || w.unit === activeUnit.value)
    .filter(w => !activeDoctor.value || w.doctor === activeDoctor.value);
});
const occTasks = computed(() => {
  const base = liveTasks.value.filter(isAppointment);
  if (isSurgeryMode.value) return base.filter(isSurgeryTask);
  if (isExam.value) {
    return base.filter(t => kindOf(t) === 'exames').filter(t => !activeUnit.value || t.unit === activeUnit.value);
  }
  return base
    .filter(t => !isSurgeryTask(t) && t.unit !== ONLINE_UNIT && kindOf(t) !== 'exames')
    .filter(t => !activeUnit.value || t.unit === activeUnit.value)
    .filter(t => !activeDoctor.value || t.doctor === activeDoctor.value);
});
const occScan = (from, days) =>
  scanAgenda({
    windows: occWindows.value,
    tasks: occTasks.value,
    blockedSet: blockedSet.value,
    blockedDays: blockedDays.value,
    from,
    days,
    freeLimit: 0,
  });
const showOccupancy = computed(() => {
  if (isTele.value || isPersonalView.value) return false;
  return occWindows.value.length > 0;
});
const showOccDetail = ref(false);
const occDay = computed(() => occScan(cursor.value, 1));
const occWeek = computed(() => occScan(startOfWeek(cursor.value, { weekStartsOn: 0 }), 7));
const occMonth = computed(() => occScan(startOfMonth(cursor.value), endOfMonth(cursor.value).getDate()));
const occCurrent = computed(() =>
  ({ month: occMonth.value, week: occWeek.value, day: occDay.value })[viewMode.value]
);
const occRows = computed(() => [
  { key: 'day', label: 'Dia', sub: format(cursor.value, 'dd/MM'), scan: occDay.value },
  { key: 'week', label: 'Semana', sub: `${format(startOfWeek(cursor.value, { weekStartsOn: 0 }), 'dd/MM')}–${format(endOfWeek(cursor.value, { weekStartsOn: 0 }), 'dd/MM')}`, scan: occWeek.value },
  { key: 'month', label: 'Mês', sub: cap(cursor.value.toLocaleDateString('pt-BR', { month: 'long' })), scan: occMonth.value },
]);
const occColor = pct => {
  if (pct >= 80) return '#EF4444';
  if (pct >= 50) return '#D4A017';
  return '#22C55E';
};
const OCC_MODALITIES = MODALITIES.filter(m => m.key !== 'teleconsulta');
const occSegments = scan => {
  if (isSurgeryMode.value || !scan.total) return null;
  return OCC_MODALITIES
    .map(m => ({ ...m, count: scan.byModality?.[m.key] || 0 }))
    .filter(s => s.count > 0)
    .map(s => ({ ...s, pct: Math.round((s.count / scan.total) * 100) }));
};
const occCaption = scan => {
  if (!scan.total) return null;
  const parts = (occSegments(scan) || []).map(s => `${s.label} ${s.pct}%`).join(' · ');
  return parts || null;
};
const dayOccupancy = day => {
  if (isDayOff(day) || !showOccupancy.value) return null;
  if (!occWindows.value.some(w => windowOnDay(w, day))) return null;
  return occScan(day, 1);
};

// rótulos/cores da janela — médicos (consultas) OU sala cirúrgica (cirurgias)
const winColor = win => {
  if (win.exam) return EXAM_COLOR;
  return win.doctor ? doctorColor(win.doctor) : surgeryLocations.value.find(l => l.key === win.unit)?.color || k.value.color;
};
const winTitle = win => {
  if (win.exam) return `Exames — ${UNITS[win.unit]?.label || win.unit}`;
  return win.doctor || `Sala cirúrgica — ${surgeryLocationLabel(win.unit)}`;
};
const winUnitLabel = win => (win.doctor || win.exam ? UNITS[win.unit]?.label : surgeryLocationLabel(win.unit));
const winVars = win => ({ '--w': winColor(win), '--w-rgb': hexToRgbSpaced(winColor(win)), '--w-deep': winColor(win) });
// item 307: para que serve a faixa ("Pós-operatório", "Retorno + Pós-operatório"; '' = tudo)
const reservedLabelOf = win => reservedFor(win).map(key => TYPE_BY_KEY[key]?.label).join(' + ');
const winOccupancy = (day, win) => {
  const slots = slotsFor(win).filter(s => !isBlocked(day, win, s));
  const filled = slots.filter(s => taskAtSlot(day, win, s)).length;
  const total = slots.length;
  return { filled, total, pct: total ? Math.round((filled / total) * 100) : 0 };
};

// ── KPIs do trilho ativo ──
const clinicTasks = computed(() =>
  liveTasks.value.filter(x => isAppointment(x) && x.status !== 'done').filter(inKind)
);
const kpiToday = computed(() => clinicTasks.value.filter(x => isSameDay(new Date(x.due_at), new Date())).length);
const kpiWeek = computed(() => {
  const start = startOfWeek(new Date(), { weekStartsOn: 0 });
  const end = endOfWeek(new Date(), { weekStartsOn: 0 });
  return clinicTasks.value.filter(x => { const d = new Date(x.due_at); return d >= start && d <= end; }).length;
});
const kpiPlaces = computed(() => {
  const inCursorMonth = clinicTasks.value.filter(x => isSameMonth(new Date(x.due_at), cursor.value));
  if (isTele.value) return [{ key: ONLINE_UNIT, label: 'Online — no mês', color: ONLINE.color, n: inCursorMonth.length }];
  const places = isSurgeryMode.value
    ? surgeryLocations.value.slice(0, 2).map(l => ({ key: l.key, label: l.label, color: l.color }))
    : Object.entries(visibleUnits.value).map(([key, u]) => ({ key, label: u.label, color: u.color }));
  return places.map(p => ({ ...p, label: `${p.label} — no mês`, n: inCursorMonth.filter(x => x.unit === p.key).length }));
});

// ── Fetch ──
const fetchTasks = async () => {
  isLoading.value = true;
  try {
    await store.dispatch('tasks/fetch');
  } catch {
    useAlert('Erro ao carregar a agenda.');
  } finally {
    isLoading.value = false;
  }
};

onMounted(async () => {
  // deep-links: ?date=AAAA-MM-DD abre o DIA; ?kind=exames abre o trilho;
  // ?view=month|week|day escolhe a visão
  if (route.query.date) {
    const d = new Date(`${route.query.date}T12:00:00`);
    if (!Number.isNaN(d.getTime())) {
      cursor.value = d;
      viewMode.value = 'day';
    }
  }
  // item 234: ?types=exames,cirurgia liga só essas camadas; ?kind= (links antigos) também vale
  if (route.query.types) {
    const wanted = String(route.query.types).split(',').filter(x => TYPE_BY_KEY[x]);
    if (wanted.length) layers.value = new Set(wanted);
  } else if (route.query.kind && LEGACY_KIND_TO_TYPES[route.query.kind]) {
    layers.value = new Set(LEGACY_KIND_TO_TYPES[route.query.kind]);
  }
  if (['month', 'week', 'day'].includes(route.query.view)) viewMode.value = route.query.view;
  if (!agents.value.length) store.dispatch('agents/get');
  await store.dispatch('crm/fetchSettings').catch(() => {});
  fetchTasks();
  loadCrmStages();
  nowTimer = setInterval(() => {
    const n = new Date();
    nowMinutes.value = n.getHours() * 60 + n.getMinutes();
  }, 60000);
});
onBeforeUnmount(() => clearInterval(nowTimer));

// ── Modal criar/editar ──
const showModal = ref(false);
const editingTask = ref(null);
const isSaving = ref(false);
const showDeleteConfirm = ref(false);

// ── 🗑️ item 304 (30/09): LIXEIRA — "Excluir" não apaga mais; o agendamento
// fica cancelado com quem/quando e pode voltar. Em 30/09 a equipe apagou os
// "duplicados" do Tatuapé (item 297) e os retornos sumiram sem rastro.
const showTrash = ref(false);
const trashBusyId = ref(0);
const TRASH_DAYS = 60;
const trashTasks = computed(() => {
  const floor = Date.now() - TRASH_DAYS * 24 * 60 * 60 * 1000;
  return allTasks.value
    .filter(t => t.canceled_at && t.due_at && new Date(t.canceled_at).getTime() >= floor)
    .sort((a, b) => new Date(b.canceled_at) - new Date(a.canceled_at));
});
const REASON_LABELS = {
  excluida_agenda: 'excluído na Agenda',
  cancelada_equipe: 'cancelado pela equipe',
  paciente_desmarcou: 'paciente desmarcou (Secretário da Agenda)',
  oftalmofacil: 'cancelado no Oftalmofácil',
};
const trashWho = t => {
  const when = t.canceled_at
    ? `${new Date(t.canceled_at).toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' })} às ${new Date(t.canceled_at).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}`
    : '';
  const reason = REASON_LABELS[t.cancel_reason] || (t.source === 'oftalmofacil' ? 'cancelado no Oftalmofácil' : 'cancelado pelo sistema');
  const by = t.canceled_by?.name ? `por ${t.canceled_by.name}` : '';
  return [reason, by, when].filter(Boolean).join(' · ');
};
const trashWhen = t =>
  `${new Date(t.due_at).toLocaleDateString('pt-BR', { weekday: 'short', day: '2-digit', month: '2-digit' })} ${hmOf(t)}`;
const restoreTask = async t => {
  trashBusyId.value = t.id;
  try {
    await store.dispatch('tasks/update', { id: t.id, canceled: false });
    useAlert(`${displayName(t)} voltou para a Agenda (${trashWhen(t)}).`);
  } catch {
    useAlert('Não consegui restaurar.');
  } finally {
    trashBusyId.value = 0;
  }
};
const purgeTask = async t => {
  if (!window.confirm(`Apagar DE VEZ ${displayName(t)} (${trashWhen(t)})? Não dá para voltar atrás.`)) return;
  trashBusyId.value = t.id;
  try {
    await store.dispatch('tasks/purge', t.id);
    useAlert('Apagado de vez.');
  } catch {
    useAlert('Não consegui apagar.');
  } finally {
    trashBusyId.value = 0;
  }
};

const defaultModality = key => ({ consultas: 'avaliacao', teleconsultas: 'teleconsulta', exames: 'exames', cirurgias: '' })[key] || 'avaliacao';
const defaultUnit = key => {
  if (key === 'teleconsultas') return ONLINE_UNIT;
  if (key === 'cirurgias') return surgeryLocations.value[0]?.key || '';
  return activeUnit.value || 'tatuape';
};
const emptyForm = (day, prefill = {}) => {
  const key = prefill.kind || k.value.kind || 'consultas';
  return {
    kind: key,
    name: prefill.name || '',
    phone: prefill.phone || '',
    procedure: prefill.procedure || '',
    doctor: prefill.doctor || activeDoctor.value || '',
    modality: prefill.modality || defaultModality(key),
    date: format(day || cursor.value, 'yyyy-MM-dd'),
    time: prefill.time || '09:00',
    unit: prefill.unit ?? defaultUnit(key),
    status: 'todo',
    canceled: false,
    description: prefill.description || '',
    // 📅 item 217: consulta NOVA (conta como agendamento) × JÁ ESTAVA MARCADA
    // fora do sistema (só lançando na Agenda — vira "Lançada", fora dos números)
    booking_kind: prefill.booking_kind || 'agendamento',
    // 🏥 item 300: CEVICO × Oftalmofácil — obrigatório ao criar (nasce em branco de propósito)
    origin: prefill.origin || '',
    channel: '', // 🧭 item 362: como chegou
    // 🔎 item 346: paciente escolhido na busca (nome travado, consulta no cadastro dele)
    contact_id: prefill.contact_id || null,
    // 💰 item 324: consulta particular + valores lançados à mão neste card
    particular: false,
    charges: [],
  };
};
const form = ref(emptyForm());
// 💰 item 324 — VALOR: o pré-configurado do tipo (Ajustes da Agenda → Valores),
// os valores lançados à mão (com quem lançou) e o que vai na confirmação
const appointmentPrices = computed(() => crmSettings.value?.appointment_prices || null);
const pricePreset = computed(() => presetAppointmentPrice(appointmentPrices.value, form.value));
const priceEffective = computed(() => effectiveAppointmentPrice(appointmentPrices.value, form.value));
const addCharge = () => {
  if (form.value.charges.length < 8) form.value.charges.push({ label: '', amount: '' });
};
const removeCharge = idx => form.value.charges.splice(idx, 1);
const chargeBy = c => (c.by_name ? `lançado por ${c.by_name}${c.at ? ` · ${format(new Date(c.at), 'dd/MM HH:mm')}` : ''}` : 'novo — fica no seu nome ao salvar');
// avisos de horário (faixa reservada / encaixe / Oftalmofácil) ficam recolhidos dentro de "Quando e onde"
const conflictsOpen = ref(false);
const BOOKING_KINDS = [
  { key: 'agendamento', label: 'Nova (agendamento)', hint: 'Foi marcada agora, pelo robô ou pela equipe — conta em "Consultas agendadas".' },
  { key: 'registro', label: 'Já estava marcada', hint: 'Veio do Oftalmofácil/telefone e só está sendo lançada na Agenda — aparece como "Lançada" e fica fora dos números de agendamento.' },
];
const bookingHint = computed(
  () => (BOOKING_KINDS.find(b => b.key === form.value.booking_kind) || BOOKING_KINDS[0]).hint
);
// item 234: o TIPO do formulário (6 opções) — o trilho + a modalidade por trás
const formType = computed(() => {
  if (form.value.kind === 'cirurgias') return TYPE_BY_KEY.cirurgia;
  if (form.value.kind === 'teleconsultas') return TYPE_BY_KEY.teleconsulta;
  if (form.value.kind === 'exames') return TYPE_BY_KEY.exames;
  return TYPE_BY_KEY[form.value.modality] || TYPE_BY_KEY.avaliacao;
});
const formKind = formType; // nome antigo, mesmo objeto (noun/article/icon/hint)
const formVars = computed(() => kindVars(formType.value));
const setFormType = key => {
  const tt = TYPE_BY_KEY[key];
  if (editingTask.value || !tt) return;
  const prevKind = form.value.kind;
  form.value.kind = tt.kind;
  form.value.modality = tt.modality || '';
  if (prevKind !== tt.kind) {
    form.value.unit = defaultUnit(tt.kind);
    if (tt.kind !== 'consultas' && tt.kind !== 'teleconsultas') form.value.procedure = '';
  }
};
const consultaModalities = MODALITIES.filter(m => ['avaliacao', 'retorno', 'pos_op'].includes(m.key));

const openCreateOnDay = (day, prefill = {}) => {
  editingTask.value = null;
  form.value = emptyForm(day, prefill);
  showDeleteConfirm.value = false;
  showModal.value = true;
};
// clique num bloco livre da janela → pré-preenchido com unidade e médico
const openCreateSlot = (day, win, slot) =>
  openCreateOnDay(day, { time: slot, unit: win.unit, doctor: win.doctor, modality: reservedFor(win)[0] });
// clique na coluna de horas (semana/dia)
const onColumnCreate = ({ day, minutes, band }) => {
  const time = `${String(Math.floor(minutes / 60)).padStart(2, '0')}:${String(minutes % 60).padStart(2, '0')}`;
  openCreateOnDay(day, { time, unit: band?.unit, doctor: band?.doctor, modality: band?.only?.[0] });
};
// mês: clicar no dia NAVEGA para a semana daquele dia
const goToWeek = day => {
  cursor.value = new Date(day);
  viewMode.value = 'week';
};
const goToDay = day => {
  cursor.value = new Date(day);
  viewMode.value = 'day';
};
const openCreateFab = () => {
  const base = viewMode.value === 'month' ? new Date() : new Date(cursor.value);
  openCreateOnDay(base);
};

// resumo no topo do modal (quando · médico · onde), para a leitura em F:
// quem e quando ficam no canto superior esquerdo, a ação no inferior direito
const formSummary = computed(() => {
  const f = form.value;
  const parts = [];
  if (f.date) {
    const d = new Date(`${f.date}T${f.time || '09:00'}`);
    parts.push(d.toLocaleDateString('pt-BR', { weekday: 'short', day: '2-digit', month: '2-digit' }) + (f.time ? ` · ${f.time}` : ''));
  }
  if (f.doctor) parts.push(doctorShort(f.doctor));
  if (f.unit === ONLINE_UNIT) parts.push('Online');
  else if (UNITS[f.unit]) parts.push(UNITS[f.unit].label);
  else if (f.kind === 'cirurgias' && f.unit) parts.push(surgeryLocations.value?.find(l => l.key === f.unit)?.label || '');
  return parts.filter(Boolean).join(' · ');
});

const openEdit = task => {
  const d = new Date(task.due_at);
  const pad = n => String(n).padStart(2, '0');
  editingTask.value = task;
  form.value = {
    kind: kindOf(task),
    name: displayName(task),
    phone: task.phone ?? '',
    procedure: task.procedure ?? '',
    doctor: task.doctor ?? '',
    modality: task.modality || (task.task_type === 'cirurgia' ? '' : 'avaliacao'),
    date: `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`,
    time: `${pad(d.getHours())}:${pad(d.getMinutes())}`,
    unit: task.unit ?? '',
    status: task.status === 'done' ? 'done' : 'todo',
    canceled: !!task.canceled_at,
    description: task.description ?? '',
    booking_kind: task.booking_kind || 'agendamento',
    origin: task.origin || '',
    channel: task.channel?.key || '', // 🧭 item 362
    contact_id: task.contact_id || null,
    particular: !!task.particular,
    charges: (task.charges || []).map(c => ({ ...c })),
  };
  showDeleteConfirm.value = false;
  conflictsOpen.value = false;
  showModal.value = true;
};

// item 255: CONFLITO de horário no formulário — quem já está no mesmo local
// sobrepondo o horário escolhido (qualquer camada, inclusive Oftalmofácil)
const formConflicts = computed(() => {
  if (!showModal.value || !form.value.date || !form.value.time) return [];
  const fk = form.value.kind;
  if (fk === 'teleconsultas') return [];
  const unit = form.value.unit;
  const start = toMin(form.value.time);
  const end = start + (DEFAULT_DURATION[fk] || 15);
  return (liveTasksByDay.value[form.value.date] || []).filter(t => {
    if (editingTask.value && t.id === editingTask.value.id) return false;
    if (t.unit ? t.unit !== unit : t.source === 'oftalmofacil') return false;
    const tStart = taskStartMin(t);
    return tStart < end && tStart + taskDuration(t) > start;
  });
});
const hubConflicts = computed(() => formConflicts.value.filter(t => t.source === 'oftalmofacil'));
// item 307: o horário cai numa faixa RESERVADA para outro tipo de atendimento?
// (ex.: avaliação na quarta 13h–14h do Dr. Henrique, que é só pós-operatório)
const reservedMismatch = computed(() => {
  if (!showModal.value || form.value.kind !== 'consultas' || !form.value.date || !form.value.time) return null;
  const spot = { dow: new Date(`${form.value.date}T12:00:00`).getDay(), unit: form.value.unit, minutes: toMin(form.value.time) };
  spot.date = form.value.date; // item 375: enxerga a abertura pontual do dia
  const win = windowAt(windows.value, { ...spot, doctor: form.value.doctor }) || windowAt(windows.value, spot);
  return win && !windowAccepts(win, form.value.modality || 'avaliacao') ? win : null;
});
// do "Precisa reagendar" das Configurações: abre o agendamento já no dia dele
const rescheduleFromSettings = task => {
  showSettings.value = false;
  cursor.value = new Date(task.due_at);
  openEdit(task);
};
const hmOf = t => format(new Date(t.due_at), 'HH:mm');

// item 300: ao CRIAR, a origem é obrigatória; ao editar agendamento antigo (sem
// origem) continua opcional — o que veio do hub já tem dono e não muda aqui
const originLocked = computed(() => Boolean(editingTask.value?.source));
const originMissing = computed(() => !editingTask.value && !form.value.origin);
const originHint = computed(() => ORIGIN_BY_KEY[form.value.origin]?.hint || 'Escolha de quem é o paciente — define a etiqueta, o CRM e a caixa do lembrete de confirmação.');
// 🧭 item 362 (21/10): COMO CHEGOU — obrigatório ao criar paciente NOVO (o que
// já tem carimbo automático de anúncio/página vem travado; o que já foi informado
// vem preenchido). Vira carimbo canal_origem no contato e conta no CAC e nas Campanhas.
const channelAuto = ref(false);
const channelMissing = computed(() => !editingTask.value && !form.value.contact_id && !form.value.channel);
const channelHint = computed(() => {
  if (channelAuto.value) return 'Carimbo automático (clique no anúncio ou página): não precisa escolher.';
  return LEAD_CHANNEL_BY_KEY[form.value.channel] ? 'Vira o rastro deste paciente no Financeiro & CAC e nas Campanhas.' : 'Pergunte: "como você chegou até a gente?" — é o que liga esta consulta ao canal que trouxe o paciente.';
});
// 📱 item 346: paciente NOVO só nasce com telefone (DDD + número) — sem ele
// não há como achar o cadastro de quem já existe e o paciente duplica
const phoneMissing = computed(() => !editingTask.value && !form.value.contact_id && (form.value.phone || '').replace(/\D/g, '').length < 10);

// 🔎 item 346 (16/10): o NOME puxa o paciente do cadastro. Escolheu → o nome
// trava (pedido dele: "tem que travar o nome, não pode editar"), telefone e
// origem vêm junto e a consulta nasce no cadastro dele. Telefone digitado que
// já é de alguém → "este telefone já é de X — usar?" (família pode dividir
// número: só avisa, não bloqueia).
const nameFocused = ref(false);
const patientSuggest = ref(null);
const phoneOwner = ref(null);
const pickPatient = p => {
  form.value.contact_id = p.id;
  form.value.name = p.name || form.value.name;
  if (p.phone_number) form.value.phone = p.phone_number;
  if (!form.value.origin && p.origin) form.value.origin = p.origin;
  // 🧭 item 362: o que já se sabe do canal vem junto (automático trava)
  form.value.channel = p.channel?.key || '';
  channelAuto.value = Boolean(p.channel?.auto);
  nameFocused.value = false;
  phoneOwner.value = null;
};
const unpickPatient = () => {
  form.value.contact_id = null;
  form.value.name = '';
  form.value.phone = '';
  form.value.channel = '';
  channelAuto.value = false;
};
const onNameKey = e => {
  if (e.key === 'Escape') { nameFocused.value = false; return; }
  patientSuggest.value?.onKey(e);
};
let phoneOwnerTimer = null;
watch(() => [form.value.phone, form.value.contact_id, showModal.value], ([phone, contactId, open]) => {
  clearTimeout(phoneOwnerTimer);
  phoneOwner.value = null;
  if (!open || contactId || (phone || '').replace(/\D/g, '').length < 10) return;
  phoneOwnerTimer = setTimeout(async () => {
    try {
      const { data } = await CrmAPI.patientPhoneOwner(phone);
      if (!form.value.contact_id && data?.payload) phoneOwner.value = data.payload;
    } catch { phoneOwner.value = null; }
  }, 400);
});
const canSave = computed(() => Boolean(form.value.name.trim() && form.value.date && !originMissing.value && !phoneMissing.value && !channelMissing.value));

const save = async () => {
  if (!canSave.value || isSaving.value) return;
  // item 255: horário tomado por paciente do Oftalmofácil = confirmação explícita
  const timeChanged = !editingTask.value ||
    format(new Date(editingTask.value.due_at), "yyyy-MM-dd'T'HH:mm") !== `${form.value.date}T${form.value.time}` ||
    editingTask.value.unit !== form.value.unit;
  if (timeChanged && hubConflicts.value.length) {
    const who = hubConflicts.value.map(t => `${hmOf(t)} ${displayName(t)}`).join(', ');
    // eslint-disable-next-line no-alert
    if (!window.confirm(`Este horário já está ocupado por paciente do Oftalmofácil (${who}). Agendar mesmo assim?`)) return;
  }
  // item 307: faixa reservada para outro tipo = a equipe decide, mas confirma
  const typeChanged = !editingTask.value || (editingTask.value.modality || 'avaliacao') !== (form.value.modality || 'avaliacao');
  if (reservedMismatch.value && (timeChanged || typeChanged)) {
    const win = reservedMismatch.value;
    // eslint-disable-next-line no-alert
    if (!window.confirm(`A faixa ${win.start}–${win.end} de ${win.doctor} está reservada para ${reservedLabelOf(win)}. Agendar mesmo assim?`)) return;
  }
  // ⏱️ item 367: hora já com o teto de avaliações = encaixe, só com confirmação
  const modality = form.value.modality || 'avaliacao';
  if (form.value.kind === 'consultas' && !NOT_EVALUATION.includes(modality) && (timeChanged || typeChanged)) {
    const hh = (form.value.time || '09:00').slice(0, 2);
    const n = evaluationsInHour(form.value.date, hh, form.value.unit, editingTask.value?.id);
    const where = UNITS[form.value.unit]?.label || form.value.unit;
    // eslint-disable-next-line no-alert
    if (n >= hourlyCap.value && !window.confirm(`Às ${hh}h, ${where} já tem ${n} avaliações (o limite é ${hourlyCap.value} por hora). Marcar mesmo assim, como encaixe?`)) return;
  }
  isSaving.value = true;
  try {
    const fk = form.value.kind;
    const dueAt = new Date(`${form.value.date}T${form.value.time || '09:00'}`);
    const payload = {
      title: form.value.name.trim(),
      phone: form.value.phone.trim(),
      procedure: form.value.procedure.trim(),
      doctor: form.value.doctor.trim(),
      modality: fk === 'cirurgias' ? null : (form.value.modality || defaultModality(fk)),
      due_at: dueAt.toISOString(),
      unit: fk === 'teleconsultas' ? ONLINE_UNIT : form.value.unit,
      status: form.value.status,
      canceled: form.value.canceled,
      description: form.value.description,
      task_type: fk === 'cirurgias' ? 'cirurgia' : 'consulta',
      priority: 'medium',
      booking_kind: fk === 'cirurgias' ? null : form.value.booking_kind,
      // 💰 item 324: particular + valores lançados à mão (o servidor grava quem lançou)
      particular: fk === 'consultas' && !!form.value.particular,
      charges: form.value.charges
        .filter(c => String(c.amount || '').trim())
        .map(c => ({ label: String(c.label || '').trim(), amount: String(c.amount).trim() })),
    };
    if (form.value.origin && !originLocked.value) payload.origin = form.value.origin;
    if (form.value.channel && !channelAuto.value) payload.channel = form.value.channel; // 🧭 item 362
    // 🔎 item 346: paciente escolhido na busca → a consulta fica no cadastro dele
    if (form.value.contact_id) payload.contact_id = form.value.contact_id;
    // o ✅ digitado no nome sai do nome e vira o confirmado de verdade
    if (editingTask.value && handConfirmed(editingTask.value) && !editingTask.value.confirmed_at) payload.confirmed = true;
    const noun = kindFor(fk).noun;
    if (editingTask.value) {
      await store.dispatch('tasks/update', { id: editingTask.value.id, ...payload });
      useAlert(`${cap(noun)} atualizad${kindFor(fk).article}`);
    } else {
      await store.dispatch('tasks/create', payload);
      useAlert(`${cap(noun)} agendad${kindFor(fk).article} ${fk === 'cirurgias' ? '🔪' : '✓'}`);
      ensureLayer(formType.value.key); // mostra onde ficou
    }
    showModal.value = false;
  } catch {
    useAlert('Erro ao salvar o agendamento.');
  } finally {
    isSaving.value = false;
  }
};

// item 304: vai para a Lixeira (não apaga) — dá para restaurar
const removeTask = async () => {
  if (!editingTask.value) return;
  const t = editingTask.value;
  try {
    await store.dispatch('tasks/remove', t.id);
    showModal.value = false;
    showDeleteConfirm.value = false;
    useAlert(`${displayName(t)} foi para a Lixeira — dá para restaurar pelo botão Lixeira.`);
  } catch {
    useAlert('Erro ao remover.');
  }
};

// ── Conferência do dia: Compareceu / Faltou + Indicação de cirurgia ──
const savingAttendanceId = ref(0);
const indicationPickerId = ref(0);

const setAttendance = async (task, value) => {
  if (savingAttendanceId.value) return;
  savingAttendanceId.value = task.id;
  try {
    const next = task.attendance === value ? null : value; // re-clique desfaz
    const payload = { id: task.id, attendance: next, status: next === 'attended' ? 'done' : 'todo' };
    if (next !== 'attended') {
      payload.surgery_indication = null;
      payload.indicated_procedure = null;
      indicationPickerId.value = 0;
    }
    await store.dispatch('tasks/update', payload);
    if (next === 'attended') useAlert(isSurgeryTask(task) ? '✓ Realizada.' : '✓ Compareceu — agora marque se houve indicação de cirurgia.');
    else if (next === 'missed') useAlert('✗ Falta registrada — card movido no CRM (se a coluna estiver configurada).');
  } catch {
    useAlert('Erro ao registrar a conferência.');
  } finally {
    savingAttendanceId.value = 0;
  }
};

const setIndication = async (task, value, procedure = null) => {
  if (savingAttendanceId.value) return;
  if (value === 'indicated' && !procedure) {
    indicationPickerId.value = indicationPickerId.value === task.id ? 0 : task.id;
    return;
  }
  savingAttendanceId.value = task.id;
  try {
    const next = task.surgery_indication === value && !procedure ? null : value;
    await store.dispatch('tasks/update', {
      id: task.id,
      surgery_indication: next,
      indicated_procedure: next === 'indicated' ? procedure : null,
    });
    indicationPickerId.value = 0;
    if (next === 'indicated') useAlert(`🎯 Cirurgia de ${procedure} indicada — card movido no CRM (se configurado).`);
  } catch {
    useAlert('Erro ao registrar a indicação.');
  } finally {
    savingAttendanceId.value = 0;
  }
};

// ── Cirurgia: "veio e NÃO fez" (pede o motivo) ──
const noSurgeryReasonId = ref(0);
const noSurgeryReason = ref('');
const toggleNoSurgery = task => {
  if (task.attendance === 'attended_not_done') {
    setAttendance(task, 'attended_not_done');
    return;
  }
  noSurgeryReasonId.value = noSurgeryReasonId.value === task.id ? 0 : task.id;
  noSurgeryReason.value = '';
};
const confirmNoSurgery = async task => {
  if (savingAttendanceId.value) return;
  savingAttendanceId.value = task.id;
  try {
    const reason = noSurgeryReason.value.trim();
    await store.dispatch('tasks/update', {
      id: task.id,
      attendance: 'attended_not_done',
      status: 'todo',
      description: reason ? `⚠️ Veio e não operou: ${reason}\n${task.description || ''}` : task.description,
    });
    noSurgeryReasonId.value = 0;
    useAlert('Registrado: o paciente veio, mas a cirurgia não aconteceu.');
  } catch {
    useAlert('Erro ao registrar.');
  } finally {
    savingAttendanceId.value = 0;
  }
};

const fmtBRL = v =>
  Number(v).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL', maximumFractionDigits: 0 });

// da consulta com indicação → agendar a CIRURGIA (trilho azul, pré-preenchida)
const scheduleSurgeryFrom = task => {
  ensureLayer('cirurgia');
  editingTask.value = null;
  form.value = emptyForm(new Date(), {
    kind: 'cirurgias',
    name: displayName(task),
    phone: task.phone || '',
    procedure: task.indicated_procedure || task.procedure || '',
    unit: surgeryLocations.value[0]?.key || '',
    description: `Origem: consulta de ${format(new Date(task.due_at), 'dd/MM')} às ${chipTime(task)} — cirurgia indicada.`,
  });
  showDeleteConfirm.value = false;
  showModal.value = true;
};
// do exame/teleconsulta/consulta → marcar um RETORNO presencial
const scheduleFollowUpFrom = task => {
  ensureLayer('retorno');
  editingTask.value = null;
  form.value = emptyForm(addDays(new Date(task.due_at), 7), {
    kind: 'consultas',
    name: displayName(task),
    phone: task.phone || '',
    doctor: task.doctor || '',
    modality: 'retorno',
    unit: task.unit && UNITS[task.unit] ? task.unit : 'tatuape',
    description: `Origem: ${kindLabelOf(task)} de ${format(new Date(task.due_at), 'dd/MM')} às ${chipTime(task)}.`,
  });
  showDeleteConfirm.value = false;
  showModal.value = true;
};

// ── Conferência do dia → colunas do CRM (config, admin) ──
const allCrmStages = ref([]);
const attendanceStages = ref({
  attended_stage_id: '', missed_stage_id: '', indicated_stage_id: '',
  surgery_done_stage_id: '', surgery_missed_stage_id: '',
});
const attendanceOwners = ref({ consulta_user_id: '', cirurgia_user_id: '', deadline: '19:00' });
// 🔁 item 343: Equipe dos retornos — retorno sem dono individual, as pessoas daqui veem todos (só admin escolhe)
const retornoTeam = ref([]);
const toggleRetornoTeam = id => {
  retornoTeam.value = retornoTeam.value.includes(id) ? retornoTeam.value.filter(x => x !== id) : [...retornoTeam.value, id];
};
const isSavingAttendanceCfg = ref(false);

const loadCrmStages = async () => {
  try {
    await store.dispatch('crm/fetchPipelines');
    const pipelines = store.getters['crm/getPipelines'] || [];
    allCrmStages.value = pipelines.flatMap(p => (p.stages || []).map(s => ({ id: s.id, name: s.name, pipeline: p.name })));
    const own = crmSettings.value?.attendance_owners || {};
    attendanceOwners.value = {
      consulta_user_id: own.consulta_user_id || '',
      cirurgia_user_id: own.cirurgia_user_id || '',
      deadline: own.deadline || '19:00',
    };
    retornoTeam.value = (crmSettings.value?.retorno_team_user_ids || []).map(Number);
    const cfg = crmSettings.value?.attendance_stages || {};
    attendanceStages.value = {
      attended_stage_id: cfg.attended_stage_id || '',
      missed_stage_id: cfg.missed_stage_id || '',
      indicated_stage_id: cfg.indicated_stage_id || '',
      surgery_done_stage_id: cfg.surgery_done_stage_id || '',
      surgery_missed_stage_id: cfg.surgery_missed_stage_id || '',
    };
  } catch {
    allCrmStages.value = [];
  }
};

const saveAttendanceStages = async () => {
  isSavingAttendanceCfg.value = true;
  try {
    await CrmAPI.updateAttendanceStages({
      attended_stage_id: attendanceStages.value.attended_stage_id || null,
      missed_stage_id: attendanceStages.value.missed_stage_id || null,
      indicated_stage_id: attendanceStages.value.indicated_stage_id || null,
      surgery_done_stage_id: attendanceStages.value.surgery_done_stage_id || null,
      surgery_missed_stage_id: attendanceStages.value.surgery_missed_stage_id || null,
    });
    await CrmAPI.updateAttendanceOwners({
      consulta_user_id: attendanceOwners.value.consulta_user_id || null,
      cirurgia_user_id: attendanceOwners.value.cirurgia_user_id || null,
      deadline: attendanceOwners.value.deadline || '19:00',
    });
    if (isAdmin.value) await CrmAPI.updateRetornoTeam(retornoTeam.value);
    useAlert('Conferência do dia configurada!');
  } catch {
    useAlert('Erro ao salvar a configuração.');
  } finally {
    isSavingAttendanceCfg.value = false;
  }
};

// ── Imprimir a lista do dia (PDF pelo diálogo de impressão) ──
// 24/09 (pedido dele): folha enxuta, com ESPAÇO PARA ANOTAÇÕES. Saíram as
// colunas de unidade e de médico, telefone estreito, observações em letra
// menor e minúscula (1/3 da largura de antes), Pagamento com as 4 opções para a
// recepção marcar (dinheiro/pix/débito/crédito), Presença unificada
// (compareceu/faltou/cirurgia indicada), Origem do paciente, Anotações e Nota
// fiscal. RETRATO (a paisagem "saiu deitada"): tudo cabe na lateral da folha.
const printPhone = raw => {
  const d = String(raw || '').replace(/\D/g, '').replace(/^55(?=\d{10,11}$)/, '');
  if (d.length === 11) return `(${d.slice(0, 2)}) ${d.slice(2, 7)}-${d.slice(7)}`;
  if (d.length === 10) return `(${d.slice(0, 2)}) ${d.slice(2, 6)}-${d.slice(6)}`;
  return String(raw || '');
};
// "RETORNO DE 30 DIAS PRK VALOR: R$0" → "Retorno de 30 dias PRK valor: R$0"
// (só rebaixa o que veio TODO em maiúscula; texto já normal fica como está)
const printObs = raw => {
  const t = String(raw || '').replace(/\s+/g, ' ').trim().slice(0, 70);
  if (!t) return '';
  const letters = t.replace(/[^A-Za-zÀ-ÿ]/g, '');
  if (letters && letters === letters.toUpperCase()) {
    const low = t.toLowerCase();
    // siglas que precisam continuar em maiúscula
    const fixed = low.replace(/\b(prk|lasik|oct|yag|iol|icl|lio|r\$)/g, m => m.toUpperCase());
    return fixed.charAt(0).toUpperCase() + fixed.slice(1);
  }
  return t;
};
// 🖨️ 24/09 (pedido dele): a pessoa ESCOLHE as colunas e a orientação da folha
// (caixinhas no painel "Imprimir"), lembrado no navegador dela.
const PRINT_COLUMNS = [
  { key: 'time', label: 'Hora', width: 34, on: true },
  { key: 'name', label: 'Paciente', width: 86, on: true },
  { key: 'phone', label: 'Telefone', width: 70, on: true },
  { key: 'proc', label: 'Problema / exame / procedimento', width: 56, on: true },
  { key: 'doctor', label: 'Médico', width: 60, on: false },
  { key: 'unit', label: 'Unidade / local', width: 58, on: false },
  { key: 'obs', label: 'Observações', width: 58, on: true },
  { key: 'pay', label: 'Pagamento (dinheiro · pix · débito · crédito)', width: 56, on: true },
  { key: 'pres', label: 'Presença (compareceu · faltou · cirurgia indicada)', width: 80, on: true },
  { key: 'origin', label: 'Origem do paciente (6 opções com valor)', width: 116, on: true },
  { key: 'notes', label: 'Anotações (espaço em branco)', width: 0, on: true },
  { key: 'nf', label: 'Nota fiscal (sim · não)', width: 34, on: true },
];
const PRINT_KEY = 'cevico_agenda_print';
const loadPrintOpts = () => {
  const base = { orientation: 'portrait', cols: Object.fromEntries(PRINT_COLUMNS.map(c => [c.key, c.on])) };
  try {
    const saved = JSON.parse(localStorage.getItem(PRINT_KEY) || 'null');
    if (saved && typeof saved === 'object') {
      if (saved.orientation === 'landscape' || saved.orientation === 'portrait') base.orientation = saved.orientation;
      if (saved.cols) PRINT_COLUMNS.forEach(c => { if (typeof saved.cols[c.key] === 'boolean') base.cols[c.key] = saved.cols[c.key]; });
    }
  } catch (e) { /* sem preferências salvas */ }
  return base;
};
const printOpts = ref(loadPrintOpts());
const showPrintModal = ref(false);
const printColsOn = computed(() => PRINT_COLUMNS.filter(c => printOpts.value.cols[c.key]));
const resetPrintOpts = () => { printOpts.value = { orientation: 'portrait', cols: Object.fromEntries(PRINT_COLUMNS.map(c => [c.key, c.on])) }; };
const printDayList = () => {
  localStorage.setItem(PRINT_KEY, JSON.stringify(printOpts.value));
  const day = cursor.value;
  const list = [...dayViewTasks.value].sort((a, b) => new Date(a.due_at) - new Date(b.due_at));
  const title = `CEVICO — ${cap(k.value.plural)} de ${day.toLocaleDateString('pt-BR', { weekday: 'long', day: '2-digit', month: '2-digit', year: 'numeric' })}`;
  const esc = s => String(s ?? '').replace(/</g, '&lt;');
  const procHeader = { cirurgias: 'Procedimento', exames: 'Exame' }[k.value.kind] || 'Problema';
  const opt = label => `<span><i></i>${label}</span>`;
  const cols = printColsOn.value;
  const cell = {
    time: t => `<td class="time">${chipTime(t)}</td>`,
    name: t => `<td class="name"><b>${esc(displayName(t))}</b></td>`,
    phone: t => `<td class="phone">${esc(printPhone(t.phone))}</td>`,
    proc: t => `<td class="proc">${esc(t.procedure || '')}</td>`,
    doctor: t => `<td class="doc">${esc(doctorShort(t.doctor || '') || '')}</td>`,
    unit: t => `<td class="doc">${esc(unitOf(t)?.label || '')}</td>`,
    obs: t => `<td class="obs">${esc(printObs(t.description))}</td>`,
    pay: () => `<td class="opts">${['Dinheiro', 'Pix', 'Débito', 'Crédito'].map(opt).join('')}</td>`,
    pres: () => `<td class="opts">${['Compareceu', 'Faltou', isSurgeryMode.value ? 'Realizada' : 'Cirurgia indicada'].map(opt).join('')}</td>`,
    origin: () => `<td class="opts origin">${['Indicação', 'Site', 'WhatsApp', 'Médico parceiro', 'Pac. antigo / Rotina', 'Convênio'].map(o => opt(`${o} <em>R$ ____</em>`)).join('')}</td>`,
    notes: () => '<td class="notes"></td>',
    nf: () => `<td class="opts">${['Sim', 'Não'].map(opt).join('')}</td>`,
  };
  const head = {
    time: 'Hora', name: 'Paciente', phone: 'Telefone', proc: procHeader, doctor: 'Médico', unit: isSurgeryMode.value ? 'Local' : 'Unidade',
    obs: 'Obs.', pay: 'Pagamento', pres: 'Presença', origin: 'Origem do paciente', notes: 'Anotações', nf: 'Nota fiscal',
  };
  const landscape = printOpts.value.orientation === 'landscape';
  const rows = list.map(t => `<tr>${cols.map(c => cell[c.key](t)).join('')}</tr>`).join('');
  const colgroup = cols.map(c => `<col${c.width ? ` style="width:${Math.round(c.width * (landscape ? 1.25 : 1))}px"` : ''}>`).join('');
  const hasNotes = printOpts.value.cols.notes;
  const html = `<!doctype html><html><head><meta charset="utf-8"><title>${title}</title>
    <style>
      @page { size: A4 ${landscape ? 'landscape' : 'portrait'}; margin: 7mm 6mm; }
      body { font-family: -apple-system, Inter, Arial, sans-serif; margin: 14px; color: #111; }
      h1 { font-size: 14px; margin: 0 0 2px; }
      p.sub { font-size: 9px; color: #555; margin: 0 0 8px; }
      /* cantos arredondados: a tabela precisa de border-spacing 0 (collapse não arredonda) */
      table { width: 100%; border-collapse: separate; border-spacing: 0; font-size: 9.5px; table-layout: ${hasNotes ? 'fixed' : 'auto'};
              border: 1px solid #999; border-radius: 10px; overflow: hidden; }
      th, td { border-right: 1px solid #999; border-bottom: 1px solid #999; padding: 3px 4px; text-align: left; vertical-align: top; }
      th:last-child, td:last-child { border-right: 0; }
      tbody tr:last-child td { border-bottom: 0; }
      th { background: #eee; font-size: 8px; text-transform: uppercase; letter-spacing: .02em; }
      /* bolinha para preencher à caneta */
      i { display: inline-block; width: 8px; height: 8px; border: 1px solid #333; border-radius: 50%; vertical-align: -1px; margin-right: 3px; }
      td.time { font-weight: bold; white-space: nowrap; }
      td.phone { font-size: 8.5px; white-space: nowrap; }
      td.doc { font-size: 8.5px; }
      td.obs { font-size: 7.5px; color: #444; line-height: 1.2; word-break: break-word; }
      td.opts { font-size: 8px; white-space: nowrap; line-height: 1.4; }
      td.opts span { display: block; }
      td.origin em { font-style: normal; color: #777; }
      td.origin span { white-space: nowrap; overflow: hidden; }
      td.notes { background: #fff; }
      @media print { body { margin: 0; } tr { page-break-inside: avoid; } }
    </style></head><body>
    <h1>${title}</h1>
    <p class="sub">${list.length} ${k.value.noun}(s) · Conferência do fim do dia: marque a forma de pagamento e a presença — depois registre no sistema (Agenda → visão Dia).</p>
    <table>
    <colgroup>${colgroup}</colgroup>
    <thead><tr>${cols.map(c => `<th>${head[c.key]}</th>`).join('')}</tr></thead><tbody>${rows}</tbody></table>
    <script>window.onload = () => window.print();<\/script>
    </body></html>`;
  const w = window.open('', '_blank');
  if (!w) { useAlert('O navegador bloqueou a janela — libere pop-ups para imprimir.'); return; }
  w.document.write(html);
  w.document.close();
  showPrintModal.value = false;
};

// ── arrastar-e-soltar (semana/dia): soltar = reagendar, confirmando no modal ──
const dragTask = ref(null);
const dragOverDay = ref('');
const onDragStart = task => { dragTask.value = task; };
const onDragOver = day => { if (dragTask.value) dragOverDay.value = dateKey(day); };
const onDragLeave = day => { if (dragOverDay.value === dateKey(day)) dragOverDay.value = ''; };
const onColumnDrop = ({ day, minutes }) => {
  const task = dragTask.value;
  dragTask.value = null;
  dragOverDay.value = '';
  if (!task || isDayOff(day)) return;
  const original = new Date(task.due_at);
  const hh = Math.floor(minutes / 60);
  const mm = minutes % 60;
  if (isSameDay(original, day) && original.getHours() === hh && original.getMinutes() === mm) return;
  openEdit(task);
  form.value.date = format(day, 'yyyy-MM-dd');
  form.value.time = `${String(hh).padStart(2, '0')}:${String(mm).padStart(2, '0')}`;
};

// ══ 🖱️ item 283 (29/09): MENU DO BOTÃO DIREITO da Agenda ══
// Só ATALHOS para o que a tela já faz — cada item chama a mesma função do
// botão/clique que já existe (nenhuma regra nova). No celular, toque longo.
const copyToClipboard = async (text, okMessage) => {
  try {
    await copyTextToClipboard(text);
    useAlert(okMessage);
  } catch {
    useAlert('Não foi possível copiar.');
  }
};
const hmFromMinutes = minutes =>
  `${String(Math.floor(minutes / 60)).padStart(2, '0')}:${String(minutes % 60).padStart(2, '0')}`;
const dayLabel = day =>
  cap(day.toLocaleDateString('pt-BR', { weekday: 'long', day: '2-digit', month: '2-digit' }));

// item 290: sinal de presença no balão (bater o olho e saber quem foi)
const attendanceMark = task => attendanceMarkOf(task);

// cancelar = a mesma chavinha "Cancelada" da ficha; quem confirma é o Salvar
const openCancel = task => {
  openEdit(task);
  form.value.canceled = true;
  useAlert('Marcado como cancelada — confira e clique em Salvar para confirmar.');
};

// 🎨 item 290: COR do agendamento (organização livre da equipe, vale para
// todos). Pinta na hora e salva; se o servidor recusar, volta como estava.
// Não mexe em presença, situação nem em nenhum indicador.
const setTaskColor = async (task, color) => {
  const next = color || null;
  if ((taskColorOf(task) || null) === next) return;
  const previous = task.color ?? null;
  store.commit('tasks/upsertTask', { ...task, color: next });
  try {
    await store.dispatch('tasks/update', { id: task.id, color: next });
  } catch {
    store.commit('tasks/upsertTask', { ...task, color: previous });
    useAlert('Não foi possível salvar a cor.');
  }
};

// ✅ item 300: a equipe marca o "confirmou" à mão (paciente confirmou por telefone)
const setConfirmed = async (task, value) => {
  if (savingAttendanceId.value) return;
  savingAttendanceId.value = task.id;
  try {
    const payload = { id: task.id, confirmed: value };
    // desfazer um ✅ digitado no nome = tirar o ✅ do nome
    if (!value && handConfirmed(task)) payload.title = displayName(task);
    await store.dispatch('tasks/update', payload);
    useAlert(value ? '✓ Consulta confirmada — card movido no CRM (se a coluna existir).' : 'Confirmação desfeita.');
  } catch {
    useAlert('Erro ao salvar a confirmação.');
  } finally {
    savingAttendanceId.value = 0;
  }
};

// consulta / exame / teleconsulta / cirurgia
const taskMenu = (task, extra = []) => {
  if (!task) return null;
  const surgery = isSurgeryTask(task);
  const busy = savingAttendanceId.value === task.id;
  const attended = task.attendance === 'attended';
  const canIndicate = attended && !surgery;
  // os mesmos nomes dos botões da conferência do dia
  const okName = surgery ? 'realizada' : 'compareceu';
  const missName = surgery ? 'não veio' : 'faltou';
  return {
    title: displayName(task),
    subtitle: `${format(new Date(task.due_at), 'dd/MM')} · ${chipTime(task)} · ${TYPE_BY_KEY[typeOf(task)]?.label || 'Agendamento'}`,
    rgb: hexToRgbSpaced(accentOf(task)),
    items: [
      { label: 'Abrir detalhes', icon: 'i-lucide-square-pen', action: () => openEdit(task) },
      { label: 'Remarcar', icon: 'i-lucide-calendar-clock', hint: 'dia e horário', action: () => openEdit(task) },
      { separator: true },
      !task.attendance && !task.canceled_at && {
        label: isConfirmed(task) ? 'Desfazer "Confirmou"' : 'Marcar: paciente confirmou',
        icon: 'i-lucide-badge-check',
        hint: isDeclined(task) ? 'respondeu NÃO ao lembrete' : '',
        disabled: busy,
        action: () => setConfirmed(task, !isConfirmed(task)),
      },
      {
        label: attended ? `Desfazer "${cap(okName)}"` : `Marcar: ${okName}`,
        icon: 'i-lucide-check',
        disabled: busy,
        action: () => setAttendance(task, 'attended'),
      },
      {
        label: task.attendance === 'missed' ? `Desfazer "${cap(missName)}"` : `Marcar: ${missName}`,
        icon: 'i-lucide-x',
        disabled: busy,
        action: () => setAttendance(task, 'missed'),
      },
      // o motivo é digitado na conferência do dia (visão Dia → Bloco do médico)
      surgery && viewMode.value === 'day' && dayPanelMode.value === 'bloco' && {
        label: task.attendance === 'attended_not_done' ? 'Desfazer "Veio e não fez"' : 'Marcar: veio e não fez',
        icon: 'i-lucide-triangle-alert',
        disabled: busy,
        action: () => toggleNoSurgery(task),
      },
      canIndicate && {
        label: 'Cirurgia indicada',
        icon: 'i-lucide-target',
        hint: task.surgery_indication === 'indicated' ? task.indicated_procedure || 'sim' : '',
        disabled: busy,
        children: PROCEDURES.map(proc => ({
          label: proc,
          icon: task.indicated_procedure === proc ? 'i-lucide-check' : '',
          action: () => setIndication(task, 'indicated', proc),
        })),
      },
      canIndicate && {
        label: task.surgery_indication === 'not_indicated' ? 'Desfazer "Sem indicação"' : 'Sem indicação de cirurgia',
        icon: 'i-lucide-circle-slash',
        disabled: busy,
        action: () => setIndication(task, 'not_indicated'),
      },
      canEditAgenda.value && canIndicate && ['exames', 'teleconsulta'].includes(typeOf(task)) && {
        label: 'Marcar retorno',
        icon: 'i-lucide-rotate-ccw',
        action: () => scheduleFollowUpFrom(task),
      },
      canEdit('agenda.cirurgias') && !surgery && task.surgery_indication === 'indicated' && {
        label: 'Agendar cirurgia',
        icon: 'i-lucide-slice',
        action: () => scheduleSurgeryFrom(task),
      },
      { separator: true },
      (task.contact_id || task.phone) && {
        label: 'Abrir conversa',
        icon: 'i-lucide-message-circle',
        hint: 'sem sair da Agenda',
        action: () => openTaskChat(task),
      },
      task.contact_id && {
        label: 'Abrir o Espaço do Paciente',
        icon: 'i-lucide-user-round',
        action: () => openPatientSpace(task),
      },
      detailOf(task)?.form_response && {
        label: 'Ler respostas do formulário',
        icon: 'i-lucide-book-open',
        action: () => openFormAnswers(task),
      },
      task.phone && {
        label: 'Copiar telefone',
        icon: 'i-lucide-phone',
        hint: task.phone,
        action: () => copyToClipboard(task.phone, 'Telefone copiado ✓'),
      },
      {
        label: 'Copiar nome',
        icon: 'i-lucide-copy',
        action: () => copyToClipboard(displayName(task), 'Nome copiado ✓'),
      },
      ...extra,
      { separator: true },
      canEditAgenda.value && {
        palette: TASK_COLORS,
        value: taskColorOf(task),
        label: 'Cor do agendamento',
        defaultLabel: 'Padrão (cor do tipo)',
        onPick: color => setTaskColor(task, color),
      },
      { separator: true },
      canEditAgenda.value && { label: 'Cancelar agendamento…', icon: 'i-lucide-ban', danger: true, action: () => openCancel(task) },
    ],
  };
};

// fechar / reabrir o dia: as MESMAS opções do cadeado (dia inteiro, unidade, médico)
const blockDayItem = day => canEdit('agenda.bloqueios') && ({
  label: isDayBlocked(day) ? 'Reabrir o dia' : 'Fechar o dia',
  icon: isDayBlocked(day) ? 'i-lucide-lock-open' : 'i-lucide-lock',
  hint: dayBlockSummary(day) && !isDayBlocked(day) ? 'fechado em parte' : '',
  disabled: isSavingBlock.value,
  children: blockOptions(day).map(o => ({
    label: o.label,
    icon: o.on ? 'i-lucide-lock' : o.icon,
    hint: o.on ? 'reabrir' : 'fechar',
    action: () => toggleBlockDay(day, o.part),
  })),
});
const printDayItem = day =>
  canView('agenda.imprimir') && dayTasks(day).length > 0 && {
    label: 'Imprimir a lista do dia',
    icon: 'i-lucide-printer',
    action: () => {
      goToDay(day);
      showPrintModal.value = true;
    },
  };
const isDayOpenNow = day => viewMode.value === 'day' && isSameDay(day, cursor.value);

// cabeçalho de um dia (semana/dia) e quadradinho do dia (mês)
const dayMenu = day => ({
  title: dayLabel(day),
  subtitle: isDayBlocked(day) ? 'dia fechado' : dayBlockSummary(day) || `${dayTasks(day).length} agendamento(s)`,
  items: [
    !isDayOpenNow(day) && { label: 'Abrir o dia', icon: 'i-lucide-calendar-check', action: () => goToDay(day) },
    viewMode.value !== 'week' && { label: 'Abrir a semana', icon: 'i-lucide-calendar-range', action: () => goToWeek(day) },
    canEditAgenda.value && !isDayOff(day) && { label: `${newLabel.value} neste dia`, icon: 'i-lucide-plus', action: () => openCreateOnDay(day) },
    { separator: true },
    blockDayItem(day),
    printDayItem(day),
  ],
});

// cada tipo da Agenda já nasce com o horário (e o médico/unidade da faixa, quando é dele)
const bandResource = band => {
  if (!band) return '';
  if (band.doctor) return 'doctor';
  return band.exam ? 'exam' : 'surgery';
};
const typeItems = (day, time, band) =>
  visibleTypes.value.map(tt => ({
    label: tt.label,
    icon: tt.icon,
    action: () => {
      const prefill = { time, kind: tt.kind, modality: tt.modality || '' };
      if (band && tt.resource === bandResource(band)) {
        prefill.unit = band.unit;
        prefill.doctor = band.doctor;
      }
      openCreateOnDay(day, prefill);
    },
  }));
// fechar/reabrir UM horário: o mesmo cadeado do bloco do médico (item 366: nó agenda.bloqueios)
const blockSlotItem = (day, win, slot) =>
  canEdit('agenda.bloqueios') && {
    label: isBlocked(day, win, slot) ? `Reabrir o horário das ${slot}` : `Fechar o horário das ${slot}`,
    icon: isBlocked(day, win, slot) ? 'i-lucide-lock-open' : 'i-lucide-lock',
    disabled: isSavingBlock.value,
    action: () => toggleBlock(day, win, slot),
  };

// espaço vazio na coluna de horas (semana/dia)
const columnMenu = ({ day, minutes, band }) => {
  if (isDayOff(day)) return dayMenu(day);
  const time = hmFromMinutes(minutes);
  return {
    title: `${dayLabel(day)} · ${time}`,
    subtitle: band ? band.label : 'fora das janelas de atendimento',
    items: [
      canEditAgenda.value && { label: `${newLabel.value} às ${time}`, icon: 'i-lucide-plus', action: () => onColumnCreate({ day, minutes, band }) },
      canEditAgenda.value && { label: 'Agendar outro tipo', icon: 'i-lucide-layers', children: typeItems(day, time, band) },
      { separator: true },
      band && blockSlotItem(day, band, time),
      blockDayItem(day),
      { separator: true },
      !isDayOpenNow(day) && { label: 'Abrir o dia', icon: 'i-lucide-calendar-check', action: () => goToDay(day) },
      printDayItem(day),
    ],
  };
};

// blocos de horário do médico (visão Dia → Bloco do médico)
const slotMenu = (day, win, slot) => {
  const list = tasksAtSlotAll(day, win, slot);
  const encaixe = canEditAgenda.value && {
    label: 'Encaixar outro paciente neste horário',
    icon: 'i-lucide-plus',
    action: () => openCreateSlot(day, win, slot),
  };
  if (list.length === 1) return taskMenu(list[0], [{ separator: true }, encaixe]);
  if (list.length > 1) {
    return {
      title: `${slot} · ${list.length} pacientes`,
      subtitle: winTitle(win),
      rgb: hexToRgbSpaced(winColor(win)),
      items: [
        ...list.map(t => ({ label: displayName(t), icon: 'i-lucide-user-round', hint: hmOf(t), action: () => openEdit(t) })),
        { separator: true },
        encaixe,
      ],
    };
  }
  const locked = isBlocked(day, win, slot);
  return {
    title: `${slot} · ${locked ? 'horário fechado' : 'horário livre'}`,
    subtitle: `${winTitle(win)} · ${winUnitLabel(win)}`,
    rgb: hexToRgbSpaced(winColor(win)),
    items: [
      canEditAgenda.value && !locked && { label: `${newLabel.value} às ${slot}`, icon: 'i-lucide-plus', action: () => openCreateSlot(day, win, slot) },
      canEditAgenda.value && !locked && { label: 'Agendar outro tipo', icon: 'i-lucide-layers', children: typeItems(day, slot, { unit: win.unit, doctor: win.doctor || '', exam: Boolean(win.exam) }) },
      { separator: true },
      blockSlotItem(day, win, slot),
    ],
  };
};

// contagem de pendentes da conferência
const pendingCount = computed(() => dayViewTasks.value.filter(t => !t.attendance).length);
</script>

<template>
  <div class="cv-page cv-agenda flex flex-col h-full w-full bg-n-surface-1" :style="pageVars">
    <!-- ══ item 244: o cabeçalho virou BLOCOS dentro da área rolável (o mini
         calendário é alto; grudado no topo roubaria a grade). Desktop: bloco
         "Calendário" à esquerda (mini mês sempre aberto + Hoje + Mês/Semana/Dia);
         à direita o bloco do PERÍODO (título grande, ‹ ›, novo) e o de FILTROS
         (camadas, quem, ferramentas). Celular: sem o mini mês, com o popover. ══ -->
    <!-- 📖 respostas do formulário do paciente -->
    <div v-if="formAnswersTask" class="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" @click.self="formAnswersTask = null">
      <div class="cv-modal cv-ag-pop w-full max-w-lg max-h-[85vh] flex flex-col">
        <div class="cv-modal-head flex items-center gap-3" style="--cv-hero: linear-gradient(135deg, #5B21B6, #7C3AED)">
          <span class="i-lucide-book-open-check text-xl" />
          <div class="flex-1 min-w-0">
            <h2 class="text-sm font-bold truncate">{{ formAnswersTask.name }}</h2>
            <p class="text-[11px] opacity-85">
              {{ formAnswersTask.detail.form_response.form }}
              · respondido em {{ new Date(formAnswersTask.detail.form_response.answered_at).toLocaleDateString('pt-BR') }}
            </p>
          </div>
          <button class="cv-glass-btn cv-iconbtn" @click="formAnswersTask = null"><span class="i-lucide-x" /></button>
        </div>
        <div class="flex-1 overflow-y-auto p-5 space-y-2.5">
          <div v-for="(ans, ai) in formAnswersTask.detail.form_response.answers" :key="ai" class="cv-sub px-3.5 py-2.5">
            <p class="text-[11px] font-semibold text-n-slate-10 mb-0.5">{{ ans.label }}</p>
            <p class="text-sm text-n-slate-12">{{ Array.isArray(ans.value) ? ans.value.join(', ') : (ans.value || '—') }}</p>
          </div>
        </div>
      </div>
    </div>

    <SkeletonScreen v-if="isLoading" variant="calendar" />

    <!-- ══ ÁREA ROLÁVEL ══ -->
    <div v-else class="flex-1 min-h-0 overflow-y-auto">
      <div class="px-4 sm:px-6 pt-4 pb-24 max-w-[1440px] mx-auto">
        <div class="grid grid-cols-1 lg:grid-cols-[272px_minmax(0,1fr)] gap-3 mb-3">
          <!-- ① CALENDÁRIO -->
          <section class="cv-ag-block hidden lg:flex flex-col p-3.5">
            <div class="flex items-center gap-2 mb-2">
              <p class="cv-ag-block-title flex-1">Calendário</p>
              <button class="cv-btn cv-btn-ghost cv-btn-sm" title="Voltar para hoje" @click="goToday">Hoje</button>
            </div>
            <div class="flex items-center justify-between mb-1">
              <button class="cv-ag-nav !w-7 !h-7" title="Mês anterior" @click="miniCursor = addMonths(miniCursor, -1)"><span class="i-lucide-chevron-left text-sm" /></button>
              <p class="text-sm font-bold text-n-slate-12">{{ miniLabel }}</p>
              <button class="cv-ag-nav !w-7 !h-7" title="Mês seguinte" @click="miniCursor = addMonths(miniCursor, 1)"><span class="i-lucide-chevron-right text-sm" /></button>
            </div>
            <div class="grid grid-cols-7 mb-0.5">
              <span v-for="wd in WEEKDAYS" :key="'mini' + wd" class="text-center text-[10px] font-semibold text-n-slate-9">{{ wd.charAt(0) }}</span>
            </div>
            <div v-for="(week, wi) in miniWeeks" :key="'mw' + wi" class="cv-ag-mini-week grid grid-cols-7" :class="miniInView(week[3]) && viewMode === 'week' ? 'cv-ag-mini-week-on' : ''">
              <button
                v-for="day in week"
                :key="'md' + day.toISOString()"
                class="cv-ag-daynum mx-auto !w-8 !h-8 text-xs hover:bg-n-alpha-2"
                :class="[
                  isSameMonth(day, miniCursor) ? '' : 'cv-ag-daynum-muted',
                  viewMode === 'day' && isSameDay(day, cursor) ? 'cv-ag-daynum-today' : '',
                ]"
                :style="isToday(day) && !(viewMode === 'day' && isSameDay(day, cursor)) ? { boxShadow: 'inset 0 0 0 1.5px var(--cv)' } : {}"
                @click="miniPick(day)"
              >
                {{ day.getDate() }}
              </button>
            </div>
            <!-- 28/09 (item 266): Mês · Semana · Dia em células iguais, sem o
                 pt-2 que desalinhava as pílulas dentro do segmentado -->
            <div class="mt-auto pt-3">
              <div class="cv-seg cv-seg-sm grid grid-cols-3 gap-1 w-full">
                <button
                  v-for="m in VIEW_MODES"
                  :key="'vm' + m.key"
                  class="cv-seg-item justify-center"
                  :class="viewMode === m.key ? 'cv-seg-on' : ''"
                  @click="viewMode = m.key"
                >
                  <span :class="m.icon" class="text-sm" />
                  {{ m.label }}
                </button>
              </div>
            </div>
          </section>

          <div class="flex flex-col gap-3 min-w-0">
            <!-- ② PERÍODO (o bloco sobe quando o calendário está aberto: backdrop-filter cria contexto de empilhamento) -->
            <section class="cv-ag-block p-5 sm:p-6 flex items-center gap-4 flex-wrap" :class="showDatePicker ? 'relative z-30' : ''">
              <div class="cv-icon cv-icon-lg hidden sm:inline-flex" :title="`Agenda de ${k.plural}`">
                <span :class="k.icon" class="text-lg" />
              </div>
              <div class="min-w-0 flex-1">
                <p class="cv-ag-block-title">{{ cap(stepLabel) }}<span v-if="navSub" class="cv-ag-block-sub"> · {{ navSub }}</span> <CevicoViewOnlyPill v-if="!canEditAgenda" /></p>
                <!-- desktop: título grande; celular: o título abre o calendário -->
                <h2 class="hidden lg:block text-2xl font-bold tracking-tight text-n-slate-12 truncate">{{ navLabel }}</h2>
                <div class="lg:hidden">
                  <div class="relative">
                              <button class="cv-ag-period" title="Clique para escolher a data num calendário" @click="toggleDatePicker">
                                <span class="truncate max-w-[60vw] lg:max-w-[360px]">{{ navLabel }}</span>
                                <span class="i-lucide-chevron-down text-xs opacity-60" />
                              </button>
                              <!-- calendário interativo. O véu invisível que fecha ao clicar fora
                                   vai para o body: o cabeçalho tem backdrop-filter, e um fixed
                                   dentro dele só cobriria o próprio cabeçalho. z-10 fica abaixo
                                   do cabeçalho (z-20), então o calendário continua clicável. -->
                              <Teleport to="body">
                                <div v-if="showDatePicker" class="fixed inset-0 z-10" @click="showDatePicker = false" />
                              </Teleport>
                              <div v-if="showDatePicker" class="cv-pop cv-ag-pop absolute left-0 top-10 z-40 w-72 p-4">
                                <div class="flex items-center justify-between mb-2">
                                  <button class="cv-ag-nav !w-7 !h-7" @click="pickerCursor = addMonths(pickerCursor, -1)"><span class="i-lucide-chevron-left text-sm" /></button>
                                  <p class="text-sm font-bold text-n-slate-12">{{ pickerLabel }}</p>
                                  <button class="cv-ag-nav !w-7 !h-7" @click="pickerCursor = addMonths(pickerCursor, 1)"><span class="i-lucide-chevron-right text-sm" /></button>
                                </div>
                                <div class="grid grid-cols-7 mb-1">
                                  <span v-for="wd in WEEKDAYS" :key="'p' + wd" class="text-center text-[10px] font-semibold text-n-slate-9">{{ wd.charAt(0) }}</span>
                                </div>
                                <div v-for="(week, wi) in pickerWeeks" :key="'pw' + wi" class="grid grid-cols-7">
                                  <button
                                    v-for="day in week"
                                    :key="'pd' + day.toISOString()"
                                    class="cv-ag-daynum mx-auto !w-8 !h-8 text-xs hover:bg-n-alpha-2"
                                    :class="[
                                      isSameMonth(day, pickerCursor) ? '' : 'cv-ag-daynum-muted',
                                      isSameDay(day, cursor) ? 'cv-ag-daynum-today' : '',
                                    ]"
                                    :style="!isSameDay(day, cursor) && isToday(day) ? { boxShadow: 'inset 0 0 0 1.5px var(--cv)' } : {}"
                                    @click="pickDate(day)"
                                  >
                                    {{ day.getDate() }}
                                  </button>
                                </div>
                                <button class="cv-btn cv-btn-sm w-full mt-2" @click="pickDate(new Date())">Hoje</button>
                              </div>
                            </div>
                </div>
              </div>
              <div class="flex items-center gap-1.5 ml-auto">
                <button class="cv-ag-nav" :title="`${cap(stepLabel)} anterior`" @click="step(-1)"><span class="i-lucide-chevron-left text-base" /></button>
                <span class="lg:hidden"><button class="cv-btn cv-btn-ghost cv-btn-sm" title="Voltar para hoje" @click="goToday">Hoje</button></span>
                <button class="cv-ag-nav" :title="`${cap(stepLabel)} seguinte`" @click="step(1)"><span class="i-lucide-chevron-right text-base" /></button>
              </div>
              <div class="lg:hidden">
                <div class="cv-seg cv-seg-sm">
                  <button v-for="m in VIEW_MODES" :key="'vmm' + m.key" class="cv-seg-item" :class="viewMode === m.key ? 'cv-seg-on' : ''" @click="viewMode = m.key">
                    <span :class="m.icon" class="text-sm" />
                  </button>
                </div>
              </div>
              <div class="flex items-center gap-1.5">
                  <button
                    v-if="viewMode === 'week'"
                    class="cv-btn cv-btn-ghost cv-btn-sm"
                    :title="hideWeekend ? 'Mostrar sábado e domingo' : 'Esconder sábado e domingo'"
                    @click="toggleWeekend"
                  >
                    <span :class="hideWeekend ? 'i-lucide-eye-off' : 'i-lucide-eye'" class="text-xs" />
                    <span class="hidden 2xl:inline">sáb/dom</span>
                  </button>
                  <button
                    v-if="viewMode === 'day' && dayViewTasks.length && canView('agenda.imprimir')"
                    class="cv-btn cv-btn-ghost cv-btn-sm"
                    title="Abre a lista do dia pronta para imprimir ou salvar em PDF"
                    @click="showPrintModal = true"
                  >
                    <span class="i-lucide-printer text-xs" />
                    <span class="hidden 2xl:inline">Imprimir</span>
                  </button>
                  <!-- 🗑️ item 304: Lixeira — o que foi excluído/cancelado nos últimos 60 dias, com quem e quando -->
                  <button
                    v-if="canView('agenda.lixeira')"
                    class="cv-btn cv-btn-ghost cv-btn-sm"
                    title="Agendamentos excluídos ou cancelados nos últimos 60 dias — quem apagou, quando, e o botão Restaurar"
                    @click="showTrash = true"
                  >
                    <span class="i-lucide-trash-2 text-xs" />
                    <span class="hidden 2xl:inline">Lixeira</span>
                    <span v-if="trashTasks.length" class="cv-chip">{{ trashTasks.length }}</span>
                  </button>
                  <button
                    v-if="isExam && canView('agenda.configuracao')"
                    class="cv-btn cv-btn-ghost cv-btn-sm"
                    title="Dias e horários em que a clínica faz exames (padrão: segunda a sexta, 08h–17h, Av. Paulista)"
                    @click="openExamWindowsModal"
                  >
                    <span class="i-lucide-clock text-xs" />
                    <span class="hidden 2xl:inline">Janela de exames</span>
                  </button>
                  <button
                    v-else-if="isPhysical && canView('agenda.configuracao')"
                    class="cv-btn cv-btn-ghost cv-btn-sm"
                    title="Faixas de horário dos médicos: quando cada um atende e para que serve cada faixa"
                    @click="openSettings('faixas')"
                  >
                    <span class="i-lucide-clock text-xs" />
                    <span class="hidden 2xl:inline">Faixas de horário</span>
                  </button>
                  <button
                    v-else-if="isSurgeryMode && canView('agenda.configuracao')"
                    class="cv-btn cv-btn-ghost cv-btn-sm"
                    title="Dias e horários em que a sala cirúrgica de cada clínica está disponível"
                    @click="openSurgeryWindowsModal"
                  >
                    <span class="i-lucide-clock text-xs" />
                    <span class="hidden 2xl:inline">Sala cirúrgica</span>
                  </button>
              </div>
              <!-- 28/09 (pedido dele: "precisa ser mais simples e evidente esse botão de fechar dia"):
                   na visão Dia o botão fica aqui no bloco Período, em qualquer modo do painel -->
              <div v-if="viewMode === 'day' && canEdit('agenda.bloqueios')" class="relative">
                <button
                  class="cv-btn"
                  :class="isDayBlocked(cursor) || partialBlocksFor(cursor).length ? 'cv-ag-dayclosed' : 'cv-btn-ghost cv-btn-danger'"
                  :disabled="isSavingBlock"
                  title="Fechar o dia inteiro, só uma unidade ou só um médico (feriado, congresso, folga): some da grade e a IA não oferece"
                  @click.stop="blockMenu = blockMenu === dateKey(cursor) ? null : dateKey(cursor)"
                >
                  <span :class="isDayBlocked(cursor) ? 'i-lucide-lock-open' : 'i-lucide-lock'" class="text-sm" />
                  {{ isDayBlocked(cursor) ? 'Reabrir dia' : dayBlockSummary(cursor) ? 'Fechado em parte' : 'Fechar dia' }}
                  <span class="i-lucide-chevron-down text-xs opacity-60" />
                </button>
                <Teleport to="body"><div v-if="blockMenu === dateKey(cursor)" class="fixed inset-0 z-10" @click="blockMenu = null" /></Teleport>
                <div v-if="blockMenu === dateKey(cursor)" class="cv-pop cv-ag-pop cv-ag-blockmenu absolute right-0 top-11 z-40 w-64 p-2">
                  <p class="cv-ag-blockmenu-title">Fechar / reabrir</p>
                  <button v-for="o in blockOptions(cursor)" :key="o.key" class="cv-ag-blockmenu-item" :class="{ on: o.on }" :disabled="isSavingBlock" @click="toggleBlockDay(cursor, o.part)">
                    <span :class="o.on ? 'i-lucide-lock' : o.icon" class="text-sm" />
                    <span class="flex-1 text-left">{{ o.label }}</span>
                    <span class="cv-ag-blockmenu-state">{{ o.on ? 'reabrir' : 'fechar' }}</span>
                  </button>
                </div>
              </div>
              <!-- 01/10 (item 307, pedido dele: "deixar mais fácil e evidente abrir e fechar agendas"):
                   um botão só para tudo que muda na agenda -->
              <!-- item 366: quem só tem "Fechar e reabrir dias" entra direto em Abrir e fechar -->
              <button v-if="canView('agenda.configuracao') || canEdit('agenda.bloqueios')" class="cv-btn cv-btn-ghost" title="Abrir e fechar agendas, faixas de horário, regras para a IA e conferência do dia" @click="openSettings('abrir')">
                <span class="i-lucide-settings-2 text-sm" />
                {{ canView('agenda.configuracao') ? 'Configurações da agenda' : 'Abrir e fechar agenda' }}
              </button>
              <button v-if="canEditAgenda" class="cv-btn" @click="openCreateOnDay(viewMode === 'month' ? new Date() : cursor)">
                <span class="i-lucide-plus text-sm" />
                <span class="hidden sm:inline">{{ newLabel }}</span>
                <span class="sm:hidden">{{ cap(k.noun) }}</span>
              </button>
            </section>

            <!-- ③ FILTROS -->
            <section class="cv-ag-block p-5 sm:p-6 flex flex-col gap-4 flex-1">
              <p class="cv-ag-block-title">Filtros</p>
              <div class="flex items-start gap-3">
                <span class="cv-ag-row-label">Camadas</span>
                <div class="min-w-0 flex-1">
                  <div class="cv-seg flex-wrap max-w-full">
                    <button
                      class="cv-ag-kind cv-ag-kind-all"
                      :class="isGeneral ? 'cv-ag-kind-on' : ''"
                      title="Agenda geral — todas as camadas ligadas. Clique num tipo para ligar/desligar só ele; clique duplo deixa só aquele tipo"
                      @click="allLayers()"
                    >
                      <span class="i-lucide-layers text-sm" />
                      <span class="hidden sm:inline">Geral</span>
                    </button>
                    <button
                      v-for="tt in visibleTypes"
                      :key="tt.key"
                      class="cv-ag-kind"
                      :class="layerOn(tt.key) ? 'cv-ag-kind-on' : 'opacity-60'"
                      :style="typeVarsOf(tt.key)"
                      :title="`${tt.label} — ${tt.hint} · clique: liga/desliga esta camada · clique duplo: só este tipo`"
                      @click="toggleLayer(tt.key)"
                      @dblclick.prevent="soloLayer(tt.key)"
                    >
                      <span class="cv-ag-kind-dot" />
                      <span :class="tt.icon" class="text-sm hidden 2xl:inline" />
                      {{ tt.label }}
                      <span class="cv-ag-kind-n" :title="`${kindCounts[tt.key]} ${rangeNoun}`">{{ kindCounts[tt.key] }}</span>
                    </button>
                  </div>
                  <!-- 🔁 item 368: a cor do reagendamento (não é tipo, por isso não liga/desliga) -->
                  <p class="text-[11px] text-n-slate-10 mt-1.5 flex items-center gap-1">
                    <span class="cv-ag-resched i-lucide-refresh-cw" /> contorno azul-piscina = consulta reagendada (a cor de dentro continua sendo o tipo)
                  </p>
                </div>
              </div>
              <div class="flex items-start gap-3 flex-wrap lg:flex-nowrap">
                <span class="cv-ag-row-label">Quem</span>
                <div class="min-w-0 flex-1 flex items-start gap-2 flex-wrap">
                  <div v-if="!isPersonalView" class="flex items-center gap-1.5 flex-wrap min-w-0">
                    <button
                      class="cv-chip"
                      :class="view === 'clinic' ? 'cv-chip-on' : ''"
                      @click="view = 'clinic'"
                    >
                      {{ isSurgeryMode ? 'Todos os locais' : 'Toda a clínica' }}
                    </button>
                    <template v-if="!isSurgeryMode">
                      <button
                        v-for="d in DOCTORS"
                        :key="'quick' + d.name"
                        class="cv-chip"
                        :class="view === `doctor:${d.name}` ? 'cv-chip-on' : ''"
                        :style="view === `doctor:${d.name}` ? { '--cv-grad': d.color, '--cv-deep-rgb': hexToRgbSpaced(d.color) } : {}"
                        @click="view = `doctor:${d.name}`"
                      >
                        <span class="w-2 h-2 rounded-full" :style="{ background: view === `doctor:${d.name}` ? '#fff' : d.color }" />
                        {{ d.short }}
                        <span v-if="isDoctorClosed(d.name)" class="text-[9px]" title="agenda fechada">⏸</span>
                      </button>
                      <template v-if="!isTele">
                        <span class="w-px h-4 bg-n-weak mx-0.5" />
                        <button
                          v-for="(u, key) in visibleUnits"
                          :key="'qu' + key"
                          class="cv-chip"
                          :class="view === `unit:${key}` ? 'cv-chip-on' : ''"
                          :style="view === `unit:${key}` ? { '--cv-grad': u.color, '--cv-deep-rgb': hexToRgbSpaced(u.color) } : {}"
                          @click="view = view === `unit:${key}` ? 'clinic' : `unit:${key}`"
                        >
                          <span class="w-2 h-2 rounded-full" :style="{ background: view === `unit:${key}` ? '#fff' : u.color }" />
                          {{ u.label }}
                        </button>
                      </template>
                    </template>
                    <template v-else>
                      <button
                        v-for="loc in surgeryLocations"
                        :key="'quickloc' + loc.key"
                        class="cv-chip"
                        :class="view === `unit:${loc.key}` ? 'cv-chip-on' : ''"
                        :style="view === `unit:${loc.key}` ? { '--cv-grad': loc.color, '--cv-deep-rgb': hexToRgbSpaced(loc.color) } : {}"
                        @click="view = `unit:${loc.key}`"
                      >
                        <span class="w-2 h-2 rounded-full" :style="{ background: view === `unit:${loc.key}` ? '#fff' : loc.color }" />
                        {{ loc.label }}
                      </button>
                      <button v-if="canEdit('agenda.cirurgias')" class="cv-chip" title="Gerenciar clínicas parceiras" @click="openLocationsModal">
                        <span class="i-lucide-map-pin text-[10px]" /> locais
                      </button>
                    </template>
                    <template v-if="hasExternal">
                      <span class="w-px h-4 bg-n-weak mx-0.5" />
                      <span class="text-[10px] font-bold uppercase tracking-wider text-n-slate-10">Origem</span>
                      <button v-for="o in [['all', 'Todas'], ['cevico', 'CEVICO'], ['oftalmofacil', 'Oftalmofácil']]" :key="'orig' + o[0]" class="cv-chip" :class="originFilter === o[0] ? 'cv-chip-on' : ''" @click="originFilter = o[0]">
                        <span v-if="o[0] === 'oftalmofacil'" class="i-lucide-hospital text-[10px]" />{{ o[1] }}
                      </button>
                    </template>
                  </div>
                  <div class="flex items-center gap-1.5 ml-auto flex-wrap">
                    <select v-model="view" class="cv-input !h-8 text-xs !w-auto max-w-[160px]" title="Ver a agenda de…">
                      <option value="clinic">{{ isSurgeryMode ? 'Todos os locais' : 'Toda a clínica' }}</option>
                      <optgroup v-if="!isSurgeryMode && !isTele" label="Unidades">
                        <option v-for="(u, key) in visibleUnits" :key="key" :value="`unit:${key}`">{{ u.label }}</option>
                      </optgroup>
                      <optgroup v-if="!isSurgeryMode" label="Médicos">
                        <option v-for="d in DOCTORS" :key="d.name" :value="`doctor:${d.name}`">{{ d.name }}</option>
                      </optgroup>
                      <option value="me">Minha agenda pessoal</option>
                      <optgroup v-if="isAdmin" label="Pessoas">
                        <option v-for="agent in agents" :key="agent.id" :value="String(agent.id)">{{ agent.name }}</option>
                      </optgroup>
                    </select>
                  </div>
                </div>
              </div>
            </section>
          </div>
        </div>

        <!-- resumo do trilho: 4 vidros pequenos -->
        <div class="grid grid-cols-2 lg:grid-cols-4 gap-2.5 mb-3">
          <div class="cv-stat px-4 py-3 flex items-center gap-3">
            <span class="cv-icon cv-icon-sm"><span :class="k.icon" class="text-xs" /></span>
            <div class="min-w-0">
              <p class="text-[11px] font-semibold text-n-slate-10 truncate">{{ cap(k.plural) }} hoje</p>
              <p class="text-xl font-bold leading-tight text-n-slate-12 tabular-nums">{{ kpiToday }}</p>
            </div>
          </div>
          <div class="cv-stat px-4 py-3 flex items-center gap-3">
            <span class="cv-icon cv-icon-sm"><span class="i-lucide-calendar-range text-xs" /></span>
            <div class="min-w-0">
              <p class="text-[11px] font-semibold text-n-slate-10 truncate">Nesta semana</p>
              <p class="text-xl font-bold leading-tight text-n-slate-12 tabular-nums">{{ kpiWeek }}</p>
            </div>
          </div>
          <button
            v-for="p in kpiPlaces"
            :key="p.key"
            class="cv-stat px-4 py-3 flex items-center gap-3 text-left transition-transform hover:-translate-y-px"
            :class="view === `unit:${p.key}` ? 'cv-sub-on' : ''"
            :disabled="isTele"
            :title="isTele ? '' : 'Ver só este lugar'"
            @click="!isTele && (view = view === `unit:${p.key}` ? 'clinic' : `unit:${p.key}`)"
          >
            <span class="cv-icon cv-icon-sm" :style="{ background: p.color }"><span class="i-lucide-map-pin text-xs" /></span>
            <div class="min-w-0">
              <p class="text-[11px] font-semibold text-n-slate-10 truncate">{{ p.label }}</p>
              <p class="text-xl font-bold leading-tight tabular-nums" :style="{ color: p.color }">{{ p.n }}</p>
            </div>
          </button>
        </div>

        <!-- Ocupação: uma linha fina; abre para ver dia/semana/mês por tipo -->
        <div v-if="showOccupancy" class="cv-sub px-4 py-2.5 mb-3">
          <button class="w-full flex items-center gap-2.5 text-left" @click="showOccDetail = !showOccDetail">
            <span class="i-lucide-gauge text-sm text-n-slate-10" />
            <p class="text-xs font-semibold text-n-slate-12 whitespace-nowrap">Ocupação</p>
            <span class="cv-chip">{{ activeDoctor ? doctorShort(activeDoctor) : activeUnit ? (UNITS[activeUnit]?.label || surgeryLocationLabel(activeUnit)) : (isSurgeryMode ? 'todos os locais' : 'toda a clínica') }}</span>
            <div class="cv-track cv-track-sm flex-1 min-w-[80px] flex">
              <template v-if="occCurrent.total && occSegments(occCurrent)">
                <div v-for="s in occSegments(occCurrent)" :key="s.key" class="h-full" :style="{ width: Math.max(s.pct, 2) + '%', background: s.color }" :title="`${s.label}: ${s.count} bloco(s)`" />
              </template>
              <div v-else-if="occCurrent.total" class="cv-fill" :style="{ width: Math.max(occCurrent.pct, 3) + '%', background: occColor(occCurrent.pct) }" />
            </div>
            <span class="text-xs font-bold text-n-slate-12 tabular-nums w-10 text-right">{{ occCurrent.total ? occCurrent.pct + '%' : '—' }}</span>
            <span class="text-[10px] text-n-slate-9 hidden sm:inline">{{ rangeNoun }}</span>
            <span :class="showOccDetail ? 'i-lucide-chevron-up' : 'i-lucide-chevron-down'" class="text-xs text-n-slate-9" />
          </button>
          <div v-if="showOccDetail" class="grid grid-cols-1 sm:grid-cols-3 gap-3 mt-3 pt-3 border-t border-n-weak">
            <div v-for="row in occRows" :key="row.key">
              <div class="flex items-center justify-between text-xs mb-1">
                <span class="text-n-slate-11">{{ row.label }} <span class="text-n-slate-9">({{ row.sub }})</span></span>
                <span class="font-bold text-n-slate-12 tabular-nums">{{ row.scan.total ? row.scan.pct + '%' : '—' }}</span>
              </div>
              <div class="cv-track cv-track-sm flex">
                <template v-if="row.scan.total && occSegments(row.scan)">
                  <div v-for="s in occSegments(row.scan)" :key="s.key" class="h-full" :style="{ width: Math.max(s.pct, 2) + '%', background: s.color }" :title="`${s.label}: ${s.count} bloco(s)`" />
                </template>
                <div v-else-if="row.scan.total" class="cv-fill" :style="{ width: Math.max(row.scan.pct, 3) + '%', background: occColor(row.scan.pct) }" />
              </div>
              <p class="text-[10px] text-n-slate-9 mt-0.5">
                {{ row.scan.total ? `${row.scan.filled} de ${row.scan.total} blocos` : 'sem janela' }}
                <template v-if="occCaption(row.scan)"> · {{ occCaption(row.scan) }}</template>
              </p>
            </div>
            <p v-if="!isSurgeryMode" class="sm:col-span-3 text-[10px] text-n-slate-9 flex items-center gap-2.5 flex-wrap">
              <span v-for="m in OCC_MODALITIES" :key="m.key" class="flex items-center gap-1"><span class="w-2 h-2 rounded-full" :style="{ background: m.color }" />{{ m.label }}</span>
              <span>· {{ isExam ? 'exames têm janela própria (seg–sex 08h–17h por padrão)' : 'exames têm janela própria, fora dos blocos dos médicos' }} · cadeados fora da conta</span>
            </p>
          </div>
        </div>

        <!-- ══ MÊS ══ -->
        <div v-if="viewMode === 'month'">
          <div class="cv-ag-month">
            <div v-for="wd in WEEKDAYS" :key="wd" class="cv-ag-month-head">{{ wd }}</div>
            <template v-for="(week, wi) in weeks" :key="wi">
              <div
                v-for="day in week"
                :key="day.toISOString()"
                v-cv-menu="() => dayMenu(day)"
                class="cv-ag-cell"
                :class="{
                  'cv-ag-cell-out': !inMonth(day),
                  'cv-ag-cell-off': isDayOff(day),
                  'cv-ag-cell-today': isToday(day),
                }"
                role="button"
                title="Abrir a semana deste dia"
                @click="goToWeek(day)"
              >
                <div class="flex items-center justify-between">
                  <span class="cv-ag-daynum !w-6 !h-6 text-xs" :class="[isToday(day) ? 'cv-ag-daynum-today' : '', !inMonth(day) ? 'cv-ag-daynum-muted' : '']">{{ day.getDate() }}</span>
                  <span v-if="isDayOff(day)" class="i-lucide-lock text-[10px]" :class="isDayBlocked(day) ? 'text-red-400' : 'text-n-slate-8'" :title="isDayBlocked(day) ? 'Dia fechado' : 'Fim de semana'" />
                  <span v-else class="flex items-center gap-0.5">
                    <span v-for="w in windowsForDay(day)" :key="(w.doctor || w.unit) + w.start" class="w-1.5 h-1.5 rounded-full" :style="{ backgroundColor: winColor(w) }" :title="`${winTitle(w)} — ${w.start}`" />
                    <span v-if="dayOccupancy(day)" class="text-[9px] font-bold ml-0.5 tabular-nums" :style="{ color: occColor(dayOccupancy(day).pct) }" :title="`${dayOccupancy(day).filled} de ${dayOccupancy(day).total} blocos ocupados`">{{ dayOccupancy(day).pct }}%</span>
                  </span>
                </div>
                <div class="flex flex-col gap-[3px] min-h-0">
                  <button
                    v-for="task in dayTasks(day).slice(0, MONTH_MAX)"
                    :key="task.id"
                    v-cv-menu="() => taskMenu(task)"
                    type="button"
                    class="cv-ag-ev"
                    :class="{ 'cv-ag-ev-done': task.status === 'done' && task.attendance !== 'missed', 'cv-ag-ev-missed': task.attendance === 'missed' }"
                    :style="evVars(task)"
                    :title="`${chipTime(task)} · ${displayName(task)}${unitOf(task) ? ' · ' + unitOf(task).label : ''}`"
                    @click.stop="openEdit(task)"
                  >
                    <span class="cv-ag-ev-time">{{ chipTime(task) }}</span>
                    <span class="cv-ag-ev-name"><span v-if="isConfirmed(task)" class="cv-ag-conf i-lucide-badge-check" :title="confirmationTitle(task)" /><span v-else-if="isDeclined(task)" class="cv-ag-conf cv-ag-conf-no i-lucide-badge-x" title="Respondeu NÃO ao lembrete — ligar para remarcar ou cancelar" />{{ displayName(task) }}</span>
                    <span v-if="attendanceMark(task)" class="cv-ag-ev-mark" :class="attendanceMark(task).cls" :title="attendanceMark(task).title">{{ attendanceMark(task).sign }}</span>
                  </button>
                  <button
                    v-if="dayTasks(day).length > MONTH_MAX"
                    type="button"
                    class="cv-ag-more text-left"
                    title="Ver o dia inteiro"
                    @click.stop="goToDay(day)"
                  >
                    +{{ dayTasks(day).length - MONTH_MAX }} mais
                  </button>
                </div>
              </div>
            </template>
          </div>
          <p class="text-[10px] text-n-slate-9 mt-2 text-center">clique num dia para abrir a semana · o número de cada tipo está no seletor acima</p>
        </div>

        <!-- ══ SEMANA ══ -->
        <div v-else-if="viewMode === 'week'" class="overflow-x-auto">
          <div class="cv-ag-grid min-w-[720px]">
            <div class="cv-ag-grid-head" :style="{ gridTemplateColumns: weekGridCols }">
              <div />
              <div v-for="day in weekDays" :key="'h' + day.toISOString()" class="relative min-w-0">
              <!-- 28/09: cadeado de FECHAR/REABRIR o dia direto na coluna da semana (admin) -->
              <!-- item 267: fechar/reabrir é da EQUIPE inteira (não só admin) -->
              <button
                type="button"
                class="cv-ag-daylock"
                :class="{ 'cv-ag-daylock-on': isDayBlocked(day) || partialBlocksFor(day).length }"
                :disabled="isSavingBlock"
                :title="dayBlockSummary(day) ? `${dayBlockSummary(day)} — clique para reabrir ou fechar mais` : 'Fechar este dia (inteiro, uma unidade ou um médico)'"
                @click.stop="blockMenu = blockMenu === dateKey(day) ? null : dateKey(day)"
              >
                <span class="i-lucide-lock" />
                <span class="cv-ag-daylock-txt">{{ isDayBlocked(day) ? 'reabrir' : partialBlocksFor(day).length ? 'em parte' : 'fechar' }}</span>
              </button>
              <Teleport to="body"><div v-if="blockMenu === dateKey(day)" class="fixed inset-0 z-10" @click="blockMenu = null" /></Teleport>
              <div v-if="blockMenu === dateKey(day)" class="cv-pop cv-ag-pop cv-ag-blockmenu absolute right-1 top-7 z-40 w-60 p-2" @click.stop>
                <p class="cv-ag-blockmenu-title">{{ WEEKDAYS[day.getDay()] }} {{ day.getDate() }} · fechar / reabrir</p>
                <button v-for="o in blockOptions(day)" :key="o.key" class="cv-ag-blockmenu-item" :class="{ on: o.on }" :disabled="isSavingBlock" @click="toggleBlockDay(day, o.part)">
                  <span :class="o.on ? 'i-lucide-lock' : o.icon" class="text-sm" />
                  <span class="flex-1 text-left">{{ o.label }}</span>
                  <span class="cv-ag-blockmenu-state">{{ o.on ? 'reabrir' : 'fechar' }}</span>
                </button>
              </div>
              <button
                v-cv-menu="() => dayMenu(day)"
                type="button"
                class="cv-ag-dayhead w-full"
                :class="{ 'cv-ag-dayhead-today': isToday(day), 'cv-ag-dayhead-off': isDayOff(day) }"
                :title="isDayOff(day) ? (isDayBlocked(day) ? 'Dia fechado' : 'Fim de semana') : 'Abrir o dia'"
                @click="goToDay(day)"
              >
                <span class="cv-ag-dayhead-wd">{{ WEEKDAYS[day.getDay()] }}</span>
                <span class="cv-ag-daynum cv-ag-daynum-lg" :class="isToday(day) ? 'cv-ag-daynum-today' : ''">{{ day.getDate() }}</span>
                <span v-if="isDayOff(day)" class="text-[9px] text-n-slate-9 flex items-center gap-0.5"><span class="i-lucide-lock text-[9px]" />{{ isDayBlocked(day) ? 'fechado' : 'sem agenda' }}</span>
                <span v-else class="flex items-center gap-1 flex-wrap justify-center min-h-[16px]">
                  <!-- item 267: parte do dia fechada (unidade/médico) — as outras janelas continuam -->
                  <span v-if="dayBlockSummary(day)" class="text-[9px] text-red-500 font-semibold flex items-center gap-0.5 basis-full justify-center"><span class="i-lucide-lock text-[9px]" />{{ dayBlockSummary(day) }}</span>
                  <span v-for="w in windowsForDay(day)" :key="(w.doctor || w.unit) + w.start" class="cv-ag-win" :style="{ '--w': winColor(w) }" :title="`${winTitle(w)} — ${w.start} às ${w.end} (${winUnitLabel(w)})`">
                    {{ w.doctor ? doctorShort(w.doctor) : surgeryLocationLabel(w.unit) }}
                  </span>
                  <span v-if="dayOccupancy(day)" class="text-[9px] font-bold tabular-nums" :style="{ color: occColor(dayOccupancy(day).pct) }">{{ dayOccupancy(day).pct }}%</span>
                  <span v-else-if="dayTasks(day).length" class="text-[9px] font-semibold text-n-slate-9">{{ dayTasks(day).length }}</span>
                </span>
              </button>
              </div>
            </div>
            <div class="cv-ag-grid-body" :style="{ gridTemplateColumns: weekGridCols }">
              <div class="cv-ag-gutter" :style="{ height: weekHours.length * WEEK_ROW_PX + 'px' }">
                <span v-for="(h, hi) in weekHours" :key="h" class="cv-ag-hour" :style="{ top: hi * WEEK_ROW_PX + 'px' }">{{ hi === 0 ? '' : String(h).padStart(2, '0') + ':00' }}</span>
              </div>
              <AgendaTimeColumn
                v-for="day in weekDays"
                :key="'c' + day.toISOString()"
                :day="day"
                :tasks="dayTasks(day)"
                :bands="bandsForDay(day)"
                :start-hour="weekSpan.start"
                :end-hour="weekSpan.end"
                :hour-px="WEEK_ROW_PX"
                :day-off="isDayOff(day)"
                :today="isToday(day)"
                :now-minutes="nowMinutes"
                :drop-active="dragOverDay === dateKey(day)"
                :duration-of="taskDuration"
                :accent-of="accentOf"
                :tag-of="tagOf"
                :name-of="displayName"
                :task-menu="taskMenu"
                :slot-menu="columnMenu"
                compact
                @create="onColumnCreate"
                @open="openEdit"
                @dragstart="onDragStart"
                @dragover="onDragOver"
                @dragleave="onDragLeave"
                @drop="onColumnDrop"
              />
            </div>
          </div>
          <p class="text-[10px] text-n-slate-9 mt-2 text-center">
            clique num espaço vazio para agendar naquele horário · arraste um balão para reagendar · faixas coloridas = horário em que cada médico atende
          </p>
        </div>

        <!-- ══ DIA ══ -->
        <div v-else>
          <!-- avisos do dia -->
          <div v-if="isWeekend(cursor) && !hasOpenWindow(cursor)" class="cv-sub flex items-center gap-2 px-4 py-3 mb-4 text-sm text-n-slate-10">
            <span class="i-lucide-lock text-base" />
            {{ WEEKDAY_FULL[cursor.getDay()] }} — sem agenda em nenhuma unidade.
          </div>
          <div v-else-if="isDayBlocked(cursor)" class="cv-block cv-strip cv-red flex items-center gap-2 px-4 py-3 mb-4 text-sm text-n-slate-11 flex-wrap">
            <span class="i-lucide-lock text-base text-red-500" />
            <b>Dia fechado</b> — sem agenda nesta data.
            <button v-if="canEdit('agenda.bloqueios')" class="cv-btn cv-btn-ghost cv-btn-sm ml-auto" @click="toggleBlockDay(cursor)">Reabrir dia</button>
          </div>

          <div ref="dayLayoutEl" class="flex flex-col lg:flex-row gap-4 items-start" :class="isSplitting ? 'select-none cursor-col-resize' : ''">
            <!-- linha do tempo do dia (ganha a largura toda quando o painel recolhe) -->
            <div class="flex-1 min-w-0 w-full">
              <div class="cv-ag-grid">
                <div class="cv-ag-grid-head" style="grid-template-columns: 56px minmax(0, 1fr)">
                  <div />
                  <div v-cv-menu="() => dayMenu(cursor)" class="cv-ag-dayhead !items-start px-3 !py-2.5" :class="isToday(cursor) ? 'cv-ag-dayhead-today' : ''">
                    <span class="cv-ag-dayhead-wd">{{ WEEKDAY_FULL[cursor.getDay()] }}</span>
                    <span class="flex items-center gap-2">
                      <span class="cv-ag-daynum cv-ag-daynum-lg" :class="isToday(cursor) ? 'cv-ag-daynum-today' : ''">{{ cursor.getDate() }}</span>
                      <span class="text-sm font-semibold text-n-slate-12">{{ dayViewTasks.length }} {{ dayViewTasks.length === 1 ? k.noun : k.plural }}</span>
                    </span>
                  </div>
                </div>
                <div class="cv-ag-grid-body" style="grid-template-columns: 56px minmax(0, 1fr)">
                  <div class="cv-ag-gutter" :style="{ height: dayHours.length * DAY_ROW_PX + 'px' }">
                    <span v-for="(h, hi) in dayHours" :key="h" class="cv-ag-hour" :style="{ top: hi * DAY_ROW_PX + 'px' }">{{ hi === 0 ? '' : String(h).padStart(2, '0') + ':00' }}</span>
                  </div>
                  <AgendaTimeColumn
                    :day="cursor"
                    :tasks="dayViewTasks"
                    :bands="bandsForDay(cursor)"
                    :start-hour="daySpan.start"
                    :end-hour="daySpan.end"
                    :hour-px="DAY_ROW_PX"
                    :day-off="isDayOff(cursor)"
                    :today="isToday(cursor)"
                    :now-minutes="nowMinutes"
                    :drop-active="dragOverDay === dateKey(cursor)"
                    :duration-of="taskDuration"
                    :accent-of="accentOf"
                    :tag-of="tagOf"
                    :name-of="displayName"
                    :task-menu="taskMenu"
                    :slot-menu="columnMenu"
                    @create="onColumnCreate"
                    @open="openEdit"
                    @dragstart="onDragStart"
                    @dragover="onDragOver"
                    @dragleave="onDragLeave"
                    @drop="onColumnDrop"
                  />
                </div>
              </div>
              <p class="text-[10px] text-n-slate-9 mt-2 text-center">cada balão ocupa o espaço do seu tempo · clique no vazio para agendar</p>
            </div>

            <!-- painel do dia (recolhível): trilho fino quando fechado -->
            <div v-if="!dayPanelOpen" class="cv-ag-side cv-ag-side-rail w-full lg:w-[52px] flex lg:flex-col items-center gap-2 p-1.5">
              <button class="cv-ag-rail-btn" title="Mostrar o bloco do médico e a conferência do dia" @click="toggleDayPanel">
                <span class="i-lucide-panel-right-open text-base" />
              </button>
              <span v-if="pendingCount" class="cv-chip cv-amber cv-ag-rail-vert" :title="`${pendingCount} pendente(s) na conferência`">{{ pendingCount }} pend.</span>
              <span v-else-if="dayViewTasks.length" class="cv-chip cv-green" title="Conferência do dia completa">✓</span>
              <span class="text-[10px] font-bold text-n-slate-10 cv-ag-rail-vert select-none">Itens do dia · Conferência</span>
            </div>
            <!-- divisor arrastável (só no desktop) -->
            <div
              v-if="dayPanelOpen"
              class="cv-ag-splitter hidden lg:flex"
              title="Arraste para regular a largura · clique duplo volta ao padrão · arraste até o fim para recolher"
              @pointerdown="startSplit"
              @dblclick="resetSplit"
            >
              <span class="cv-ag-splitter-pill" />
            </div>
            <div v-if="dayPanelOpen" class="cv-ag-side w-full space-y-4" :class="isSplitting ? 'cv-ag-side-dragging' : ''" :style="{ '--side': dayPanelPct + '%' }">
              <div class="flex items-center gap-2 flex-wrap">
                <div class="cv-seg cv-seg-sm">
                  <button class="cv-seg-item" :class="dayPanelMode === 'itens' ? 'cv-seg-on' : ''" title="Só as caixas do dia: hora, quem, o quê e os selos (clique na caixa para abrir)" @click="setDayPanelMode('itens')">
                    <span class="i-lucide-list text-sm" /> Itens do dia
                  </button>
                  <button class="cv-seg-item" :class="dayPanelMode === 'bloco' ? 'cv-seg-on' : ''" title="Bloco do médico com a grade de horários + conferência (compareceu / faltou / indicação)" @click="setDayPanelMode('bloco')">
                    <span class="i-lucide-stethoscope text-sm" /> Bloco do médico · Conferência
                  </button>
                </div>
                <button class="cv-btn cv-btn-ghost cv-btn-sm ml-auto" title="Recolher este painel e dar a largura toda ao calendário" @click="toggleDayPanel">
                  <span class="i-lucide-panel-right-close text-sm" /> <span class="hidden sm:inline">Recolher</span>
                </button>
              </div>
              <!-- janelas do dia: blocos de horário (livre / ocupado / cadeado) — só no modo Bloco do médico -->
              <div v-if="dayPanelMode === 'bloco' && !isDayBlocked(cursor) && windowsForDay(cursor).length" class="space-y-3">
                <div v-for="win in windowsForDay(cursor)" :key="(win.doctor || win.unit) + win.start" class="cv-sub p-4" :style="winVars(win)">
                  <div class="flex items-center gap-2 flex-wrap mb-3">
                    <span class="cv-icon cv-icon-sm" :style="{ background: winColor(win) }">
                      <span :class="win.doctor ? 'i-lucide-stethoscope' : 'i-lucide-slice'" class="text-xs" />
                    </span>
                    <p class="text-sm font-bold text-n-slate-12">{{ winTitle(win) }}</p>
                    <span class="cv-chip" :style="{ '--cv-rgb': hexToRgbSpaced(winColor(win)), '--cv-deep': winColor(win) }">{{ winUnitLabel(win) }}</span>
                    <span class="text-xs text-n-slate-10"><template v-if="win.turno">{{ win.turno }} · </template>{{ win.start }}–{{ win.end }} · {{ win.block }} min</span>
                    <span v-if="reservedFor(win).length" class="cv-chip cv-amber" title="Faixa reservada: a IA não oferece consulta nova aqui e a equipe é avisada ao marcar outro tipo">só {{ reservedLabelOf(win) }}</span>
                    <span class="text-[10px] px-2 py-0.5 rounded-full font-bold text-white ml-auto" :style="{ backgroundColor: occColor(winOccupancy(cursor, win).pct) }" :title="`${winOccupancy(cursor, win).filled} de ${winOccupancy(cursor, win).total} blocos ocupados`">
                      {{ winOccupancy(cursor, win).pct }}%
                    </span>
                    <button v-if="canEdit('agenda.bloqueios')" class="cv-btn cv-btn-ghost cv-btn-sm cv-btn-danger" title="Fechar o dia inteiro (feriado, congresso, folga...)" @click="toggleBlockDay(cursor)">
                      <span class="i-lucide-lock text-[10px]" /> fechar dia
                    </button>
                  </div>
                  <!-- ⏱️ item 367: avaliações em cada hora × o teto (hora cheia em vermelho) -->
                  <div v-if="countsHourly(win)" class="flex flex-wrap gap-1.5 mb-2">
                    <span v-for="hh in hoursOfWin(win)" :key="hh" class="cv-chip" :class="hourLoad(cursor, win, hh) >= hourlyCap ? 'cv-red' : 'cv-slate'" :title="hourLoad(cursor, win, hh) >= hourlyCap ? 'Hora cheia: os robôs não marcam mais avaliações aqui; a equipe pode encaixar, confirmando' : 'Avaliações marcadas nesta hora'">
                      {{ hh }}h · {{ hourLoad(cursor, win, hh) }}/{{ hourlyCap }}<template v-if="hourLoad(cursor, win, hh) >= hourlyCap"> · cheia</template>
                    </span>
                  </div>
                  <div class="grid grid-cols-3 sm:grid-cols-4 xl:grid-cols-5 gap-2">
                    <template v-for="slot in slotsFor(win)" :key="slot">
                      <!-- item 319: um balão POR PACIENTE (antes o encaixe virava "+2" escondido) -->
                      <template v-if="taskAtSlot(cursor, win, slot)">
                        <span v-for="(st, si) in tasksAtSlotAll(cursor, win, slot)" :key="slot + '-' + st.id" v-cv-menu="() => taskMenu(st)" class="relative group min-w-0">
                          <button class="cv-ag-slot cv-ag-slot-taken truncate" :class="[isSoftTask(st) ? 'cv-ag-slot-soft' : '', isRescheduled(st) ? 'cv-ag-slot-resched' : '']" :title="`${slot} · ${displayName(st)}${softTitle(st)}`" @click="openEdit(st)">
                            <span class="tabular-nums opacity-90">{{ slot }}</span>
                            <span v-if="isRescheduled(st)" class="cv-ag-resched i-lucide-refresh-cw" :title="rescheduledTitle(st)" />
                            <span v-if="isConfirmed(st)" class="cv-ag-conf i-lucide-badge-check" :title="confirmationTitle(st)" /><span v-else-if="isDeclined(st)" class="cv-ag-conf cv-ag-conf-no i-lucide-badge-x" title="Respondeu NÃO ao lembrete — ligar para remarcar ou cancelar" />
                            <span class="truncate">{{ displayName(st) }}</span>
                          </button>
                          <button v-if="si === 0" class="cv-ag-corner" title="Encaixe: agendar OUTRO paciente neste mesmo horário" @click.stop="openCreateSlot(cursor, win, slot)"><span class="i-lucide-plus" /></button>
                        </span>
                      </template>
                      <button v-else-if="isBlocked(cursor, win, slot)" v-cv-menu="() => slotMenu(cursor, win, slot)" class="cv-ag-slot cv-ag-slot-locked" :title="canEdit('agenda.bloqueios') ? 'Horário fechado — clique para reabrir' : 'Horário fechado'" @click="canEdit('agenda.bloqueios') && toggleBlock(cursor, win, slot)">
                        <span class="i-lucide-lock text-[10px]" /> {{ slot }}
                      </button>
                      <span v-else v-cv-menu="() => slotMenu(cursor, win, slot)" class="relative group">
                        <button class="cv-ag-slot cv-ag-slot-free tabular-nums" :class="hourFull(cursor, win, slot) ? 'opacity-50' : ''" :title="hourFull(cursor, win, slot) ? `Hora cheia (${hourlyCap} avaliações) — dá para encaixar, confirmando` : 'Bloco livre — clique para agendar'" @click="openCreateSlot(cursor, win, slot)">{{ slot }}</button>
                        <button v-if="canEdit('agenda.bloqueios')" class="cv-ag-corner" title="Fechar este horário 🔒" @click.stop="toggleBlock(cursor, win, slot)"><span class="i-lucide-lock" /></button>
                      </span>
                    </template>
                    <!-- item 319: quem está marcado FORA da faixa (antes ou depois) também aparece -->
                    <span v-for="ot in outsideTasks(cursor, win)" :key="'out-' + ot.id" v-cv-menu="() => taskMenu(ot)" class="relative group min-w-0">
                      <button class="cv-ag-slot cv-ag-slot-taken cv-ag-slot-out truncate" :class="isSoftTask(ot) ? 'cv-ag-slot-soft' : ''" :title="`${hmOf(ot)} · ${displayName(ot)} — fora da faixa ${win.start}–${win.end}${softTitle(ot)}`" @click="openEdit(ot)">
                        <span class="tabular-nums opacity-90">{{ hmOf(ot) }}</span>
                        <span v-if="isConfirmed(ot)" class="cv-ag-conf i-lucide-badge-check" :title="confirmationTitle(ot)" /><span v-else-if="isDeclined(ot)" class="cv-ag-conf cv-ag-conf-no i-lucide-badge-x" title="Respondeu NÃO ao lembrete — ligar para remarcar ou cancelar" />
                        <span class="truncate">{{ displayName(ot) }}</span>
                      </button>
                    </span>
                  </div>
                </div>
              </div>

              <!-- vazio -->
              <div v-if="!dayViewTasks.length && (dayPanelMode === 'itens' || !windowsForDay(cursor).length) && !isDayOff(cursor)" class="cv-sub text-center py-14 text-n-slate-10">
                <span class="cv-icon cv-icon-xl mx-auto mb-3 block"><span :class="k.icon" class="text-xl" /></span>
                <p class="text-sm">Nenhum{{ k.article === 'a' ? 'a' : '' }} {{ k.noun }} neste dia.</p>
                <button class="cv-btn mt-3" @click="openCreateOnDay(cursor)"><span class="i-lucide-plus text-sm" /> {{ newLabel }}</button>
              </div>

              <!-- Conferência do dia -->
              <template v-if="dayViewTasks.length">
                <div class="flex items-center gap-2 flex-wrap">
                  <span class="cv-icon cv-icon-sm"><span :class="dayPanelMode === 'itens' ? 'i-lucide-list' : 'i-lucide-clipboard-check'" class="text-xs" /></span>
                  <p class="text-sm font-bold text-n-slate-12">{{ dayPanelMode === 'itens' ? 'Itens do dia' : `Conferência d${k.article}s ${k.plural} do dia` }}</p>
                  <span v-if="dayPanelMode === 'itens'" class="cv-chip cv-chip-on">{{ dayViewTasks.length }}</span>
                  <span v-if="pendingCount" class="cv-chip cv-amber">{{ pendingCount }} pendente(s)</span>
                  <span v-else class="cv-chip cv-green">tudo conferido ✓</span>
                </div>
                <div class="space-y-2.5">
                  <div
                    v-for="task in dayViewTasks"
                    :key="task.id"
                    v-cv-menu="() => taskMenu(task)"
                    class="cv-ag-card"
                    :class="task.attendance === 'missed' ? 'opacity-75' : ''"
                    :style="evVars(task)"
                    @click="openEdit(task)"
                  >
                    <!-- item 243: GRADE — hora | quem e o quê (procedimento em negrito) | selos alinhados à direita -->
                    <div class="cv-ag-card-grid">
                      <span class="cv-ag-card-time" :style="{ color: accentOf(task) }">{{ chipTime(task) }}</span>
                      <div class="min-w-0">
                        <p class="text-sm font-semibold text-n-slate-12 truncate" :class="task.attendance === 'missed' ? 'line-through' : ''"><span v-if="isConfirmed(task)" class="cv-ag-conf i-lucide-badge-check" :title="confirmationTitle(task)" /><span v-else-if="isDeclined(task)" class="cv-ag-conf cv-ag-conf-no i-lucide-badge-x" title="Respondeu NÃO ao lembrete — ligar para remarcar ou cancelar" />{{ displayName(task) }}</p>
                        <p v-if="task.procedure" class="cv-ag-card-proc"><span class="i-lucide-eye text-[11px] opacity-60" /> {{ task.procedure }}</p>
                        <div class="cv-ag-card-meta">
                          <span v-if="task.phone"><span class="i-lucide-phone text-[10px]" />{{ task.phone }}</span>
                          <span v-if="task.doctor"><span class="i-lucide-stethoscope text-[10px]" />{{ task.doctor }}</span>
                          <button v-if="task.contact_id" class="hover:underline text-[11px] font-semibold" :style="{ color: accentOf(task) }" @click.stop="openPatientSpace(task)">
                            <PatientSpaceIcon :size="12" /> Espaço do Paciente
                          </button>
                        </div>
                      </div>
                      <div class="cv-ag-card-pills">
                        <span class="cv-chip" :style="{ '--cv-rgb': hexToRgbSpaced(typeColorOf(task)), '--cv-deep': typeColorOf(task) }" :title="TYPE_BY_KEY[typeOf(task)].hint"><span :class="TYPE_BY_KEY[typeOf(task)].icon" class="text-[10px] shrink-0" /> {{ TYPE_BY_KEY[typeOf(task)].label }}</span>
                        <span v-if="unitOf(task)" class="cv-chip" :style="{ '--cv-rgb': hexToRgbSpaced(unitOf(task).color), '--cv-deep': unitOf(task).color }"><span class="i-lucide-map-pin text-[10px] shrink-0" /> <span class="truncate">{{ unitOf(task).label }}</span></span>
                        <span v-if="task.source" class="cv-chip cv-slate" :title="`Veio do ${task.source === 'oftalmofacil' ? 'Oftalmofácil' : task.source}${task.source_detail ? ' · parceiro: ' + task.source_detail : ''}`"><span class="i-lucide-hospital text-[10px] shrink-0" /> <span class="truncate">{{ originLabel(task) }}</span></span>
                        <span v-if="task.origin && !task.source" class="cv-chip" :style="{ '--cv-rgb': hexToRgbSpaced(originOf(task).color), '--cv-deep': originOf(task).color }" :title="originOf(task).hint"><span :class="originOf(task).icon" class="text-[10px] shrink-0" /> {{ originOf(task).label }}</span>
                        <span v-if="isRescheduled(task)" class="cv-chip cv-cyan" :title="rescheduledTitle(task)"><span class="i-lucide-refresh-cw text-[11px] shrink-0" /> {{ rescheduledTitle(task) }}</span>
                        <span v-if="!task.attendance && isConfirmed(task)" class="cv-chip cv-green" :title="confirmationTitle(task)"><span class="i-lucide-badge-check text-[11px] shrink-0" /> Confirmou</span>
                        <span v-else-if="!task.attendance && isDeclined(task)" class="cv-chip cv-red" title="Respondeu NÃO ao lembrete — ligar para remarcar ou cancelar"><span class="i-lucide-badge-x text-[11px] shrink-0" /> Disse não</span>
                        <span v-if="task.attendance === 'attended'" class="cv-chip cv-green">{{ isSurgeryTask(task) ? '✓ Realizada' : '✓ Compareceu' }}</span>
                        <span v-else-if="task.attendance === 'missed'" class="cv-chip cv-red">{{ isSurgeryTask(task) ? '✗ Não veio' : '✗ Faltou' }}</span>
                        <span v-else-if="task.attendance === 'attended_not_done'" class="cv-chip cv-amber">⚠️ Veio e não fez</span>
                        <span v-if="isAdmin && isSurgeryTask(task) && task.crm_value" class="cv-chip cv-green" :title="task.surgery_payment ? `Forma de pagamento: ${task.surgery_payment}` : 'Valor do card no CRM'">
                          💰 {{ fmtBRL(task.crm_value) }}<template v-if="task.surgery_payment"> · {{ task.surgery_payment }}</template>
                        </span>
                        <span v-if="task.surgery_indication === 'indicated'" class="cv-chip cv-gold cv-chip-on" :title="task.indicated_procedure || 'Cirurgia indicada'"><span class="truncate">🎯 {{ task.indicated_procedure || 'Cirurgia indicada' }}</span></span>
                        <span v-else-if="task.surgery_indication === 'not_indicated'" class="cv-chip cv-slate">Sem indicação</span>
                      </div>
                    </div>

                    <div v-if="detailOf(task) && (detailOf(task).labels.length || detailOf(task).form_response)" class="flex items-center gap-1.5 mt-1.5 flex-wrap">
                      <span v-for="lbl in detailOf(task).labels" :key="task.id + lbl" class="cv-chip" :style="{ '--cv-rgb': hexToRgbSpaced(accentOf(task)), '--cv-deep': accentOf(task) }">🏷 {{ lbl }}</span>
                      <button v-if="detailOf(task).form_response" class="cv-btn cv-btn-sm" style="--cv-grad: linear-gradient(135deg, #5B21B6, #7C3AED); --cv-deep-rgb: 91 33 182" title="Respostas que o paciente deu no formulário — leia antes da consulta" @click.stop="openFormAnswers(task)">
                        📖 Ler respostas do formulário
                      </button>
                    </div>

                    <!-- item 290: presença RÁPIDA também no modo Itens — os mesmos botões e a mesma
                         função da conferência (clicar de novo desfaz; sem confirmação, é reversível) -->
                    <div v-if="dayPanelMode === 'itens'" class="cv-ag-quick" @click.stop>
                      <button class="cv-ag-act cv-ag-act-sm" style="--a: #059669" :class="task.attendance === 'attended' ? 'cv-ag-act-on' : ''" :disabled="savingAttendanceId === task.id" :title="task.attendance === 'attended' ? 'Clique de novo para desfazer' : 'Marcar presença'" @click="setAttendance(task, 'attended')">
                        {{ isSurgeryTask(task) ? '✓ Realizada' : '✓ Compareceu' }}
                      </button>
                      <button class="cv-ag-act cv-ag-act-sm" style="--a: #DC2626" :class="task.attendance === 'missed' ? 'cv-ag-act-on' : ''" :disabled="savingAttendanceId === task.id" :title="task.attendance === 'missed' ? 'Clique de novo para desfazer' : 'Marcar falta'" @click="setAttendance(task, 'missed')">
                        {{ isSurgeryTask(task) ? '✕ Não veio' : '✕ Faltou' }}
                      </button>
                    </div>

                    <!-- conferência: compareceu / faltou → indicação (modo Bloco do médico; no modo Itens a caixa abre no clique) -->
                    <div v-if="dayPanelMode === 'bloco'" class="flex items-center gap-1.5 mt-2.5 pt-2.5 border-t border-n-weak flex-wrap" @click.stop>
                      <button class="cv-ag-act" style="--a: #059669" :class="task.attendance === 'attended' ? 'cv-ag-act-on' : ''" :disabled="savingAttendanceId === task.id" @click="setAttendance(task, 'attended')">
                        {{ isSurgeryTask(task) ? '✓ Realizada' : '✓ Compareceu' }}
                      </button>
                      <button v-if="isSurgeryTask(task)" class="cv-ag-act" style="--a: #D97706" :class="task.attendance === 'attended_not_done' ? 'cv-ag-act-on' : ''" :disabled="savingAttendanceId === task.id" title="O paciente veio, mas a cirurgia não aconteceu — registre o motivo" @click="toggleNoSurgery(task)">
                        ⚠️ Veio e não fez
                      </button>
                      <button class="cv-ag-act" style="--a: #DC2626" :class="task.attendance === 'missed' ? 'cv-ag-act-on' : ''" :disabled="savingAttendanceId === task.id" @click="setAttendance(task, 'missed')">
                        {{ isSurgeryTask(task) ? '✗ Não veio' : '✗ Faltou' }}
                      </button>
                      <div v-if="noSurgeryReasonId === task.id" class="w-full flex items-center gap-1.5 mt-1">
                        <input v-model="noSurgeryReason" class="cv-input flex-1 !h-8 text-xs" placeholder="Qual foi o motivo? (pressão alta, desistiu, exame pendente...)" @keyup.enter="confirmNoSurgery(task)" />
                        <button class="cv-btn cv-btn-sm cv-amber" :disabled="savingAttendanceId === task.id" @click="confirmNoSurgery(task)">Registrar</button>
                      </div>

                      <template v-if="task.attendance === 'attended' && !isSurgeryTask(task)">
                        <span class="text-n-slate-8 text-[10px]">·</span>
                        <button class="cv-ag-act" style="--a: #B8860B" :class="task.surgery_indication === 'indicated' ? 'cv-ag-act-on' : ''" :disabled="savingAttendanceId === task.id" @click="setIndication(task, 'indicated')">🎯 Cirurgia indicada</button>
                        <button class="cv-ag-act" style="--a: #64748B" :class="task.surgery_indication === 'not_indicated' ? 'cv-ag-act-on' : ''" :disabled="savingAttendanceId === task.id" @click="setIndication(task, 'not_indicated')">Sem indicação</button>
                        <button v-if="['exames', 'teleconsulta'].includes(typeOf(task))" class="cv-ag-act" style="--a: #2563EB" title="Marcar um retorno presencial a partir deste atendimento" @click="scheduleFollowUpFrom(task)">📅 Marcar retorno</button>
                      </template>
                      <button v-if="!isSurgeryTask(task) && task.surgery_indication === 'indicated'" class="cv-btn cv-btn-sm" style="--cv-grad: linear-gradient(135deg, #0369A1, #38BDF8); --cv-deep-rgb: 7 89 133" title="Abre a Agenda de Cirurgias com os dados do paciente preenchidos" @click="scheduleSurgeryFrom(task)">
                        🔪 Agendar cirurgia
                      </button>
                    </div>
                    <div v-if="indicationPickerId === task.id" class="flex flex-wrap gap-1 mt-1.5" @click.stop>
                      <span class="text-[10px] text-n-slate-10 w-full">Qual procedimento foi indicado?</span>
                      <button v-for="proc in PROCEDURES" :key="proc" class="cv-chip cv-gold" :disabled="savingAttendanceId === task.id" @click="setIndication(task, 'indicated', proc)">{{ proc }}</button>
                    </div>
                  </div>
                </div>
              </template>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- ══ Modal criar/editar (a concha veste a cor do TIPO escolhido) ══ -->
    <div v-if="showModal" class="fixed inset-0 z-50 flex items-center justify-center bg-black/55 p-4" @click.self="showModal = false">
      <div class="cv-modal cv-ag-pop cv-ag-pop-wide w-full max-w-lg max-h-[92vh] flex flex-col" :style="formVars">
        <div class="cv-modal-head flex items-center gap-3">
          <span class="cv-glass w-9 h-9 flex items-center justify-center flex-shrink-0"><span :class="formKind.icon" class="text-base" /></span>
          <div class="flex-1 min-w-0">
            <p class="text-[11px] font-bold uppercase tracking-wider opacity-85">
              {{ editingTask ? `Editar ${formKind.noun}` : (formKind.key === 'cirurgia' ? 'Agendar cirurgia' : `${formKind.article === 'o' ? 'Novo' : 'Nova'} ${formKind.noun}`) }}
            </p>
            <h2 class="text-base font-bold leading-tight truncate">{{ form.name.trim() || (editingTask ? formKind.noun : 'Paciente') }}</h2>
            <p class="text-[11px] opacity-90 truncate">{{ formSummary || formKind.hint }}</p>
            <p v-if="ORIGIN_BY_KEY[form.origin] || (editingTask && isConfirmed(editingTask))" class="flex items-center gap-1.5 mt-1 flex-wrap">
              <span v-if="ORIGIN_BY_KEY[form.origin]" class="cv-ag-head-pill"><span :class="ORIGIN_BY_KEY[form.origin].icon" class="text-[10px]" /> {{ ORIGIN_BY_KEY[form.origin].label }}</span>
              <span v-if="editingTask && isConfirmed(editingTask)" class="cv-ag-head-pill" :title="confirmationTitle(editingTask)"><span class="i-lucide-badge-check text-[11px]" /> Confirmou</span>
            </p>
          </div>
          <button v-if="editingTask && (editingTask.contact_id || editingTask.phone)" class="cv-glass-btn" title="Abrir a conversa deste paciente, sem sair da Agenda" @click="openTaskChat(editingTask)">
            <span class="i-lucide-message-circle" /> <span class="hidden sm:inline">Conversa</span>
          </button>
          <button v-if="editingTask?.contact_id" class="cv-glass-btn" title="Abrir o Espaço do Paciente" @click="openPatientSpace(editingTask)">
            <PatientSpaceIcon :size="16" /> <span class="hidden sm:inline">Paciente</span>
          </button>
          <button class="cv-glass-btn cv-iconbtn" @click="showModal = false"><span class="i-lucide-x" /></button>
        </div>

        <div class="flex-1 overflow-y-auto p-5 lg:p-7 space-y-4 lg:space-y-5 cv-ag-form">
          <!-- o TIPO (só ao criar) -->
          <div v-if="!editingTask">
            <span class="cv-label block mb-1.5">Tipo</span>
            <!-- item 300: grade 3×2 — os 6 tipos cabem inteiros, nada cortado -->
            <div class="cv-ag-kind-grid">
              <button
                v-for="tt in TYPES"
                :key="'ft' + tt.key"
                type="button"
                class="cv-ag-kind"
                :class="formType.key === tt.key ? 'cv-ag-kind-on' : ''"
                :style="typeVarsOf(tt.key)"
                :title="`${tt.label} — ${tt.hint}`"
                @click="setFormType(tt.key)"
              >
                <span :class="tt.icon" class="text-sm shrink-0" />
                <span class="truncate">{{ tt.label }}</span>
              </button>
            </div>
          </div>

          <!-- item 324: no computador o card abre em DUAS colunas (quem/quando · motivo/valor/situação) -->
          <div class="cv-ag-cols">
            <div class="space-y-4 lg:space-y-5 min-w-0">
          <!-- 1 · quem -->
          <section class="cv-ag-sec">
            <header class="cv-ag-sec-head"><span class="cv-ag-num">1</span><div><h3>Paciente</h3><p>quem vem</p></div></header>
            <div class="grid grid-cols-1 sm:grid-cols-[1.5fr_1fr] gap-3">
              <div class="relative">
                <span class="cv-label block mb-1">Nome do paciente *</span>
                <!-- 🔎 item 346: paciente do cadastro → nome travado -->
                <div v-if="form.contact_id" class="cv-input w-full flex items-center gap-2 !bg-n-alpha-1" title="Paciente do cadastro: o nome não se edita aqui">
                  <span class="i-lucide-lock text-xs text-n-slate-10" />
                  <span class="flex-1 min-w-0 truncate font-semibold">{{ form.name }}</span>
                  <button type="button" class="text-[11px] font-semibold text-n-blue-11 hover:underline" @click="unpickPatient">trocar</button>
                </div>
                <template v-else>
                  <input v-model="form.name" class="cv-input w-full" placeholder="Digite para buscar no cadastro…" autocomplete="off" @focus="nameFocused = true" @blur="nameFocused = false" @keydown="onNameKey" />
                  <PatientSuggest ref="patientSuggest" :query="form.name" :active="nameFocused" action="pick" title="Já está no cadastro? Escolha a pessoa" @pick="pickPatient" />
                </template>
              </div>
              <div>
                <span class="cv-label block mb-1">Telefone {{ editingTask || form.contact_id ? '' : '*' }}</span>
                <input v-model="form.phone" class="cv-input w-full" placeholder="(11) 98888-7777" />
                <span v-if="phoneMissing && form.phone" class="text-[11px] text-amber-700 mt-1 block">Com DDD: (11) 98888-7777</span>
                <span v-if="phoneOwner" class="text-[11px] text-amber-800 mt-1 flex items-center gap-1 flex-wrap">
                  <span class="i-lucide-triangle-alert text-xs" /> Este telefone já é de <strong>{{ phoneOwner.name }}</strong>
                  <button type="button" class="font-semibold text-n-blue-11 hover:underline" @click="pickPatient(phoneOwner)">usar este paciente</button>
                </span>
              </div>
            </div>
            <!-- 🏥 item 300: de quem é o paciente (obrigatório ao criar) -->
            <div v-if="!originLocked" class="mt-3">
              <span class="cv-label block mb-1.5">Paciente de quem? {{ editingTask ? '' : '*' }}</span>
              <div class="cv-ag-origin" :class="originMissing ? 'cv-ag-origin-need' : ''">
                <button
                  v-for="o in ORIGINS"
                  :key="'or' + o.key"
                  type="button"
                  class="cv-ag-origin-opt"
                  :class="form.origin === o.key ? 'cv-ag-origin-on' : ''"
                  :style="{ '--o': o.color, '--o-rgb': hexToRgbSpaced(o.color) }"
                  :title="o.hint"
                  @click="form.origin = o.key"
                >
                  <span :class="form.origin === o.key ? 'i-lucide-circle-check' : o.icon" class="text-sm shrink-0" />
                  <span class="truncate">{{ o.label }}</span>
                </button>
              </div>
              <p class="text-[11px] mt-1.5" :class="originMissing ? 'text-amber-700 dark:text-amber-400 font-semibold' : 'text-n-slate-10'">{{ originHint }}</p>
            </div>
            <!-- 🧭 item 362: como o paciente chegou (obrigatório ao criar paciente novo) -->
            <div v-if="!originLocked" class="mt-3">
              <span class="cv-label block mb-1.5">Como chegou? {{ channelMissing ? '*' : '' }}</span>
              <div class="cv-ag-origin cv-ag-channel" :class="channelMissing ? 'cv-ag-origin-need' : ''">
                <button
                  v-for="o in LEAD_CHANNELS"
                  :key="'ch' + o.key"
                  type="button"
                  class="cv-ag-origin-opt"
                  :class="form.channel === o.key ? 'cv-ag-origin-on' : ''"
                  :style="{ '--o': o.color, '--o-rgb': hexToRgbSpaced(o.color) }"
                  :disabled="channelAuto"
                  :title="o.title || o.label"
                  @click="form.channel = form.channel === o.key && !channelAuto ? '' : o.key"
                >
                  <span :class="form.channel === o.key ? 'i-lucide-circle-check' : o.icon" class="text-sm shrink-0" />
                  <span class="truncate">{{ o.label }}</span>
                </button>
              </div>
              <p class="text-[11px] mt-1.5" :class="channelMissing ? 'text-amber-700 dark:text-amber-400 font-semibold' : 'text-n-slate-10'">{{ channelHint }}</p>
            </div>
          </section>

          <!-- 2 · quando e onde -->
          <section class="cv-ag-sec">
            <header class="cv-ag-sec-head"><span class="cv-ag-num">2</span><div><h3>Quando e onde</h3><p>dia, horário, médico e unidade</p></div></header>
            <div class="grid grid-cols-2 gap-3">
              <div>
                <span class="cv-label block mb-1">Dia *</span>
                <input v-model="form.date" type="date" class="cv-input w-full" />
              </div>
              <div>
                <span class="cv-label block mb-1">Horário</span>
                <input v-model="form.time" type="time" class="cv-input w-full" />
              </div>
              <div>
                <span class="cv-label block mb-1">Médico</span>
                <select v-model="form.doctor" class="cv-input w-full">
                  <option value="">A definir</option>
                  <option v-for="d in DOCTORS" :key="d.name" :value="d.name">{{ d.name }}</option>
                </select>
              </div>
              <div v-if="form.kind === 'teleconsultas'">
                <span class="cv-label block mb-1">Onde</span>
                <div class="cv-input w-full flex items-center gap-2 text-sm text-n-slate-11"><span class="i-lucide-video text-sm" /> Online (vídeo)</div>
              </div>
              <div v-else-if="form.kind !== 'cirurgias'">
                <span class="cv-label block mb-1">Unidade</span>
                <select v-model="form.unit" class="cv-input w-full">
                  <option v-for="(u, key) in UNITS" :key="key" :value="key">{{ u.label }}</option>
                  <option value="">Agenda pessoal (sem unidade)</option>
                </select>
              </div>
              <div v-else>
                <span class="cv-label mb-1 flex items-center justify-between">
                  Local da cirurgia
                  <button v-if="canEdit('agenda.cirurgias')" type="button" class="normal-case tracking-normal font-semibold hover:underline" style="color: var(--cv)" @click="openLocationsModal">gerenciar</button>
                </span>
                <select v-model="form.unit" class="cv-input w-full">
                  <option v-for="loc in surgeryLocations" :key="loc.key" :value="loc.key">{{ loc.label }}</option>
                  <option value="">A definir</option>
                </select>
              </div>
            </div>
            <!-- item 324: avisos de horário saem de cima do botão de salvar — ficam aqui, recolhidos (sanfona) -->
            <div v-if="reservedMismatch" class="cv-ag-note cv-ag-note-amber mt-3">
              <p class="cv-ag-note-head"><span class="i-lucide-triangle-alert text-sm shrink-0" /> Faixa reservada para {{ reservedLabelOf(reservedMismatch) }}</p>
              <p class="mt-0.5">{{ reservedMismatch.doctor }} atende só {{ reservedLabelOf(reservedMismatch) }} das {{ reservedMismatch.start }} às {{ reservedMismatch.end }} neste dia. Para outro tipo de atendimento, escolha um horário fora desta faixa.</p>
            </div>
            <div v-if="formConflicts.length" class="cv-ag-note mt-3" :class="hubConflicts.length ? 'cv-ag-note-red' : 'cv-ag-note-amber'">
              <button type="button" class="cv-ag-note-head w-full text-left" :title="conflictsOpen ? 'Recolher' : 'Ver quem está neste horário'" @click="conflictsOpen = !conflictsOpen">
                <span class="i-lucide-triangle-alert text-sm shrink-0" />
                <span class="flex-1 min-w-0">{{ hubConflicts.length ? 'Horário ocupado por paciente do Oftalmofácil' : 'Já tem agendamento neste horário (encaixe)' }}</span>
                <span class="cv-ag-note-count">{{ formConflicts.length }}</span>
                <span :class="conflictsOpen ? 'i-lucide-chevron-up' : 'i-lucide-chevron-down'" class="text-sm shrink-0" />
              </button>
              <div v-if="conflictsOpen" class="mt-1.5 space-y-0.5">
                <p v-for="t in formConflicts" :key="'cf' + t.id">• {{ hmOf(t) }} · {{ displayName(t) }} · {{ TYPE_BY_KEY[typeOf(t)]?.label || 'Agendamento' }}<template v-if="t.source === 'oftalmofacil'"> · Oftalmofácil{{ t.source_detail ? ` (${t.source_detail})` : '' }}</template></p>
              </div>
            </div>
          </section>
            </div>
            <div class="space-y-4 lg:space-y-5 min-w-0">
          <!-- 3 · motivo -->
          <section class="cv-ag-sec">
            <header class="cv-ag-sec-head"><span class="cv-ag-num">3</span><div><h3>Motivo</h3><p>{{ form.kind === 'cirurgias' ? 'procedimento' : form.kind === 'exames' ? 'exame e origem do agendamento' : 'tipo, problema e origem do agendamento' }}</p></div></header>
            <div class="space-y-3">
              <div v-if="editingTask && form.kind === 'consultas'">
                <span class="cv-label block mb-1.5">Tipo de consulta</span>
                <div class="cv-seg cv-seg-sm">
                  <button v-for="m in consultaModalities" :key="m.key" type="button" class="cv-seg-item" :class="form.modality === m.key ? 'cv-seg-on' : ''" @click="form.modality = m.key">{{ m.label }}</button>
                </div>
              </div>
              <div>
                <span class="cv-label block mb-1">{{ procedureLabel }}</span>
                <input v-model="form.procedure" list="agenda-procedimentos" class="cv-input w-full" :placeholder="form.kind === 'exames' ? 'Pentacam, OCT…' : form.kind === 'cirurgias' ? 'Catarata, PRK…' : 'Catarata, refrativa…'" />
                <datalist id="agenda-procedimentos">
                  <option v-for="p in procedureOptions" :key="p" :value="p" />
                </datalist>
              </div>
              <!-- 📅 item 217: o que conta como agendamento -->
              <div v-if="form.kind !== 'cirurgias'">
                <span class="cv-label block mb-1.5">Esta {{ formKind.noun }} é</span>
                <div class="cv-seg cv-seg-sm flex-wrap">
                  <button v-for="b in BOOKING_KINDS" :key="b.key" type="button" class="cv-seg-item" :class="form.booking_kind === b.key ? 'cv-seg-on' : ''" @click="form.booking_kind = b.key">{{ b.label }}</button>
                </div>
                <p class="text-[11px] text-n-slate-11 mt-1.5 leading-snug">{{ bookingHint }}</p>
              </div>
            </div>
          </section>

          <!-- 4 · valor (item 324): pré-configurado do tipo + valores lançados à mão -->
          <section class="cv-ag-sec cv-ag-sec-value">
            <header class="cv-ag-sec-head"><span class="cv-ag-num">4</span><div><h3>Valor</h3><p>o que vai na mensagem de confirmação</p></div></header>
            <div class="space-y-3">
              <div v-if="form.kind === 'consultas'">
                <span class="cv-label block mb-1.5">Esta consulta é</span>
                <div class="cv-seg cv-seg-sm">
                  <button type="button" class="cv-seg-item" :class="!form.particular ? 'cv-seg-on' : ''" @click="form.particular = false">Padrão</button>
                  <button type="button" class="cv-seg-item" :class="form.particular ? 'cv-seg-on' : ''" title="Consulta particular: vale o valor pré-configurado do médico" @click="form.particular = true">Particular</button>
                </div>
                <p v-if="form.particular && !form.doctor" class="text-[11px] text-amber-700 dark:text-amber-400 font-semibold mt-1.5">Escolha o médico em "Quando e onde" para puxar o valor particular dele.</p>
              </div>
              <div class="cv-ag-price">
                <span class="cv-ag-price-label">Vai na confirmação</span>
                <span class="cv-ag-price-value">{{ priceEffective?.text ? priceLabel(priceEffective.text) : (priceEffective ? 'valor padrão' : '—') }}</span>
                <span class="cv-ag-price-source">{{ priceEffective ? priceEffective.label : 'cirurgia: só valor lançado à mão' }}</span>
              </div>
              <p v-if="pricePreset && priceEffective?.source !== 'preset'" class="text-[11px] text-n-slate-11">Pré-configurado para este tipo: <b class="text-n-slate-12">{{ priceLabel(pricePreset.text) }}</b> ({{ pricePreset.label }}) — o valor lançado à mão tem prioridade.</p>
              <div>
                <span class="cv-label block mb-1.5">Valores lançados à mão</span>
                <div v-for="(c, idx) in form.charges" :key="'ch' + idx" class="cv-ag-charge">
                  <input v-model="c.label" class="cv-input flex-1 min-w-0" maxlength="60" placeholder="Descrição (consulta, exame…)" />
                  <input v-model="c.amount" class="cv-input cv-ag-charge-amount" maxlength="20" inputmode="decimal" placeholder="R$ 0,00" />
                  <button type="button" class="cv-btn cv-btn-ghost cv-btn-sm cv-iconbtn" title="Tirar este valor" @click="removeCharge(idx)"><span class="i-lucide-x" /></button>
                  <p class="cv-ag-charge-by">{{ chargeBy(c) }}</p>
                </div>
                <button v-if="form.charges.length < 8" type="button" class="cv-btn cv-btn-ghost cv-btn-sm" @click="addCharge"><span class="i-lucide-plus text-xs" /> {{ form.charges.length ? 'Adicionar outro valor' : 'Adicionar valor' }}</button>
                <p class="text-[11px] text-n-slate-10 mt-1.5">Mais de um valor: a mensagem leva a soma. Sem valor à mão, vale o pré-configurado do tipo.</p>
              </div>
            </div>
          </section>

          <!-- 5 · situação -->
          <section class="cv-ag-sec">
            <header class="cv-ag-sec-head"><span class="cv-ag-num">5</span><div><h3>Situação</h3><p>como está e o que anotar</p></div></header>
            <div class="space-y-3">
              <div>
                <div class="cv-seg cv-seg-sm">
                  <button type="button" class="cv-seg-item" :class="form.status === 'todo' && !form.canceled ? 'cv-seg-on' : ''" @click="form.status = 'todo'; form.canceled = false">Agendada</button>
                  <button type="button" class="cv-seg-item" :class="form.status === 'done' && !form.canceled ? 'cv-seg-on' : ''" style="--cv-grad: linear-gradient(135deg, #047857, #10B981); --cv-deep-rgb: 6 95 70" @click="form.status = 'done'; form.canceled = false">Concluída</button>
                  <button v-if="editingTask" type="button" class="cv-seg-item" :class="form.canceled ? 'cv-seg-on' : ''" style="--cv-grad: linear-gradient(135deg, #991B1B, #EF4444); --cv-deep-rgb: 153 27 27" @click="form.canceled = !form.canceled">Cancelada</button>
                </div>
                <p v-if="form.canceled" class="text-[10px] text-red-500 mt-1">Sai do calendário e conta no indicador de canceladas.</p>
              </div>
              <div>
                <span class="cv-label block mb-1">Observações</span>
                <textarea v-model="form.description" rows="2" class="cv-input w-full resize-none" placeholder="Convênio, pedido especial, retorno..." />
              </div>
            </div>
          </section>
            </div>
          </div>
        </div>

        <!-- rodapé: excluir discreto à esquerda, ação principal no canto inferior DIREITO (área terminal da leitura) -->
        <div class="cv-modal-foot flex items-center gap-2 flex-wrap">
          <div v-if="editingTask && canEditAgenda" class="mr-auto">
            <button v-if="!showDeleteConfirm" class="text-xs text-n-slate-11 hover:text-red-600 flex items-center gap-1" @click="showDeleteConfirm = true"><span class="i-lucide-trash-2 text-xs" /> Excluir</button>
            <!-- item 304: confirmação com NOME e HORÁRIO (a mesma consulta pode aparecer em mais de um lugar — não é duplicada) -->
            <div v-else class="flex items-center gap-2 flex-wrap">
              <span class="text-xs text-n-slate-11">Mover <b class="text-n-slate-12">{{ displayName(editingTask) }}</b> · {{ trashWhen(editingTask) }} para a Lixeira? Dá para restaurar depois.</span>
              <button class="cv-btn cv-btn-sm cv-red" @click="removeTask"><span class="i-lucide-trash-2 text-xs" /> Mover para a Lixeira</button>
              <button class="cv-btn cv-btn-ghost cv-btn-sm" @click="showDeleteConfirm = false">Não</button>
            </div>
          </div>
          <button class="cv-btn cv-btn-ghost cv-btn-lg" :class="editingTask && canEditAgenda ? '' : 'ml-auto'" @click="showModal = false">{{ canEditAgenda ? 'Cancelar' : 'Fechar' }}</button>
          <button v-if="canEditAgenda" class="cv-btn cv-btn-lg min-w-[170px]" :disabled="!canSave || isSaving" :title="originMissing ? 'Escolha se o paciente é da CEVICO ou do Oftalmofácil' : phoneMissing ? 'Informe o telefone do paciente com DDD' : channelMissing ? 'Informe como o paciente chegou' : ''" @click="save">
            <span :class="isSaving ? 'i-lucide-loader-2 animate-spin' : 'i-lucide-check'" class="text-sm" />
            {{ isSaving ? 'Salvando…' : (editingTask ? `Salvar ${formKind.noun}` : `Agendar ${formKind.noun}`) }}
          </button>
        </div>
      </div>
    </div>

    <!-- ══ 🗑️ Lixeira (item 304): excluídos/cancelados nos últimos 60 dias ══ -->
    <div v-if="showTrash" class="fixed inset-0 z-[52] flex items-center justify-center bg-black/55 p-4" @click.self="showTrash = false">
      <div class="cv-modal cv-ag-pop w-full max-w-2xl max-h-[92vh] flex flex-col" :style="pageVars">
        <div class="cv-modal-head flex items-center gap-3">
          <span class="cv-glass w-9 h-9 flex items-center justify-center flex-shrink-0"><span class="i-lucide-trash-2 text-base" /></span>
          <div class="flex-1 min-w-0">
            <p class="text-[11px] font-bold uppercase tracking-wider opacity-85">Lixeira da Agenda</p>
            <h2 class="text-base font-bold leading-tight truncate">{{ trashTasks.length }} agendamento(s) nos últimos {{ TRASH_DAYS }} dias</h2>
            <p class="text-[11px] opacity-90 truncate">excluído ou cancelado não some de verdade: fica aqui com quem e quando, e volta com 1 clique</p>
          </div>
          <button class="cv-glass-btn cv-iconbtn" @click="showTrash = false"><span class="i-lucide-x" /></button>
        </div>
        <div class="flex-1 overflow-y-auto p-4 space-y-2">
          <p v-if="!trashTasks.length" class="text-sm text-n-slate-11 text-center py-8">Nada na Lixeira. 🎉</p>
          <div v-for="t in trashTasks" :key="'trash' + t.id" class="cv-ag-slotrow items-center">
            <span class="cv-icon cv-icon-sm flex-shrink-0" :style="{ background: TYPE_BY_KEY[typeOf(t)]?.color || '#64748B' }"><span :class="TYPE_BY_KEY[typeOf(t)]?.icon || 'i-lucide-calendar'" class="text-xs" /></span>
            <div class="flex-1 min-w-0">
              <p class="text-sm font-bold text-n-slate-12 truncate">{{ displayName(t) }} <span class="text-xs font-normal text-n-slate-11">· {{ trashWhen(t) }}<template v-if="t.unit"> · {{ UNITS[t.unit]?.label || t.unit }}</template> · {{ TYPE_BY_KEY[typeOf(t)]?.label || 'Agendamento' }}</span></p>
              <p class="text-xs text-n-slate-11 truncate">{{ trashWho(t) }}<template v-if="t.phone"> · {{ t.phone }}</template></p>
            </div>
            <button v-if="canEdit('agenda.lixeira')" class="cv-btn cv-btn-sm" :disabled="trashBusyId === t.id" title="Volta para a Agenda no mesmo dia e horário" @click="restoreTask(t)"><span class="i-lucide-undo-2 text-xs" /> Restaurar</button>
            <button v-if="isAdmin" class="cv-btn cv-btn-ghost cv-btn-sm cv-red" :disabled="trashBusyId === t.id" title="Apaga de vez (só administrador)" @click="purgeTask(t)"><span class="i-lucide-x text-xs" /></button>
          </div>
        </div>
      </div>
    </div>

    <!-- ══ Imprimir: escolher colunas e orientação ══ -->
    <div v-if="showPrintModal" class="fixed inset-0 z-[52] flex items-center justify-center bg-black/55 p-4" @click.self="showPrintModal = false">
      <div class="cv-modal cv-ag-pop w-full max-w-md max-h-[92vh] flex flex-col" :style="pageVars">
        <div class="cv-modal-head flex items-center gap-3">
          <span class="cv-glass w-9 h-9 flex items-center justify-center flex-shrink-0"><span class="i-lucide-printer text-base" /></span>
          <div class="flex-1 min-w-0">
            <p class="text-[11px] font-bold uppercase tracking-wider opacity-85">Imprimir a lista do dia</p>
            <h2 class="text-base font-bold leading-tight truncate">{{ cursor.toLocaleDateString('pt-BR', { weekday: 'long', day: '2-digit', month: '2-digit' }) }}</h2>
            <p class="text-[11px] opacity-90 truncate">{{ dayViewTasks.length }} {{ dayViewTasks.length === 1 ? k.noun : k.plural }} · {{ printColsOn.length }} colunas · a escolha fica salva neste navegador</p>
          </div>
          <button class="cv-glass-btn cv-iconbtn" @click="showPrintModal = false"><span class="i-lucide-x" /></button>
        </div>
        <div class="flex-1 overflow-y-auto p-5 space-y-4 cv-ag-form">
          <section class="cv-ag-sec">
            <header class="cv-ag-sec-head"><span class="cv-ag-num">1</span><div><h3>Orientação da folha</h3><p>em pé cabe mais linhas; deitada dá mais espaço para anotar</p></div></header>
            <div class="cv-seg cv-seg-sm">
              <button type="button" class="cv-seg-item" :class="printOpts.orientation === 'portrait' ? 'cv-seg-on' : ''" @click="printOpts.orientation = 'portrait'"><span class="i-lucide-rectangle-vertical text-xs" /> Em pé (retrato)</button>
              <button type="button" class="cv-seg-item" :class="printOpts.orientation === 'landscape' ? 'cv-seg-on' : ''" @click="printOpts.orientation = 'landscape'"><span class="i-lucide-rectangle-horizontal text-xs" /> Deitada (paisagem)</button>
            </div>
          </section>
          <section class="cv-ag-sec">
            <header class="cv-ag-sec-head"><span class="cv-ag-num">2</span><div><h3>Colunas</h3><p>marque o que deve sair na folha, na ordem em que aparecem</p></div></header>
            <div class="space-y-1.5">
              <label v-for="c in PRINT_COLUMNS" :key="c.key" class="cv-ag-check">
                <input v-model="printOpts.cols[c.key]" type="checkbox" />
                <span>{{ c.label }}</span>
              </label>
            </div>
            <button type="button" class="text-xs font-semibold hover:underline mt-3" style="color: var(--cv)" @click="resetPrintOpts">↺ Voltar ao padrão</button>
          </section>
        </div>
        <div class="cv-modal-foot flex items-center gap-2">
          <button class="cv-btn cv-btn-ghost cv-btn-lg ml-auto" @click="showPrintModal = false">Cancelar</button>
          <button class="cv-btn cv-btn-lg min-w-[170px]" :disabled="!printColsOn.length" @click="printDayList">
            <span class="i-lucide-printer text-sm" /> Imprimir
          </button>
        </div>
      </div>
    </div>

    <!-- Modal: janela da SALA CIRÚRGICA -->
    <div v-if="showSurgeryWindowsModal" class="fixed inset-0 z-[55] flex items-center justify-center bg-black/55 p-4" @click.self="showSurgeryWindowsModal = false">
      <div class="cv-modal cv-ag-pop w-full max-w-lg max-h-[90vh] flex flex-col" :style="kindVars('cirurgias')">
        <div class="cv-modal-head flex items-center gap-3">
          <span class="i-lucide-clock text-xl" />
          <div class="flex-1">
            <h2 class="text-base font-bold">Janela da sala cirúrgica</h2>
            <p class="text-[11px] opacity-85">dias e horários em que a sala de cada clínica parceira está disponível</p>
          </div>
          <button class="cv-glass-btn cv-iconbtn" @click="showSurgeryWindowsModal = false"><span class="i-lucide-x" /></button>
        </div>
        <div class="flex-1 overflow-y-auto p-5 space-y-3">
          <p class="text-xs text-n-slate-10">
            A ocupação e os blocos livres do trilho de cirurgias vêm daqui.
            <button v-if="canEdit('agenda.cirurgias')" class="font-semibold hover:underline" style="color: var(--cv)" @click="openLocationsModal">Gerenciar clínicas →</button>
          </p>
          <div v-if="!editSurgeryWindows.length" class="cv-sub text-center py-6 text-n-slate-10 text-sm">Nenhuma janela ainda — adicione a primeira.</div>
          <div v-for="(w, i) in editSurgeryWindows" :key="i" class="cv-sub grid grid-cols-2 sm:grid-cols-6 gap-2 items-center p-2.5">
            <select v-model="w.dow" :disabled="!canEdit('agenda.configuracao')" class="cv-input !h-8 text-xs">
              <option v-for="(d, di) in WEEKDAY_FULL" :key="di" :value="di">{{ d }}</option>
            </select>
            <select v-model="w.location" :disabled="!canEdit('agenda.configuracao')" class="cv-input !h-8 text-xs">
              <option v-for="loc in surgeryLocations" :key="loc.key" :value="loc.key">{{ loc.label }}</option>
            </select>
            <input v-model="w.start" type="time" :disabled="!canEdit('agenda.configuracao')" class="cv-input !h-8 text-xs" />
            <input v-model="w.end" type="time" :disabled="!canEdit('agenda.configuracao')" class="cv-input !h-8 text-xs" />
            <select v-model="w.block" :disabled="!canEdit('agenda.configuracao')" class="cv-input !h-8 text-xs">
              <option :value="10">10 min</option><option :value="15">15 min</option><option :value="20">20 min</option>
              <option :value="30">30 min</option><option :value="60">1 hora</option><option :value="90">1h30</option><option :value="120">2 horas</option>
            </select>
            <button v-if="canEdit('agenda.configuracao')" class="text-n-slate-9 hover:text-red-500 i-lucide-trash-2 text-sm justify-self-center" @click="removeSurgeryWindow(i)" />
          </div>
          <button v-if="canEdit('agenda.configuracao')" class="cv-btn cv-btn-ghost cv-btn-sm" @click="addSurgeryWindow"><span class="i-lucide-plus text-xs" /> Adicionar janela</button>
        </div>
        <div v-if="canEdit('agenda.configuracao')" class="cv-modal-foot flex gap-2">
          <button class="cv-btn flex-1" :disabled="isSavingSurgeryWindows" @click="saveSurgeryWindows">{{ isSavingSurgeryWindows ? 'Salvando…' : 'Salvar janelas' }}</button>
          <button class="cv-btn cv-btn-ghost" @click="showSurgeryWindowsModal = false">Cancelar</button>
        </div>
      </div>
    </div>

    <!-- 🔬 Modal: janela de exames (23/09) -->
    <div v-if="showExamWindowsModal" class="fixed inset-0 z-[55] flex items-center justify-center bg-black/55 p-4" @click.self="showExamWindowsModal = false">
      <div class="cv-modal cv-ag-pop w-full max-w-lg max-h-[90vh] flex flex-col" :style="kindVars('exames')">
        <div class="cv-modal-head flex items-center gap-3">
          <span class="i-lucide-scan-eye text-xl" />
          <div class="flex-1">
            <h2 class="text-base font-bold">Janela de exames</h2>
            <p class="text-[11px] opacity-85">dias, horários e unidade em que a clínica faz exames — separada da agenda dos médicos</p>
          </div>
          <button class="cv-glass-btn cv-iconbtn" @click="showExamWindowsModal = false"><span class="i-lucide-x" /></button>
        </div>
        <div class="flex-1 overflow-y-auto p-5 space-y-3">
          <p class="text-xs text-n-slate-10">
            Padrão da CEVICO: segunda a sexta, das 08h às 17h, no IOP da Av. Paulista, em blocos de 30 min. A ocupação e os blocos livres do trilho de exames vêm daqui; os exames aparecem na cor deles em toda a agenda.
          </p>
          <div v-if="!editExamWindows.length" class="cv-sub text-center py-6 text-n-slate-10 text-sm">Nenhuma janela — adicione a primeira.</div>
          <div v-for="(w, i) in editExamWindows" :key="i" class="cv-sub grid grid-cols-2 sm:grid-cols-6 gap-2 items-center p-2.5">
            <select v-model="w.dow" :disabled="!canEdit('agenda.configuracao')" class="cv-input !h-8 text-xs">
              <option v-for="(d, di) in WEEKDAY_FULL" :key="di" :value="di">{{ d }}</option>
            </select>
            <select v-model="w.unit" :disabled="!canEdit('agenda.configuracao')" class="cv-input !h-8 text-xs">
              <option v-for="(u, key) in UNITS" :key="key" :value="key">{{ u.label }}</option>
            </select>
            <input v-model="w.start" type="time" :disabled="!canEdit('agenda.configuracao')" class="cv-input !h-8 text-xs" />
            <input v-model="w.end" type="time" :disabled="!canEdit('agenda.configuracao')" class="cv-input !h-8 text-xs" />
            <select v-model="w.block" :disabled="!canEdit('agenda.configuracao')" class="cv-input !h-8 text-xs">
              <option :value="10">10 min</option><option :value="15">15 min</option><option :value="20">20 min</option>
              <option :value="30">30 min</option><option :value="45">45 min</option><option :value="60">1 hora</option>
            </select>
            <button v-if="canEdit('agenda.configuracao')" class="text-n-slate-9 hover:text-red-500 i-lucide-trash-2 text-sm justify-self-center" @click="removeExamWindow(i)" />
          </div>
          <button v-if="canEdit('agenda.configuracao')" class="cv-btn cv-btn-ghost cv-btn-sm" @click="addExamWindow"><span class="i-lucide-plus text-xs" /> Adicionar janela</button>
        </div>
        <div v-if="canEdit('agenda.configuracao')" class="cv-modal-foot flex gap-2">
          <button class="cv-btn flex-1" :disabled="isSavingExamWindows" @click="saveExamWindows">{{ isSavingExamWindows ? 'Salvando…' : 'Salvar janela' }}</button>
          <button class="cv-btn cv-btn-ghost" @click="showExamWindowsModal = false">Cancelar</button>
        </div>
      </div>
    </div>

    <!-- Modal: locais de cirurgia -->
    <div v-if="showLocationsModal" class="fixed inset-0 z-[56] flex items-center justify-center bg-black/55 p-4" @click.self="showLocationsModal = false">
      <div class="cv-modal cv-ag-pop w-full max-w-sm flex flex-col" :style="kindVars('cirurgias')">
        <div class="cv-modal-head flex items-center gap-3">
          <span class="i-lucide-map-pin text-xl" />
          <h2 class="text-base font-bold flex-1">Locais de cirurgia</h2>
          <button class="cv-glass-btn cv-iconbtn" @click="showLocationsModal = false"><span class="i-lucide-x" /></button>
        </div>
        <div class="p-5 space-y-2">
          <p class="text-xs text-n-slate-10">Clínicas parceiras onde as cirurgias acontecem (ex.: IOP). Aparecem no campo "Local da cirurgia" ao agendar.</p>
          <div v-for="(loc, i) in locationsDraft" :key="i" class="flex items-center gap-2">
            <input v-model="loc.label" class="cv-input flex-1" placeholder="Nome da clínica (ex.: IOP)" />
            <button class="text-n-slate-9 hover:text-red-500 i-lucide-trash-2 text-sm" @click="removeLocationRow(i)" />
          </div>
          <button class="cv-btn cv-btn-ghost cv-btn-sm" @click="addLocationRow"><span class="i-lucide-plus text-xs" /> Adicionar local</button>
        </div>
        <div class="cv-modal-foot flex gap-2">
          <button class="cv-btn flex-1" :disabled="isSavingLocations" @click="saveLocations">{{ isSavingLocations ? 'Salvando…' : 'Salvar locais' }}</button>
          <button class="cv-btn cv-btn-ghost" @click="showLocationsModal = false">Cancelar</button>
        </div>
      </div>
    </div>

    <!-- 🗂️ item 307: Configurações da agenda (abrir e fechar · faixas · regras da IA · conferência) -->
    <AgendaSettings
      v-if="showSettings"
      :units="UNITS"
      :initial-tab="settingsTab"
      @close="showSettings = false"
      @reschedule="rescheduleFromSettings"
      @open-exam-windows="showSettings = false; openExamWindowsModal()"
      @open-surgery-windows="showSettings = false; openSurgeryWindowsModal()"
    >
      <template #conferencia>
        <!-- Conferência do dia → colunas do CRM (admin) -->
        <div v-if="canEdit('agenda.configuracao')" class="cv-sub p-3.5 space-y-2.5 cv-gold">
          <p class="text-xs font-bold text-n-slate-12 flex items-center gap-1.5">
            <span class="i-lucide-list-checks text-sm" style="color: #B8860B" /> Conferência do dia → CRM
          </p>
          <p class="text-[11px] text-n-slate-10 leading-relaxed">
            Ao marcar <b>Compareceu / Faltou / Cirurgia indicada</b> na lista do dia, o card do paciente move sozinho para a coluna escolhida — e as automações dessa coluna disparam.
          </p>
          <div class="cv-row cv-amber p-2.5 space-y-2">
            <p class="text-[10px] font-semibold text-n-slate-11">⏰ Prazo da conferência — sem conferir até o horário, nasce a tarefa "Concluir a conferência do dia" para a responsável</p>
            <div class="grid grid-cols-1 sm:grid-cols-3 gap-2">
              <div>
                <span class="cv-label block mb-0.5">Consultas — responsável</span>
                <select v-model="attendanceOwners.consulta_user_id" class="cv-input w-full !h-8 text-xs">
                  <option value="">Ninguém (desligado)</option>
                  <option v-for="agent in agents" :key="agent.id" :value="String(agent.id)">{{ agent.name }}</option>
                </select>
              </div>
              <div>
                <span class="cv-label block mb-0.5">Cirurgias — responsável</span>
                <select v-model="attendanceOwners.cirurgia_user_id" class="cv-input w-full !h-8 text-xs">
                  <option value="">Ninguém (desligado)</option>
                  <option v-for="agent in agents" :key="agent.id" :value="String(agent.id)">{{ agent.name }}</option>
                </select>
              </div>
              <div>
                <span class="cv-label block mb-0.5">Horário limite</span>
                <input v-model="attendanceOwners.deadline" type="time" class="cv-input w-full !h-8 text-xs" />
              </div>
            </div>
          </div>
          <!-- 🔁 item 343: Equipe dos retornos (admin) -->
          <div v-if="isAdmin" class="cv-row p-2.5 space-y-2">
            <p class="text-[10px] font-semibold text-n-slate-11">🔁 Equipe dos retornos — retorno não fica no nome de quem marcou: as pessoas marcadas aqui veem todos os retornos (tarefas e avisos) e quem pegar primeiro assume</p>
            <div class="flex flex-wrap gap-1.5">
              <button v-for="agent in agents" :key="`ret-${agent.id}`" type="button" class="cv-chip" :class="retornoTeam.includes(agent.id) ? 'cv-chip-on' : ''" @click="toggleRetornoTeam(agent.id)">
                {{ retornoTeam.includes(agent.id) ? '✓ ' : '' }}{{ agent.name }}
              </button>
            </div>
            <p class="text-[10px] text-n-slate-9">Ninguém marcado = como antes (retorno fica com quem marcou).</p>
          </div>
          <div class="space-y-2">
            <div v-for="opt in [
              { key: 'attended_stage_id', label: '✓ Compareceu → mover card para' },
              { key: 'missed_stage_id', label: '✗ Faltou → mover card para' },
              { key: 'indicated_stage_id', label: '🎯 Cirurgia indicada → mover card para' },
              { key: 'surgery_done_stage_id', label: '🔪 Cirurgia realizada → mover card para' },
              { key: 'surgery_missed_stage_id', label: '✗ Não veio à cirurgia → mover card para' },
            ]" :key="opt.key">
              <span class="cv-label block mb-0.5">{{ opt.label }}</span>
              <select v-model="attendanceStages[opt.key]" class="cv-input w-full !h-8 text-xs">
                <option value="">Não mover</option>
                <option v-for="s in allCrmStages" :key="s.id" :value="s.id">{{ s.name }} ({{ s.pipeline }})</option>
              </select>
            </div>
          </div>
          <button class="cv-btn w-full" :disabled="isSavingAttendanceCfg" @click="saveAttendanceStages">{{ isSavingAttendanceCfg ? 'Salvando…' : 'Salvar conferência do dia' }}</button>
        </div>
        <p v-if="!canEdit('agenda.configuracao')" class="text-xs text-n-slate-9">Só quem edita a configuração da agenda ajusta a conferência do dia.</p>
      </template>
    </AgendaSettings>

    <!-- botão + flutuante -->
    <button v-if="canEditAgenda" class="cv-ag-fab" :title="newLabel" @click="openCreateFab">
      <span class="i-lucide-plus text-2xl" />
    </button>
  </div>
</template>
